import React, { useEffect, useState, useMemo } from "react";
import { useTutorial } from "../../context/TutorialContext";
import { getAuthHeaders } from "../../utils/habiApi";
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { tolerantId, fetchUsersByField } from "../../utils/firestoreHelpers";
import {
  COMMUNICATION_TEMPLATES,
  groupTemplatesByCategory,
  CATEGORY_LABELS,
  META_STATUS_LABELS,
  isTemplateEnabledForBuilding,
  hasBuildingOverride,
} from "../../utils/communicationTemplates";
import { logAudit } from "../../utils/auditLog";
import { formatDocumentoDisplay } from "../../utils/documentoFormat";
import { auth, db } from "../../firebase";
import { getOptionalPasswordResetActionCodeSettings } from "../../utils/authEmailActions";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import {
  importarUnidades,
  assignOccupants,
  deleteUnit,
  setBuildingAdmin,
  createStaffUser,
  updateStaffUser,
  removeStaffUser,
  nuevoRequestId,
  mensajeDeError,
  codigoDeError,
} from "../../utils/identityApi";
import { useBuilding } from "../../context/BuildingContext";
import {
  FaPen,
  FaTrash,
  FaBuilding,
  FaUsers,
  FaDoorOpen,
  FaUserTie,
  FaPlus,
  FaPlusCircle,
  FaSave,
  FaTimes,
  FaDownload,
  FaUpload,
  FaRobot,
  FaFileExcel,
  FaMagic,
  FaImage,
  FaFilePdf,
  FaFile,
  FaComments,
  FaSwimmingPool,
  FaMoneyBillWave,
  FaCalculator,
  FaExclamationTriangle,
  FaSync,
  FaEllipsisH,
  FaToggleOn,
  FaToggleOff,
  FaTruck,
} from "react-icons/fa";
import { createPortal } from "react-dom";
import { colors, shadows, radius, spacing, typography, transitions, zIndex, cardBase, btnPrimary, btnSecondary, btnDanger, btnGhost, inputBase, pageContainer, pageTitle as pageTitleStyle, tableHeader, tableCell, modalOverlay } from "../../utils/designTokens";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import RevisionUnidadesModal from "../../components/RevisionUnidadesModal";
import {
  filaDesdeUnidad,
  sumarAlBorrador,
  estadosDeFilas,
  cambiosFisicosParaExistente,
  leerBorrador,
  guardarBorrador,
  borrarBorrador,
} from "../../utils/borradorUnidades";
import VisBadge from "../../components/VisBadge";
import { slugifyFundName, RESERVED_FUND_IDS, CUSTOM_FUND_COLORS } from "../../utils/fundHelpers";
import { normKind, esAlquilerDelEdificio, UNIT_KIND_ALQUILER } from "../../utils/unidadesEdificio";
import { useLatestRate } from "../../utils/exchangeRates";
import Loader from "../../components/Loader";
import EmptyState from "../../components/EmptyState";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import AmenityConfigTab from "./AmenityConfigTab";
import SaldosProveedoresTab from "./SaldosProveedoresTab";
import CriteriosPorEdificios from "./CriteriosPorEdificios";
import EditarPropietariosModal from "./EditarPropietariosModal";
import { useJsApiLoader } from "@react-google-maps/api";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const gmapLibraries = ["places"];

// Solicitud de baja abierta (approveBuildingDeletion): pendiente, en curso o con
// error para reintentar. Con cualquiera de esas no se pide otra.
const ESTADOS_BAJA_ABIERTA = ["pendiente", "procesando", "error"];
const AVISO_BAJA = {
  pendiente: "Este edificio tiene una solicitud de eliminación pendiente de aprobación de un superadmin.",
  procesando: "La eliminación de este edificio está en curso.",
  error: "La eliminación de este edificio quedó con error. Un superadmin tiene que reintentarla.",
};
// Si hubiera más de una abierta (datos viejos), se muestra la más avanzada.
const estadoBajaAbierta = (snap) => {
  const estados = snap.docs.map((d) => d.data()?.status || "pendiente");
  return ["procesando", "error", "pendiente"].find((e) => estados.includes(e)) || null;
};

// URL del backend PropIA para procesamiento con IA
const Habi_API_URL = (() => {
  const wsUrl = process.env.REACT_APP_PROPIA_WS_URL || "wss://propia-agent-241529496588.us-central1.run.app";
  return wsUrl.replace("wss://", "https://").replace("ws://", "http://").replace("/ws", "");
})();

// URL del microservicio de Catastro para consulta de valores catastrales
// URL de producción de Catastro (Cloud Run São Paulo)
const CATASTRO_API_URL = process.env.REACT_APP_CATASTRO_API_URL && process.env.REACT_APP_CATASTRO_API_URL.startsWith("http")
  ? process.env.REACT_APP_CATASTRO_API_URL
  : "https://catastro-service-241529496588.southamerica-east1.run.app";

// Bancos, cooperativas y medios de pago con operativa en Uruguay.
// Ordenados alfabéticamente (orden case-insensitive con locale es).
const BANCOS_URUGUAY = [
  "Banco Nacion Arg.",
  "Bandes",
  "BBVA",
  "BHU",
  "BROU",
  "Citibank",
  "Dinero Electronico ANDA",
  "FUCAC",
  "FUCEREP",
  "GRIN",
  "Heritage",
  "HSBC",
  "Itau",
  "Mercadopago",
  "Midinero",
  "OCA Blue",
  "PREX",
  "Santander",
  "Scotiabank",
];

// ── Identidad (Fase 0C de seguridad) ─────────────────────────────────────────
// Las altas y los cambios de identidad van por callables (src/utils/identityApi.js):
// esta pantalla ya no escribe users, userbuilding, occupancies, admin, employee
// ni counters, ni los ocupantes o el tipo de una unidad. Siguen directo: los
// campos físicos de apartments, los datos y la configuración de building y el
// horario/sueldo de employeebuilding.

const SLOTS_UNIDAD = ["owner", "ownerSecondary", "tenant", "tenantSecondary"];
const ETIQUETA_SLOT = {
  owner: "Propietario",
  ownerSecondary: "Propietario complementario",
  tenant: "Inquilino",
  tenantSecondary: "Inquilino complementario",
};
/** Campos del formulario "Datos": lo único que guarda ese botón (no la config financiera ni un campo id). */
const CAMPOS_DATOS_EDIFICIO = ["name", "address", "city", "department", "country", "padron", "floors", "elevators", "complex", "lat", "lng"];
/** Campos físicos que el modal "Editar unidad" guarda directo en apartments (allowlist de 0D). */
const CAMPOS_FISICOS_UNIDAD = ["number", "floor", "m2", "bedroom", "bathroom", "valorCatastral", "balcony", "garage", "infront"];
const CAMPOS_NUMERICOS_UNIDAD = new Set(["m2", "bedroom", "bathroom", "valorCatastral"]);
/** Datos personales del colaborador que se editan por updateStaffUser (el email no se edita acá). */
const CAMPOS_STAFF_EDITABLES = ["NombreCompleto", "ApellidosCompleto", "tipoDocumento", "Documento", "celular"];
const TIPOS_DOCUMENTO = ["CI", "DNI", "Pasaporte", "RUT", "Otro"];
/** Carga masiva: lotes de hasta 100 filas por llamada (el servidor acepta 300, pero corta a los 540 s). */
const LOTE_IMPORTACION = 100;
/** Errores que se arreglan reintentando el mismo pedido (corte, concurrencia, tope de tiempo). */
const CODIGOS_TRANSITORIOS = new Set(["aborted", "deadline-exceeded", "unavailable", "internal", "unknown", ""]);
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/** Hoy (AAAA-MM-DD) en hora de Montevideo, no la fecha UTC de toISOString(). */
const hoyMontevideo = () => {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Montevideo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const parte = (tipo) => (partes.find((p) => p.type === tipo) || {}).value;
  return `${parte("year")}-${parte("month")}-${parte("day")}`;
};

const etiquetaMeses = (meses) =>
  (Array.isArray(meses) ? meses : [])
    .map((m) => `${MESES[Number(m.month) - 1] || m.month} ${m.year}`)
    .join(", ");

/** "2026-10-19" → "19/10/2026". */
const fechaLegible = (iso) => {
  const [y, m, d] = String(iso || "").split("-");
  return y && m && d ? `${d}/${m}/${y}` : String(iso || "");
};

const esErrorTransitorio = (e) => CODIGOS_TRANSITORIOS.has(codigoDeError(e));

// K2: importarUnidades no asigna una ficha existente que también está vinculada
// a otro edificio que no administrás (u otra causa que revisa un superadmin): la
// fila falla con detalles.motivo "revision_superadmin" y la unidad no se crea.
const MSJ_REVISION_FILA = "Esta persona ya tiene una ficha vinculada a otro edificio; la revisa un superadmin.";
const esFilaEnRevision = (fila) => fila?.detalles?.motivo === "revision_superadmin";
const textoFilaEnRevision = (fila) => `${String(fila?.error || "").trim() || MSJ_REVISION_FILA} La unidad no se creó.`;

// K2 en assignOccupants (Guardar unidad): quien entra a un lugar la revisa un
// superadmin (ficha de otro edificio, cuenta con otro email o activada por un
// link en claro…). El servidor no asigna a nadie (failed-precondition con
// details {motivo "revision_superadmin", causa, slot}) y la pantalla no guarda
// los demás cambios: es un aviso, no un error.
const MSJ_REVISION_ASIGNACION = "Esta persona la tiene que revisar un superadmin antes de asignarla.";
const LUGAR_DE_SLOT = {
  owner: "propietario",
  ownerSecondary: "propietario secundario",
  tenant: "inquilino",
  tenantSecondary: "inquilino secundario",
};
const esAsignacionEnRevision = (e) => e?.details?.motivo === "revision_superadmin";
const textoAsignacionEnRevision = (e) => {
  const lugar = LUGAR_DE_SLOT[e?.details?.slot];
  const nada = lugar
    ? `No se asignó el ${lugar} ni se guardó ningún otro cambio de la unidad.`
    : "No se guardó ningún cambio de la unidad.";
  return `${mensajeDeError(e, MSJ_REVISION_ASIGNACION)} ${nada}`;
};

/** Valor de un lugar de la unidad; "" si está vacío (null, "", 0, "null"…). */
const valorSlot = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  return ["", "0", "null", "undefined", "nan"].includes(s.toLowerCase()) ? "" : s;
};

const textoLimpio = (v) => (v == null ? "" : String(v).trim());

const siNoONull = (v) => {
  if (v === true || v === false) return v;
  const s = textoLimpio(v).toLowerCase();
  if (["true", "si", "sí", "s", "yes", "1"].includes(s)) return true;
  if (["false", "no", "n", "0"].includes(s)) return false;
  return null;
};

/** Campos físicos tipados como los guarda el resto de la app (número o null, booleano o null). */
const camposFisicosDe = (u) => {
  const out = {};
  CAMPOS_FISICOS_UNIDAD.forEach((k) => {
    const v = u[k];
    if (k === "number" || k === "floor") out[k] = textoLimpio(v);
    else if (CAMPOS_NUMERICOS_UNIDAD.has(k)) {
      const s = textoLimpio(v);
      out[k] = s === "" ? null : Number.isFinite(Number(s)) ? Number(s) : s;
    } else if (k === "balcony" || k === "garage") out[k] = siNoONull(v);
    else out[k] = textoLimpio(v) || null;
  });
  return out;
};

/** Forma comparable de un campo físico (55 y "55", true y "si" son lo mismo). */
const comparableFisico = (k, v) => {
  if (k === "balcony" || k === "garage") return String(siNoONull(v));
  const s = textoLimpio(v);
  if (CAMPOS_NUMERICOS_UNIDAD.has(k) && s !== "" && Number.isFinite(Number(s))) return String(Number(s));
  return s;
};

const ordenarUnidades = (lista) =>
  lista.sort((a, b) => a.floor - b.floor || String(a.number).localeCompare(String(b.number)));

/**
 * Filas de alícuotas en el mismo orden que las unidades (handleSaveFinanzas
 * compara por índice). Una unidad "Alquiler del edificio" queda fija en 0
 * (`fija`): no paga gastos comunes y las demás suman 1000‰.
 */
const alicuotasDe = (unidades) =>
  unidades.map((u) => ({
    unitId: u.id,
    number: u.number,
    m2: u.m2 || "",
    valorCatastral: u.valorCatastral || "",
    alicuota: esAlquilerDelEdificio(u) ? 0 : (u.alicuota ?? ""),
    fija: esAlquilerDelEdificio(u),
  }));

/** Alícuotas estimadas en proporción a `campo` (m² o valor catastral); las fijas quedan en 0. */
const estimarAlicuotas = (filas, campo) => {
  const total = filas.filter((a) => !a.fija).reduce((s, a) => s + (Number(a[campo]) || 0), 0);
  return filas.map((a) => ({
    ...a,
    alicuota: a.fija ? (0).toFixed(4) : ((Number(a[campo]) || 0) / total * 1000).toFixed(4),
  }));
};

/** Propietarios (titular y complementario) de las unidades: opciones de la comisión. */
const propietariosDe = (unidades, residentes) => {
  const ids = new Set();
  unidades.forEach((u) => ["owner", "ownerSecondary"].forEach((f) => {
    const v = valorSlot(u[f]);
    if (v) ids.add(v);
  }));
  return residentes.filter((r) => ids.has(String(r.userid)));
};

// Rol del formulario de la comisión → campo del doc building.
const CAMPOS_COMISION = {
  presidente: "commissionPresidente",
  secretario: "commissionSecretario",
  tesorero: "commissionTesorero",
  primerVocal: "commissionPrimerVocal",
  segundoVocal: "commissionSegundoVocal",
};

/**
 * building.commissionNames (contrato C10 de las reglas 0D): las mismas claves
 * que los campos de la comisión (commissionPresidente...) y, en cada una, el
 * nombre a mostrar (≤ 200) o null. Lo lee el Inicio del residente, que no lee
 * fichas ajenas de users. El campo commission* guarda el userid; un valor con
 * espacios es un nombre escrito a mano (dato viejo) y va tal cual.
 *
 * Un userid cuya ficha no está entre los propietarios cargados: si el rol no
 * cambió desde que se abrió la pantalla, conserva el nombre ya guardado
 * (`previos`). Así una ficha que no se pudo leer (sin accessBids todavía, o la
 * lectura negada) no borra el nombre del Inicio al guardar otro rol. Si no
 * había nombre guardado, o el rol cambió a ese userid, queda en null.
 */
export const nombresDeComision = (comision, propietarios, { previos = null, originales = null } = {}) => {
  const nombres = {};
  Object.entries(CAMPOS_COMISION).forEach(([rol, campo]) => {
    const v = String(comision?.[rol] ?? "").trim();
    const ficha = v ? (propietarios || []).find((u) => String(u.userid) === v) : null;
    const previo = previos && typeof previos[campo] === "string" ? previos[campo].trim() : "";
    const sinCambio = v !== "" && v === String(originales?.[rol] ?? "").trim();
    const nombre = ficha
      ? String(ficha.name || [ficha.NombreCompleto, ficha.ApellidosCompleto].filter(Boolean).join(" ")).trim()
      : /\s/.test(v)
        ? v
        : sinCambio
          ? previo
          : "";
    nombres[campo] = nombre ? nombre.slice(0, 200) : null;
  });
  return nombres;
};

/** Ficha que representa a un userid repetido (dato viejo): el residente vigente primero. */
const rangoFicha = (u) =>
  (Number(u.typeuser ?? 0) === 0 ? 0 : 2) + (!u.archivedAt && u.mergedIntoUserid == null ? 0 : 1);

async function cargarUnidadesDelEdificio(buildingid) {
  const snap = await getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingid))));
  // El id real del doc va último: un campo `id` guardado en el doc no lo pisa.
  return ordenarUnidades(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
}

/** userid → clave canónica (102 | "102" | "0102" → "102"). */
const claveUserid = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

/**
 * Todas las fichas users de esos userids, vistas desde el edificio. C10 de las
 * reglas 0D: el staff lee una ficha ajena solo si la consulta fija accessBids
 * array-contains el edificio, así que van por fetchUsersByField("userid",
 * variantes, edificio) de firestoreHelpers (en tandas). Trae TODAS las fichas
 * de cada userid, sin valores por defecto: la elección (residente o
 * colaborador) es de quien llama. Lanza si la lectura se niega o falla.
 */
async function fichasUsersDelEdificio(userids, buildingid) {
  const claves = [...new Set((userids || []).map(claveUserid).filter(Boolean))];
  if (!claves.length) return [];
  return fetchUsersByField("userid", claves.flatMap((k) => tolerantId(k)), buildingid);
}

/**
 * Fichas users de los ocupantes de las unidades, una por userid (la de
 * residente vigente). Reemplaza el escaneo completo de users: solo se
 * consultan los userids de las unidades, con el edificio (C10).
 */
async function cargarResidentesDeUnidades(unidades, buildingid) {
  const ids = new Set();
  unidades.forEach((u) => SLOTS_UNIDAD.forEach((f) => {
    const v = valorSlot(u[f]);
    if (v) ids.add(v);
  }));
  const fichas = await fichasUsersDelEdificio([...ids], buildingid);
  const porUserid = new Map();
  fichas.forEach((u) => {
    const clave = String(u.userid ?? u.id);
    const previa = porUserid.get(clave);
    if (!previa || rangoFicha(u) < rangoFicha(previa)) porUserid.set(clave, u);
  });
  return [...porUserid.values()].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
  );
}

/**
 * Personal del edificio: cada employeebuilding con su ficha users. La ficha
 * editable es la del colaborador (typeuser 3); si un dato viejo solo tiene
 * otra, se muestra pero no se edita por callable. La colección employee (un
 * espejo de esa ficha) no se lee: con las reglas 0D el staff no la puede
 * listar (C2: list solo superadmin) y la consulta haría fallar toda la pestaña.
 * Las fichas van en una sola lectura con el edificio (C10); si se niega, el
 * personal sale igual, sin datos personales y sin edición por callable.
 */
async function cargarPersonalDelEdificio(buildingid) {
  const ebSnap = await getDocs(
    query(collection(db, "employeebuilding"), where("building", "in", tolerantId(buildingid)))
  );
  const vinculos = ebSnap.docs.map((d) => ({ ...d.data(), id: d.id }));
  let todas = [];
  try {
    todas = await fichasUsersDelEdificio(vinculos.map((eb) => eb.employeeid), buildingid);
  } catch (e) {
    console.warn("[EditarEdificio] No se pudieron leer las fichas del personal:", e?.code || e?.message);
  }
  const porUserid = new Map();
  todas.forEach((u) => {
    const clave = claveUserid(u.userid);
    if (!porUserid.has(clave)) porUserid.set(clave, []);
    porUserid.get(clave).push(u);
  });
  return vinculos.map((eb) => {
    const fichas = porUserid.get(claveUserid(eb.employeeid)) || [];
    const colaboradores = fichas.filter((x) => Number(x.typeuser) === 3);
    const ficha =
      colaboradores.find((x) => !x.archivedAt && x.mergedIntoUserid == null) || colaboradores[0] || null;
    return {
      ...eb,
      userData: ficha || fichas[0] || null,
      fichaEditable: !!ficha,
    };
  });
}

/** Fila de la carga masiva (Excel o IA) → unidad de importarUnidades. */
const unidadParaImportar = (u) => {
  const out = { number: textoLimpio(u.number), floor: textoLimpio(u.floor) };
  ["m2", "valorCatastral", "bedroom", "bathroom", "balcony", "garage", "infront"].forEach((k) => {
    const v = u[k];
    if (v == null || (typeof v === "string" && !v.trim())) return;
    out[k] = typeof v === "string" ? v.trim() : v;
  });
  const ocupantes = [];
  const agregar = (slot, nombre, apellidos, tipoDoc, documento, celular, email) => {
    const residente = {};
    if (textoLimpio(nombre)) residente.nombre = textoLimpio(nombre);
    if (textoLimpio(apellidos)) residente.apellidos = textoLimpio(apellidos);
    if (textoLimpio(documento)) residente.documento = textoLimpio(documento);
    if (textoLimpio(celular)) residente.celular = textoLimpio(celular);
    if (textoLimpio(email)) residente.email = textoLimpio(email);
    // Solo el tipo de documento no alcanza para cargar a alguien (como antes).
    if (!Object.keys(residente).length) return;
    if (textoLimpio(tipoDoc)) residente.tipoDocumento = textoLimpio(tipoDoc).replace(/[.\s]/g, "");
    ocupantes.push({ slot, residente });
  };
  agregar("owner", u.propNombre, u.propApellido, u.propTipoDoc, u.propCedula, u.propCelular, u.propEmail);
  agregar("ownerSecondary", u.propNombre2, u.propApellido2, u.propTipoDoc2, u.propCedula2, u.propCelular2, u.propEmail2);
  agregar("tenant", u.inqNombre, u.inqApellido, u.inqTipoDoc, u.inqCedula, u.inqCelular, u.inqEmail);
  agregar("tenantSecondary", u.inqNombre2, u.inqApellido2, u.inqTipoDoc2, u.inqCedula2, u.inqCelular2, u.inqEmail2);
  if (ocupantes.length) out.ocupantes = ocupantes;
  return out;
};

const etiquetaUnidad = (u) => `Unidad ${u.number || "?"} · Piso ${u.floor || "?"}`;

const EditarEdificio = ({ setSelected, setNavigationGuard, setUnidadSeleccionada, setUnidadBackTo, initialTab, setInitialTab, userType }) => {
  const { selectedBuilding, setSelectedBuilding, refreshBuildings } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const { rate: bcuRate, loading: bcuLoading } = useLatestRate();
  const [tab, setTab] = useState(initialTab || "datos");

  // Consumir la pestaña inicial (ej. volver a "Unidades" desde el detalle de
  // unidad) una sola vez, para que próximas aperturas usen la pestaña por defecto.
  useEffect(() => {
    if (initialTab && setInitialTab) setInitialTab(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google Maps Places Autocomplete para direccion
  const { isLoaded: gmapLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: gmapLibraries });
  const addressInputRef = React.useRef(null);
  const addressAutocompleteRef = React.useRef(null);

  // Registro de acciones para el tutorial: cambiar de tab automaticamente
  const { registerAction, unregisterAction } = useTutorial();
  useEffect(() => {
    const tabIds = ["datos", "admins", "unidades", "empleado", "amenities", "finanzas", "saldosproveedores", "comision", "comunicaciones"];
    tabIds.forEach((t) => {
      registerAction(`editar-ir-tab-${t}`, () => setTab(t));
    });
    return () => {
      tabIds.forEach((t) => unregisterAction(`editar-ir-tab-${t}`));
    };
  }, [registerAction, unregisterAction]);

  const [buildingData, setBuildingData] = useState(null);
  const [originalBuildingData, setOriginalBuildingData] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminId, setNewAdminId] = useState("");
  // La pestaña Administradores es solo de superadmin (setBuildingAdmin lo exige).
  // null mientras se averigua; después true o false.
  const [esSuperadmin, setEsSuperadmin] = useState(null);
  const [savingBuilding, setSavingBuilding] = useState(false);
  const [buildingErrors, setBuildingErrors] = useState({});
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [deletingBuilding, setDeletingBuilding] = useState(false);
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  // Status de la solicitud de baja abierta (ESTADOS_BAJA_ABIERTA) o null.
  const [bajaAbierta, setBajaAbierta] = useState(null);
  const hasPendingDeletion = !!bajaAbierta;

  const [units, setUnits] = useState([]);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [newUnit, setNewUnit] = useState({
    number: "", floor: "", bedroom: "", bathroom: "", m2: "",
    valorCatastral: "", balcony: false, garage: false,
    owner: "", ownerSecondary: "", tenant: "", tenantSecondary: "",
    unitKind: "normal",
  });
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [unitErrors, setUnitErrors] = useState({});
  const [showUnitActionsMenu, setShowUnitActionsMenu] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);
  const [addingUnit, setAddingUnit] = useState(false);
  const [deletingUnitId, setDeletingUnitId] = useState(null);
  // Desde qué fecha rige un cambio de ocupantes (tenencias): hoy en Montevideo por defecto.
  const [fechaCambioOcupantes, setFechaCambioOcupantes] = useState("");
  const defaultNewUnit = {
    number: "", floor: "", bedroom: "", bathroom: "", m2: "",
    valorCatastral: "", balcony: false, garage: false,
    owner: "", ownerSecondary: "", tenant: "", tenantSecondary: "",
    unitKind: "normal",
  };

  const [showAlicuotasModal, setShowAlicuotasModal] = useState(false);

  // Estado para modal de saldos iniciales masivos
  const [showInitialBalancesModal, setShowInitialBalancesModal] = useState(false);
  const [showEditPropietariosModal, setShowEditPropietariosModal] = useState(false);
  const [initialBalancesData, setInitialBalancesData] = useState([]);
  const [savingInitialBalances, setSavingInitialBalances] = useState(false);
  // Fecha del saldo (fecha de carga). Es la única fuente de verdad: el mes/año
  // del payment de migración se derivan de esta fecha. Por defecto, el último
  // día del mes actual al abrir el modal; el admin puede ajustarla.
  const [migrationMaturity, setMigrationMaturity] = useState("");
  useEffect(() => {
    if (!showInitialBalancesModal) return;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const lastDay = new Date(y, m, 0).getDate();
    setMigrationMaturity(`${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`);
  }, [showInitialBalancesModal]);

  // Estados para carga masiva y IA
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMode, setBulkMode] = useState("excel"); // "excel" | "ai"
  const [excelData, setExcelData] = useState([]);
  const [aiText, setAiText] = useState("");
  const [aiFiles, setAiFiles] = useState([]); // archivos de imagen/PDF
  const [processingAI, setProcessingAI] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { hechas, total } mientras se importa
  const [bulkResult, setBulkResult] = useState(null); // resultado por fila de la última carga masiva
  const fileInputRef = React.useRef(null);
  const aiFileInputRef = React.useRef(null);

  // Borrador de la carga con IA (tabla editable). `bid` viaja con las filas
  // para no guardar el borrador de un edificio en otro al cambiar de edificio;
  // `sucio` evita reescribir (y re-fechar) un borrador que solo se leyó.
  const [revision, setRevision] = useState({ bid: null, filas: [], sucio: false });
  const [revisionGuardadaEn, setRevisionGuardadaEn] = useState(null);
  const [showRevision, setShowRevision] = useState(false);

  useEffect(() => {
    const bid = buildingData?.buildingid;
    const b = bid != null ? leerBorrador(bid) : null;
    setRevision({ bid: bid ?? null, filas: b ? b.filas : [], sucio: false });
    setRevisionGuardadaEn(b ? b.actualizadoEn : null);
    setShowRevision(false);
  }, [buildingData?.buildingid]);

  // Se guarda en cada cambio (sin demora): salir de la pantalla no pierde lo último tipeado.
  useEffect(() => {
    if (!revision.sucio || revision.bid == null) return;
    setRevisionGuardadaEn(guardarBorrador(revision.bid, revision.filas));
  }, [revision]);

  const setFilasRevision = (filas) => setRevision((r) => ({ ...r, filas, sucio: true }));

  // Estados para consulta catastral
  const [showCatastroModal, setShowCatastroModal] = useState(false);
  const [catastroLoading, setCatastroLoading] = useState(false);
  const [catastroProgress, setCatastroProgress] = useState(0);
  const [catastroResults, setCatastroResults] = useState(null);
  const [catastroSelectedUnits, setCatastroSelectedUnits] = useState([]);
  const [catastroJobId, setCatastroJobId] = useState(null); // Job activo en Firestore
  const [catastroJobData, setCatastroJobData] = useState(null); // Datos del job (para banner)
  const catastroAbortRef = React.useRef(false);
  const catastroAbortControllerRef = React.useRef(null); // AbortController para cancelar fetch en curso

  // Inicializar autocomplete de direccion en tab Datos
  useEffect(() => {
    if (tab !== "datos" || !gmapLoaded || !addressInputRef.current || !buildingData) return;
    if (addressAutocompleteRef.current) {
      window.google?.maps?.event?.clearInstanceListeners(addressAutocompleteRef.current);
      addressAutocompleteRef.current = null;
    }
    const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ["address"],
      componentRestrictions: { country: ["uy"] },
    });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      let city = "";
      let department = "";
      let country = "";
      place.address_components?.forEach((c) => {
        if (c.types.includes("locality")) city = c.long_name;
        if (c.types.includes("administrative_area_level_1")) {
          const DEPARTAMENTOS_UY = ["Artigas", "Canelones", "Cerro Largo", "Colonia", "Durazno", "Flores", "Florida", "Lavalleja", "Maldonado", "Montevideo", "Paysandú", "Río Negro", "Rivera", "Rocha", "Salto", "San José", "Soriano", "Tacuarembó", "Treinta y Tres"];
          const candidates = [
            c.short_name,
            c.long_name,
            c.long_name.replace(/^Departamento de\s+/i, "").replace(/\s+Department$/i, "").trim(),
          ];
          for (const candidate of candidates) {
            const match = DEPARTAMENTOS_UY.find((d) => d.toLowerCase() === (candidate || "").toLowerCase());
            if (match) { department = match; break; }
          }
          if (!department) department = candidates[2] || c.long_name;
        }
        if (c.types.includes("country")) country = c.long_name;
      });
      setBuildingData((prev) => ({
        ...prev,
        address: place.formatted_address || prev.address,
        city: city || prev.city,
        department: department || prev.department,
        country: country || prev.country,
        lat,
        lng,
      }));
    });
    addressAutocompleteRef.current = ac;
    return () => {
      if (addressAutocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(addressAutocompleteRef.current);
        addressAutocompleteRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, gmapLoaded, buildingData === null]);

  const [residents, setResidents] = useState([]);
  const [buildingUsers, setBuildingUsers] = useState([]);

  // Solo se puede asignar como propietario/inquilino de una unidad a usuarios que
  // YA son residentes de este edificio (titular o adicional de alguna unidad).
  // Los residentes nuevos se dan de alta desde Gestión de residentes o por la
  // carga masiva, no desde acá. `id` es el doc users (lo que reciben los
  // callables); `value`, el userid que guarda la unidad.
  const buildingResidentOptions = useMemo(() => {
    const ids = new Set();
    const collect = (u) =>
      [u?.owner, u?.ownerSecondary, u?.tenant, u?.tenantSecondary].forEach((v) => {
        if (v != null && v !== "") ids.add(String(v));
      });
    units.forEach(collect);
    // No perder de vista los valores actuales de la unidad en edición.
    if (editingUnit) collect(editingUnit);
    return residents
      .filter((u) => ids.has(String(u.userid)))
      .map((u) => ({
        id: u.id,
        value: u.userid,
        label: `${u.name || u.NombreCompleto || ""}${u.email ? ` (${u.email})` : ""}`,
      }));
  }, [residents, units, editingUnit]);
  const noBuildingResidents = buildingResidentOptions.length === 0;
  const [commissionData, setCommissionData] = useState({
    presidente: "",
    secretario: "",
    tesorero: "",
    primerVocal: "",
    segundoVocal: "",
  });
  const [originalCommissionData, setOriginalCommissionData] = useState(null);
  const [savingCommission, setSavingCommission] = useState(false);

  const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  const [employees, setEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    NombreCompleto: "",
    ApellidosCompleto: "",
    tipoDocumento: "CI",
    Documento: "",
    email: "",
    celular: "",
    horarioInicio: "",
    horarioFin: "",
    diasTrabajo: [],
    sueldo: "",
    inicioActividad: "",
  });
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [employeeErrors, setEmployeeErrors] = useState({});
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [savingEmployeeEdit, setSavingEmployeeEdit] = useState(false);
  const [removingEmployeeId, setRemovingEmployeeId] = useState(null);

  // requestId por pedido: se reusa solo para reintentar el MISMO pedido (mismo
  // payload) después de un corte o una confirmación; el servidor devuelve lo ya
  // hecho sin repetirlo. Un pedido que terminó bien libera la clave.
  const intentosRef = React.useRef(new Map());
  const requestIdPara = (clave, payload) => {
    const firma = JSON.stringify(payload);
    const previo = intentosRef.current.get(clave);
    if (previo && previo.firma === firma) return previo.requestId;
    const requestId = nuevoRequestId();
    intentosRef.current.set(clave, { firma, requestId });
    return requestId;
  };
  const intentoTerminado = (clave) => intentosRef.current.delete(clave);

  /**
   * Confirmación que pide el servidor con failed-precondition: qué preguntar y
   * con qué flag reintentar. null si el error no se resuelve confirmando.
   */
  const confirmacionPedida = (e) => {
    if (codigoDeError(e) !== "failed-precondition") return null;
    const det = (e && e.details) || {};
    const msg = mensajeDeError(e);
    if (det.requiereConfirmacion === "mes_cerrado") {
      const meses = etiquetaMeses(det.meses);
      return {
        flag: "confirmarMesCerrado",
        titulo: "Mes con cierre",
        mensaje: meses && !msg.includes(meses) ? `${msg}\n\nMeses con cierre: ${meses}.` : msg,
        confirmLabel: "Confirmar igual",
      };
    }
    if (det.requiereConfirmacion === "porteria") {
      return { flag: "confirmarPorteria", titulo: "Pasar a portería", mensaje: msg, confirmLabel: "Pasar a portería", danger: true };
    }
    if (det.motivo === "sin_admins") {
      return { flag: "confirmarSinAdmins", titulo: "Edificio sin administradores", mensaje: msg, confirmLabel: "Quitar igual", danger: true };
    }
    if (det.motivo === "celular_compartido" || det.motivo === "telefono_compartido") {
      return { flag: "confirmarCompartido", titulo: "¿Celular compartido?", mensaje: msg, confirmLabel: "Es compartido, guardar" };
    }
    // D3 (createStaffUser con superadmin): la cuenta de ese email se activó con
    // un link anterior al sistema nuevo y puede ser de otra persona.
    if (det.motivo === "confirmar_link_en_claro") {
      return {
        flag: "confirmarLinkEnClaro",
        titulo: "Cuenta activada con un link anterior",
        mensaje: `${msg}\n\nSi no la reconoce, no sigas: que entre con «Olvidé mi contraseña» con su email y después repetí el alta.`,
        confirmLabel: "Ya la verifiqué, continuar",
        danger: true,
      };
    }
    if (det.requiereSuperadmin === true && esSuperadmin === true) {
      return {
        flag: "forzar",
        titulo: "Unidad con historia financiera",
        mensaje: `${msg}\n\nComo superadmin podés forzar la eliminación igual.`,
        confirmLabel: "Forzar eliminación",
        danger: true,
      };
    }
    return null;
  };

  /**
   * Llama a un callable de identidad resolviendo las confirmaciones del servidor:
   * pregunta con su mensaje y reintenta con el flag y el MISMO requestId (un
   * intento rechazado no queda guardado en el servidor). Devuelve la respuesta,
   * o null si el usuario no confirmó.
   */
  const llamarConConfirmaciones = async (fn, params, { clave, conRequestId = true } = {}) => {
    let actuales = conRequestId ? { ...params, requestId: requestIdPara(clave, params) } : { ...params };
    for (let intento = 0; intento < 6; intento += 1) {
      try {
        const res = await fn(actuales);
        if (conRequestId) intentoTerminado(clave);
        return res;
      } catch (e) {
        const conf = confirmacionPedida(e);
        if (!conf || actuales[conf.flag] !== undefined) throw e;
        const ok = await confirmDialog(conf.mensaje, {
          title: conf.titulo,
          confirmLabel: conf.confirmLabel,
          danger: !!conf.danger,
        });
        if (!ok) {
          if (conRequestId) intentoTerminado(clave);
          return null;
        }
        actuales = { ...actuales, [conf.flag]: true };
      }
    }
    throw new Error("No se pudo completar la operación. Reintentá.");
  };

  // Finanzas tab state
  const [finanzasData, setFinanzasData] = useState({
    distributionMethod: "alicuotas",
    interestMethod: "porcentaje",
    interestRate: "",
    interestFixedAmount: "",
    gracePeriodDays: "0",
    gracePeriodMode: "dias", // "dias" | "fin_de_mes" | "dia_fijo"
    graceUntilDay: "",
    moraIndexationEnabled: false,
    moraStartDate: "",
    reserveFundMethod: "",
    reserveFundPercentage: "",
    reserveFundFixedAmount: "",
    paymentDueDay: "",
    diaCorte: "",
    autoFineEnabled: false,
    autoFineDaysThreshold: "",
    autoFineAmount: "",
    autoFineCurrency: "UYU",
    bankAccounts: [], // [{ id, bank, accountType, accountNumber, currency }]
    cajaBankAccountId: "",
    reserveFundBankAccountId: "",
    cajaBankAccountIdUSD: "",
    reserveFundBankAccountIdUSD: "",
    initialBankBalance: "",
    initialBankBalanceUSD: "",
    initialReserveFund: "",
    initialReserveFundUSD: "",
    exchangeRateUSD: "",
    usdExpensePolicy: "convert_to_uyu",
  });
  const [originalFinanzasData, setOriginalFinanzasData] = useState(null);
  const [savingFinanzas, setSavingFinanzas] = useState(false);
  const [alicuotas, setAlicuotas] = useState([]); // [{unitId, number, m2, valorCatastral, alicuota}]
  // FIN-019: Fondos personalizados
  const [customFunds, setCustomFunds] = useState([]);
  const [originalCustomFunds, setOriginalCustomFunds] = useState(null);
  const [showFundModal, setShowFundModal] = useState(false);
  const [editingFund, setEditingFund] = useState(null);
  const [fundForm, setFundForm] = useState({ name: "", method: "percentage", percentage: "", fixedAmount: "", currency: "UYU", initialBalance: "", bankAccountId: "" });
  const [fundErrors, setFundErrors] = useState({});
  // Cuentas bancarias del edificio
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({ bank: "", accountType: "", accountNumber: "", currency: "UYU" });
  const [accountErrors, setAccountErrors] = useState({});

  const getResidentByUserid = (userid) =>
    residents.find((r) => String(r.userid) === String(userid));
  const hasValue = (v) => {
    if (v === null || v === undefined) return false;
    return String(v).trim() !== "";
  };
  const hasAssignedUser = (v) => {
    if (!hasValue(v)) return false;
    const s = String(v).trim().toLowerCase();
    return s !== "0" && s !== "null" && s !== "undefined" && s !== "nan";
  };
  const getResidentLabel = (userid) => {
    const r = getResidentByUserid(userid);
    const fullName = r
      ? `${String(r.NombreCompleto || "").trim()} ${String(r.ApellidosCompleto || "").trim()}`.trim()
      : "";
    const rawName = r ? String(fullName || r.name || "-") : "";
    const cleanName = rawName
      .replace(/\s*\([^)]*@[^)]*\)\s*/gi, " ") // quita cualquier "(mail@...)" en cualquier posición
      .replace(/\s*\(main\)\s*/gi, " ")
      .replace(/\s*\([^)]*\)\s*/g, " ")
      .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, " ") // quita email suelto
      .replace(/[\[\]<>]/g, " ")
      .replace(/\s{2,}/g, " ")
      .replace(/^[,;:.\-\s]+|[,;:.\-\s]+$/g, "")
      .trim();
    return r ? (cleanName || "-") : userid ? `ID: ${userid}` : "-";
  };
  const normalizeExcelKey = (key) =>
    String(key ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  const getExcelValue = (row, aliases) => {
    const entries = Object.entries(row || {});
    const lookup = new Map(entries.map(([k, v]) => [normalizeExcelKey(k), v]));
    for (const alias of aliases) {
      const value = lookup.get(normalizeExcelKey(alias));
      if (value !== undefined && value !== null && String(value).trim() !== "") return value;
    }
    return "";
  };
  const yesNoLabel = (value) => {
    // La carga masiva y el modal guardan booleanos; los datos viejos, "si"/"no".
    if (value === true) return "Sí";
    if (value === false) return "No";
    const v = String(value || "").trim().toLowerCase();
    if (["si", "sí", "s", "yes", "true", "1"].includes(v)) return "Sí";
    if (["no", "n", "false", "0"].includes(v)) return "No";
    return "-";
  };

  // ¿Quién mira la pantalla? Si App no pasa userType, se toma de user_access/{uid}
  // (lo mismo que mira el servidor) y, si ese doc no existe, del claim de rol.
  useEffect(() => {
    if (userType != null) {
      setEsSuperadmin(Number(userType) === 2);
      return undefined;
    }
    const user = auth.currentUser;
    if (!user) {
      setEsSuperadmin(false);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      let superadmin = false;
      try {
        const snap = await getDoc(doc(db, "user_access", user.uid));
        if (snap.exists()) superadmin = snap.data()?.superadmin === true;
        else superadmin = Number((await user.getIdTokenResult())?.claims?.habitia_typeuser) === 2;
      } catch (e) {
        console.warn("[EditarEdificio] No se pudo leer user_access:", e?.code || e?.message);
      }
      if (!cancelado) setEsSuperadmin(superadmin);
    })();
    return () => {
      cancelado = true;
    };
  }, [userType]);

  // Administradores del edificio y cuentas elegibles: solo los ve (y cambia) un superadmin.
  const buildingIdActual = buildingData?.buildingid;
  useEffect(() => {
    if (esSuperadmin !== true || buildingIdActual == null) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const [usersSnap, linksSnap] = await Promise.all([
          getDocs(query(collection(db, "users"), where("typeuser", "==", 1))),
          getDocs(query(collection(db, "admin"), where("buildingid", "in", tolerantId(buildingIdActual)))),
        ]);
        if (cancelado) return;
        setAdminUsers(usersSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
        const vistos = new Set();
        const lista = [];
        linksSnap.docs.forEach((d) => {
          const userid = d.data().userid;
          if (userid == null || vistos.has(String(userid))) return;
          vistos.add(String(userid));
          lista.push({ userid });
        });
        setAdmins(lista);
      } catch (e) {
        console.error("Error cargando administradores:", e);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [esSuperadmin, buildingIdActual]);

  // Si la pestaña Administradores quedó elegida (tutorial, pestaña inicial) y la
  // persona no es superadmin, se vuelve a Datos.
  useEffect(() => {
    if (tab === "admins" && esSuperadmin === false) setTab("datos");
  }, [tab, esSuperadmin]);

  useEffect(() => {
    if (!selectedBuilding) return;

    const fetchData = async () => {
      const buildingSnap = await getDoc(doc(db, "building", selectedBuilding.id));
      // Preservamos el doc id en el state local. Esto desacopla la pantalla del
      // selectedBuilding del context, que puede volverse null si el filtro
      // global del header cambia a "Todos los edificios" mientras estamos
      // editando (sin ese id local, upload/delete de reglamentos crashea).
      const buildingInfo = { ...buildingSnap.data(), id: buildingSnap.id };
      setBuildingData(buildingInfo);
      setOriginalBuildingData({ ...buildingInfo });

      // Load finanzas fields from building doc
      const fin = {
        distributionMethod: buildingInfo.distributionMethod || "alicuotas",
        interestMethod: buildingInfo.interestMethod || "porcentaje",
        interestRate: buildingInfo.interestRate ?? "",
        moraIndexationEnabled: buildingInfo.moraIndexationEnabled === true,
        moraStartDate: buildingInfo.moraStartDate ?? "",
        interestFixedAmount: buildingInfo.interestFixedAmount ?? "",
        gracePeriodDays: buildingInfo.gracePeriodDays ?? "0",
        gracePeriodMode: buildingInfo.gracePeriodMode || "dias",
        graceUntilDay: buildingInfo.graceUntilDay ?? "",
        reserveFundMethod: buildingInfo.reserveFundMethod || "",
        reserveFundPercentage: buildingInfo.reserveFundPercentage ?? "",
        reserveFundFixedAmount: buildingInfo.reserveFundFixedAmount ?? "",
        paymentDueDay: buildingInfo.paymentDueDay ?? "",
        diaCorte: buildingInfo.diaCorte ?? "",
        autoFineEnabled: buildingInfo.autoFineEnabled || false,
        autoFineDaysThreshold: buildingInfo.autoFineDaysThreshold ?? "",
        autoFineAmount: buildingInfo.autoFineAmount ?? "",
        autoFineCurrency: buildingInfo.autoFineCurrency ?? "UYU",
        // Migrar bankAccountName viejo (string) a bankAccounts (array estructurado) si no existe todavía
        bankAccounts: Array.isArray(buildingInfo.bankAccounts) && buildingInfo.bankAccounts.length > 0
          ? buildingInfo.bankAccounts
          : (buildingInfo.bankAccountName
              ? [{
                  id: `legacy-${Date.now()}`,
                  bank: buildingInfo.bankAccountName,
                  accountType: "",
                  accountNumber: "",
                  currency: "UYU",
                }]
              : []),
        cajaBankAccountId: buildingInfo.cajaBankAccountId ?? "",
        reserveFundBankAccountId: buildingInfo.reserveFundBankAccountId ?? "",
        cajaBankAccountIdUSD: buildingInfo.cajaBankAccountIdUSD ?? "",
        reserveFundBankAccountIdUSD: buildingInfo.reserveFundBankAccountIdUSD ?? "",
        initialBankBalance: buildingInfo.initialBankBalance ?? "",
        initialBankBalanceUSD: buildingInfo.initialBankBalanceUSD ?? "",
        initialReserveFund: buildingInfo.initialReserveFund ?? "",
        initialReserveFundUSD: buildingInfo.initialReserveFundUSD ?? "",
        exchangeRateUSD: buildingInfo.exchangeRateUSD ?? "",
        usdExpensePolicy: buildingInfo.usdExpensePolicy ?? "convert_to_uyu",
      };
      setFinanzasData(fin);
      setOriginalFinanzasData({ ...fin });

      // FIN-019: Cargar fondos personalizados
      setCustomFunds(buildingInfo.customFunds || []);
      setOriginalCustomFunds(buildingInfo.customFunds || []);

      // Cargar datos de comision del edificio
      const commFields = {
        presidente: buildingInfo.commissionPresidente || "",
        secretario: buildingInfo.commissionSecretario || "",
        tesorero: buildingInfo.commissionTesorero || "",
        primerVocal: buildingInfo.commissionPrimerVocal || "",
        segundoVocal: buildingInfo.commissionSegundoVocal || "",
      };
      setCommissionData(commFields);
      setOriginalCommissionData({ ...commFields });

      // Unidades y las fichas de sus ocupantes (nombres, opciones de los selectores
      // y propietarios para la comisión). Sin escanear toda la colección users:
      // solo las fichas del edificio (C10, accessBids).
      const unitList = await cargarUnidadesDelEdificio(buildingInfo.buildingid);
      let residentsList = [];
      try {
        residentsList = await cargarResidentesDeUnidades(unitList, buildingInfo.buildingid);
      } catch (e) {
        console.warn("[EditarEdificio] No se pudieron leer los ocupantes:", e?.code || e?.message);
      }
      setUnits(unitList);
      setResidents(residentsList);
      setBuildingUsers(propietariosDe(unitList, residentsList));
      setAlicuotas(alicuotasDe(unitList));

      try {
        setEmployees(await cargarPersonalDelEdificio(buildingInfo.buildingid));
      } catch (e) {
        console.warn("[EditarEdificio] No se pudo leer el personal:", e?.code || e?.message);
      }
    };

    fetchData().catch((e) => console.error("Error cargando el edificio:", e));
  }, [selectedBuilding]);

  /** Vuelve a leer las unidades y sus ocupantes (después de altas por callable). */
  const recargarUnidades = async () => {
    const bid = buildingData?.buildingid;
    if (bid == null) return;
    try {
      const unitList = await cargarUnidadesDelEdificio(bid);
      const residentsList = await cargarResidentesDeUnidades(unitList, bid).catch((e) => {
        console.warn("[EditarEdificio] No se pudieron leer los ocupantes:", e?.code || e?.message);
        return residents;
      });
      setUnits(unitList);
      setResidents(residentsList);
      setBuildingUsers(propietariosDe(unitList, residentsList));
      setAlicuotas(alicuotasDe(unitList));
    } catch (e) {
      console.error("Error recargando unidades:", e);
    }
  };

  const recargarPersonal = async () => {
    const bid = buildingData?.buildingid;
    if (bid == null) return;
    try {
      setEmployees(await cargarPersonalDelEdificio(bid));
    } catch (e) {
      console.error("Error recargando el personal:", e);
    }
  };

  // Verificar si ya existe una solicitud de eliminación abierta
  useEffect(() => {
    if (!selectedBuilding?.id) return;
    const checkPending = async () => {
      const q = query(
        collection(db, "building_deletion_requests"),
        where("buildingDocId", "==", selectedBuilding.id),
        where("status", "in", ESTADOS_BAJA_ABIERTA)
      );
      const snap = await getDocs(q);
      setBajaAbierta(estadoBajaAbierta(snap));
    };
    checkPending();
  }, [selectedBuilding?.id]);

  const isBuildingDirty = useMemo(() => {
    if (!buildingData || !originalBuildingData) return false;
    return CAMPOS_DATOS_EDIFICIO.some((f) => String(buildingData[f] ?? "") !== String(originalBuildingData[f] ?? ""));
  }, [buildingData, originalBuildingData]);

  const isCommissionDirty = useMemo(() => {
    if (!originalCommissionData) return false;
    return Object.keys(commissionData).some(
      (k) => String(commissionData[k] || "") !== String(originalCommissionData[k] || "")
    );
  }, [commissionData, originalCommissionData]);

  const handleSaveCommission = async () => {
    setSavingCommission(true);
    try {
      // Nombres para el Inicio del residente, que no lee fichas ajenas de users
      // (C10). Se reescriben enteros en cada guardado; un rol sin cambios cuya
      // ficha no se pudo leer conserva el nombre ya guardado (no queda en null).
      const commissionNames = nombresDeComision(commissionData, buildingUsers, {
        previos: buildingData?.commissionNames,
        originales: originalCommissionData,
      });
      await updateDoc(doc(db, "building", selectedBuilding.id), {
        commissionPresidente: commissionData.presidente || "",
        commissionSecretario: commissionData.secretario || "",
        commissionTesorero: commissionData.tesorero || "",
        commissionPrimerVocal: commissionData.primerVocal || "",
        commissionSegundoVocal: commissionData.segundoVocal || "",
        commissionNames,
      });
      setBuildingData((prev) => (prev ? { ...prev, commissionNames } : prev));
      setOriginalCommissionData({ ...commissionData });
      showToast("Comisión guardada correctamente", "success");
    } catch (e) {
      console.error("Error guardando comisión:", e);
      showToast("Error al guardar la comisión", "error");
    } finally {
      setSavingCommission(false);
    }
  };

  const handleSaveBuilding = async () => {
    // Sincronizar direccion desde el input no controlado (Google Places)
    const currentAddress = addressInputRef.current ? addressInputRef.current.value : buildingData.address;
    const dataToSave = { ...buildingData, address: currentAddress };

    const requiredFields = ["name", "address", "city", "department", "country"];
    const errorMap = {};
    requiredFields.forEach((f) => { if (!dataToSave[f]?.trim()) errorMap[f] = "Requerido"; });
    if (Object.keys(errorMap).length > 0) {
      setBuildingErrors(errorMap);
      showToast("Completá todos los campos obligatorios (*)", "warning");
      return;
    }
    setBuildingErrors({});
    setSavingBuilding(true);
    try {
      // Solo los campos de este formulario: antes se mandaba el doc entero (pisaba
      // la configuración financiera con la copia cargada y grababa un campo `id`).
      // Firestore no acepta undefined: se omiten.
      const cleanData = {};
      CAMPOS_DATOS_EDIFICIO.forEach((k) => {
        if (dataToSave[k] !== undefined) cleanData[k] = dataToSave[k];
      });
      await updateDoc(doc(db, "building", buildingData.id || selectedBuilding.id), cleanData);
      setBuildingData(dataToSave);
      setOriginalBuildingData({ ...dataToSave });
      // Actualizar el context para que los cambios se reflejen sin recargar
      if (refreshBuildings) refreshBuildings();
      showToast("Cambios guardados correctamente", "success");
    } catch (e) {
      console.error("Error guardando edificio:", e);
      showToast("Error al guardar", "error");
    } finally {
      setSavingBuilding(false);
    }
  };

  // Administradores: el link admin lo escribe setBuildingAdmin (solo superadmin),
  // que devuelve la lista vigente después del cambio.
  const aplicarAdmins = (res) => setAdmins((res?.adminUserids || []).map((userid) => ({ userid })));

  const handleAddAdmin = async () => {
    if (!newAdminId || savingAdmin) return;
    setSavingAdmin(true);
    try {
      const res = await setBuildingAdmin({
        buildingId: buildingData.buildingid,
        accion: "agregar",
        userDocId: newAdminId,
      });
      aplicarAdmins(res);
      setNewAdminId("");
      if (res?.cambio) showToast("Administrador agregado.", "success");
      else showToast("Este usuario ya es administrador del edificio.", "warning");
    } catch (e) {
      console.error("Error agregando admin:", e);
      showToast(mensajeDeError(e, "No se pudo agregar el administrador"), "error");
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userid) => {
    if (savingAdmin || userid == null || userid === "") return;
    setSavingAdmin(true);
    try {
      // userid del chip: sirve aunque la ficha users ya no exista.
      const res = await llamarConConfirmaciones(
        setBuildingAdmin,
        { buildingId: buildingData.buildingid, accion: "quitar", userid },
        { conRequestId: false }
      );
      if (!res) return;
      aplicarAdmins(res);
      if (res.cambio) showToast("Administrador quitado.", "success");
      else showToast("Ese usuario ya no era administrador del edificio.", "info");
    } catch (e) {
      console.error("Error eliminando admin:", e);
      showToast(mensajeDeError(e, "No se pudo quitar el administrador"), "error");
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleTabChange = async (newTab) => {
    if (tab === "finanzas" && isFinanzasDirty && newTab !== "finanzas") {
      const leave = await confirmDialog(
        "Tenés cambios sin guardar en la configuración financiera. Si salís ahora, los cambios se van a perder.",
        { title: "Cambios sin guardar", danger: true, confirmLabel: "Salir sin guardar", cancelLabel: "Quedarme" }
      );
      if (!leave) return;
    }
    setTab(newTab);
  };

  const handleBack = () => {
    setSelected("edificios");
  };

  const handleDeleteBuilding = () => {
    if (hasPendingDeletion) return;
    if (!selectedBuilding || !buildingData) return;
    setShowDeleteReasonModal(true);
  };

  const handleSubmitDeletionRequest = async () => {
    if (!deleteReason.trim()) return;
    if (deletingBuilding) return;
    setDeletingBuilding(true);
    try {
      // Verificar que no exista otra solicitud abierta (pendiente, en curso o con error)
      const q = query(
        collection(db, "building_deletion_requests"),
        where("buildingDocId", "==", selectedBuilding.id),
        where("status", "in", ESTADOS_BAJA_ABIERTA)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        setBajaAbierta(estadoBajaAbierta(existing));
        showToast("Ya existe una solicitud de eliminación abierta para este edificio", "warning");
        setShowDeleteReasonModal(false);
        setDeletingBuilding(false);
        return;
      }

      await addDoc(collection(db, "building_deletion_requests"), {
        buildingDocId: selectedBuilding.id,
        buildingid: buildingData.buildingid,
        buildingName: buildingData.name || "Sin nombre",
        requestedBy: auth.currentUser?.email || "desconocido",
        requestedByUid: auth.currentUser?.uid || null,
        reason: deleteReason.trim(),
        status: "pendiente",
        adminResponse: "",
        respondedBy: null,
        respondedByUid: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await logAudit("request_building_deletion", {
        buildingId: buildingData.buildingid,
        entityId: selectedBuilding.id,
        entityType: "building",
        newValue: { buildingName: buildingData.name, reason: deleteReason.trim() },
      });

      showToast("Solicitud de eliminación enviada. Un superadmin debe aprobarla.", "success");
      setBajaAbierta("pendiente");
      setShowDeleteReasonModal(false);
      setDeleteReason("");
    } catch (e) {
      console.error("Error creando solicitud de eliminación:", e);
      showToast("No se pudo enviar la solicitud de eliminación", "error");
    } finally {
      setDeletingBuilding(false);
    }
  };


  const openEditUnit = (unit) => {
    setEditingUnit({ ...unit });
    setFechaCambioOcupantes(hoyMontevideo());
    setShowUnitModal(true);
  };

  const cerrarModalUnidad = () => {
    if (savingUnit) return;
    setShowUnitModal(false);
  };

  /**
   * Guardar unidad. Ocupantes y tipo de unidad van por assignOccupants (el
   * servidor registra las tenencias y los vínculos con el edificio); los campos
   * físicos que cambiaron, por updateDoc con la lista permitida, sin el spread
   * de la unidad (que llevaba id, building y ocupantes).
   */
  const handleSaveUnit = async () => {
    if (!editingUnit || savingUnit) return;
    const prevUnit = units.find((u) => u.id === editingUnit.id) || {};
    const nuevos = Object.fromEntries(SLOTS_UNIDAD.map((f) => [f, valorSlot(editingUnit[f])]));

    const kindNuevo = normKind(editingUnit);
    // "Alquiler del edificio": el dueño es el edificio, la unidad no lleva propietario.
    const sinPropietario = kindNuevo === UNIT_KIND_ALQUILER;
    if (sinPropietario) {
      nuevos.owner = "";
      nuevos.ownerSecondary = "";
    } else if (!nuevos.owner) {
      showToast("El campo Propietario es obligatorio.", "warning");
      return;
    }
    const participants = SLOTS_UNIDAD.map((f) => nuevos[f]).filter(Boolean);
    if (new Set(participants).size !== participants.length) {
      showToast("No se puede repetir el mismo usuario en titular/secundario de propietario o inquilino.", "warning");
      return;
    }
    if (!textoLimpio(editingUnit.number) || !textoLimpio(editingUnit.floor)) {
      showToast("Completá el número y el piso de la unidad.", "warning");
      return;
    }

    // Solo los lugares que cambian: así no se pisa lo que otro staff cambió en
    // los demás. El residente elegido va por su doc users (option.id).
    const slots = {};
    SLOTS_UNIDAD.forEach((f) => {
      if (nuevos[f] === valorSlot(prevUnit[f])) return;
      if (!nuevos[f]) {
        slots[f] = "";
        return;
      }
      const opcion = buildingResidentOptions.find((o) => String(o.value) === nuevos[f]);
      slots[f] = opcion?.id || nuevos[f];
    });
    const cambianOcupantes = Object.keys(slots).length > 0;
    const cambiaTipo = kindNuevo !== normKind(prevUnit);
    // Pasar a "Alquiler del edificio" una unidad con propietario se lo quita (va en
    // la misma llamada a assignOccupants): se avisa antes.
    if (sinPropietario && cambiaTipo && (valorSlot(prevUnit.owner) || valorSlot(prevUnit.ownerSecondary))) {
      const ok = await confirmDialog(
        "La unidad tiene propietario. Al pasarla a Alquiler del edificio se le quita: el dueño pasa a ser el edificio y esa persona deja de tener acceso por esta unidad. ¿Seguimos?",
        { title: "Pasar a Alquiler del edificio", confirmLabel: "Quitar propietario y seguir", danger: true }
      );
      if (!ok) return;
    }
    const hoy = hoyMontevideo();
    const fechaEfectiva = fechaCambioOcupantes || hoy;
    if (cambianOcupantes && fechaEfectiva > hoy) {
      showToast("La fecha del cambio de ocupantes no puede ser futura.", "warning");
      return;
    }

    const fisicos = camposFisicosDe(editingUnit);
    const cambiosFisicos = {};
    CAMPOS_FISICOS_UNIDAD.forEach((k) => {
      if (comparableFisico(k, fisicos[k]) !== comparableFisico(k, prevUnit[k])) cambiosFisicos[k] = fisicos[k];
    });
    const hayFisicos = Object.keys(cambiosFisicos).length > 0;

    if (!cambianOcupantes && !cambiaTipo && !hayFisicos) {
      setShowUnitModal(false);
      return;
    }

    setSavingUnit(true);
    try {
      let res = null;
      if (cambianOcupantes || cambiaTipo) {
        try {
          res = await llamarConConfirmaciones(
            assignOccupants,
            {
              apartmentId: editingUnit.id,
              buildingId: buildingData.buildingid,
              ...(cambianOcupantes ? { slots, fechaEfectiva } : {}),
              ...(cambiaTipo ? { unitKind: kindNuevo } : {}),
            },
            { clave: `assignOccupants:${editingUnit.id}` }
          );
        } catch (e) {
          if (esAsignacionEnRevision(e)) {
            // Nada cambió en el servidor: el modal queda abierto para elegir a
            // otra persona o guardar sin ese cambio de ocupante.
            showToast(textoAsignacionEnRevision(e), "warning");
            return;
          }
          console.error("Error guardando ocupantes:", e);
          showToast(mensajeDeError(e, "No se pudo guardar la unidad"), "error");
          // Otro cambio en el medio: se recargan las unidades para reintentar sobre lo vigente.
          if (codigoDeError(e) === "aborted") await recargarUnidades();
          return;
        }
        if (!res) return; // No confirmó: no se guardó nada.
      }

      let fisicosGuardados = !hayFisicos;
      if (hayFisicos) {
        try {
          await updateDoc(doc(db, "apartments", editingUnit.id), { ...cambiosFisicos, updatedAt: serverTimestamp() });
          fisicosGuardados = true;
        } catch (e) {
          console.error("Error guardando datos de la unidad:", e);
        }
      }

      const actualizada = {
        ...prevUnit,
        ...(fisicosGuardados ? cambiosFisicos : {}),
        ...(res ? { ...(res.ocupantes || {}), unitKind: res.unitKind } : {}),
      };
      const nuevasUnidades = units.map((u) => (u.id === editingUnit.id ? actualizada : u));
      setUnits(nuevasUnidades);
      setAlicuotas((prev) => prev.map((a) => (a.unitId === editingUnit.id
        ? {
            ...a,
            number: actualizada.number,
            m2: actualizada.m2 || "",
            valorCatastral: actualizada.valorCatastral || "",
            fija: esAlquilerDelEdificio(actualizada),
            ...(esAlquilerDelEdificio(actualizada) ? { alicuota: 0 } : {}),
          }
        : a)));
      if (res) setBuildingUsers(propietariosDe(nuevasUnidades, residents));

      if (!fisicosGuardados) {
        showToast(
          res ? "Se guardaron los ocupantes, pero no los demás datos de la unidad. Reintentá." : "No se pudo guardar la unidad",
          "error"
        );
        return;
      }
      setShowUnitModal(false);
      showToast(
        res && cambianOcupantes && !res.sinCambios
          ? `Unidad actualizada. El cambio de ocupantes rige desde el ${fechaLegible(res.fechaEfectiva || fechaEfectiva)}.`
          : "Unidad actualizada.",
        "success"
      );
    } finally {
      setSavingUnit(false);
    }
  };

  /** Baja de unidad por deleteUnit: cierra tenencias y vínculos en el servidor. */
  const handleDeleteUnit = async (id) => {
    if (deletingUnitId) return;
    const ok = await confirmDialog(
      "¿Eliminar esta unidad? Los usuarios asociados serán desvinculados de esta unidad pero no se eliminarán sus cuentas.",
      { title: "Eliminar unidad", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;
    const quitarDeLaLista = () => {
      const restantes = units.filter((u) => u.id !== id);
      setUnits(restantes);
      setAlicuotas((prev) => prev.filter((a) => a.unitId !== id));
      setBuildingUsers(propietariosDe(restantes, residents));
    };
    setDeletingUnitId(id);
    try {
      // Con historia financiera el servidor la frena; un superadmin puede forzar.
      const res = await llamarConConfirmaciones(
        deleteUnit,
        { apartmentId: id, confirmar: true, buildingId: buildingData.buildingid },
        { clave: `deleteUnit:${id}` }
      );
      if (!res) return;
      quitarDeLaLista();
      showToast(res.forzado ? "Unidad eliminada (tenía historia financiera)." : "Unidad eliminada.", "success");
    } catch (e) {
      if (codigoDeError(e) === "not-found") {
        quitarDeLaLista();
        showToast("Esa unidad ya no existía.", "info");
        return;
      }
      console.error("Error eliminando unidad:", e);
      showToast(mensajeDeError(e, "No se pudo eliminar la unidad"), "error");
      if (codigoDeError(e) === "aborted") await recargarUnidades();
    } finally {
      setDeletingUnitId(null);
    }
  };

  /** Alta de una unidad: importarUnidades con una sola fila (sin tenencias, como antes). */
  const handleAddUnit = async () => {
    if (addingUnit) return;
    const ue = {};
    if (!textoLimpio(newUnit.number)) ue.number = "Requerido";
    if (!textoLimpio(newUnit.floor)) ue.floor = "Requerido";
    if (Object.keys(ue).length > 0) {
      setUnitErrors(ue);
      showToast("Completá todos los campos obligatorios (*)", "warning");
      return;
    }
    setUnitErrors({});

    const alreadyExists = units.find(
      (u) => String(u.number) === String(newUnit.number) && String(u.floor) === String(newUnit.floor)
    );
    if (alreadyExists) return showToast("Ya existe una unidad con ese número y piso.", "warning");

    // Ocupantes: residentes que ya son del edificio, por su doc users (no el userid).
    // Una unidad "Alquiler del edificio" no lleva propietario (el dueño es el edificio).
    const kindNueva = normKind(newUnit);
    const sinPropietario = kindNueva === UNIT_KIND_ALQUILER;
    const ocupantes = [];
    const elegidos = [];
    for (const slot of SLOTS_UNIDAD) {
      if (sinPropietario && (slot === "owner" || slot === "ownerSecondary")) continue;
      const v = valorSlot(newUnit[slot]);
      if (!v) continue;
      const opcion = buildingResidentOptions.find((o) => String(o.value) === v);
      if (!opcion?.id) {
        showToast(`Revisá ${ETIQUETA_SLOT[slot]}: elegí un residente de la lista.`, "warning");
        return;
      }
      elegidos.push(v);
      ocupantes.push({ slot, userDocId: opcion.id });
    }
    if (new Set(elegidos).size !== elegidos.length) {
      showToast("No se puede repetir el mismo usuario en titular/secundario de propietario o inquilino.", "warning");
      return;
    }

    const unidad = {
      number: textoLimpio(newUnit.number),
      floor: textoLimpio(newUnit.floor),
      m2: newUnit.m2,
      bedroom: newUnit.bedroom,
      bathroom: newUnit.bathroom,
      valorCatastral: newUnit.valorCatastral,
      balcony: newUnit.balcony,
      garage: newUnit.garage,
      ...(textoLimpio(newUnit.orientation) ? { infront: textoLimpio(newUnit.orientation) } : {}),
      ...(kindNueva !== "normal" ? { unitKind: kindNueva } : {}),
      ...(ocupantes.length ? { ocupantes } : {}),
    };
    const params = { buildingId: buildingData.buildingid, unidades: [unidad] };
    const clave = "agregarUnidad";
    setAddingUnit(true);
    try {
      const res = await importarUnidades({ ...params, requestId: requestIdPara(clave, params) });
      intentoTerminado(clave);
      const fila = (res?.filas || [])[0] || {};
      if (fila.estado === "creada") {
        setNewUnit({ ...defaultNewUnit });
        setShowAddUnitModal(false);
        showToast("Unidad agregada correctamente", "success");
        await recargarUnidades();
      } else if (fila.estado === "omitida") {
        showToast("Ya existe una unidad con ese número y piso.", "warning");
        await recargarUnidades();
      } else if (esFilaEnRevision(fila)) {
        // K2: no es un error de carga; esa persona queda para un superadmin.
        showToast(`${textoFilaEnRevision(fila)} Podés agregarla sin esa persona.`, "warning");
      } else {
        showToast(fila.error || "No se pudo agregar la unidad", "error");
      }
    } catch (e) {
      // Se conserva el requestId: reintentar lo mismo no duplica la unidad.
      console.error("Error agregando unidad:", e);
      showToast(mensajeDeError(e, "No se pudo agregar la unidad"), "error");
    } finally {
      setAddingUnit(false);
    }
  };

  // ========== FUNCIONES PARA CARGA MASIVA ==========
  
  // Descargar template Excel
  const downloadExcelTemplate = async () => {
    const templateData = [
      {
        numero: "101", piso: "1", m2: "50", valor_catastral: "150000", dormitorios: "2", banos: "1", balcon: "si", garage: "no", orientacion: "frente",
        "prop_tipo_doc (si hay 2 separar con /)": "CI/CI",
        "prop_nombre (si hay 2 separar con /)": "Juan Pérez/Ana Pérez", "prop_cedula (si hay 2 separar con /)": "12345678/23456789", "prop_celular (si hay 2 separar con /)": "099123456/098111222", "prop_email (si hay 2 separar con /)": "juan@mail.com/ana@mail.com",
        "inq_tipo_doc (si hay 2 separar con /)": "",
        "inq_nombre (si hay 2 separar con /)": "", "inq_cedula (si hay 2 separar con /)": "", "inq_celular (si hay 2 separar con /)": "", "inq_email (si hay 2 separar con /)": "",
      },
      {
        numero: "102", piso: "1", m2: "55", valor_catastral: "165000", dormitorios: "2", banos: "1", balcon: "no", garage: "si", orientacion: "contrafrente",
        "prop_tipo_doc (si hay 2 separar con /)": "CI",
        "prop_nombre (si hay 2 separar con /)": "María García", "prop_cedula (si hay 2 separar con /)": "87654321", "prop_celular (si hay 2 separar con /)": "098765432", "prop_email (si hay 2 separar con /)": "maria@mail.com",
        "inq_tipo_doc (si hay 2 separar con /)": "CI/CI",
        "inq_nombre (si hay 2 separar con /)": "Carlos López/Lucía López", "inq_cedula (si hay 2 separar con /)": "11223344/22334455", "inq_celular (si hay 2 separar con /)": "097111222/092333444", "inq_email (si hay 2 separar con /)": "carlos@mail.com/lucia@mail.com",
      },
      {
        numero: "201", piso: "2", m2: "60", valor_catastral: "185000", dormitorios: "3", banos: "2", balcon: "si", garage: "si", orientacion: "frente",
        "prop_tipo_doc (si hay 2 separar con /)": "",
        "prop_nombre (si hay 2 separar con /)": "", "prop_cedula (si hay 2 separar con /)": "", "prop_celular (si hay 2 separar con /)": "", "prop_email (si hay 2 separar con /)": "",
        "inq_tipo_doc (si hay 2 separar con /)": "",
        "inq_nombre (si hay 2 separar con /)": "", "inq_cedula (si hay 2 separar con /)": "", "inq_celular (si hay 2 separar con /)": "", "inq_email (si hay 2 separar con /)": "",
      },
    ];

    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(templateData);

    // Ajustar ancho de columnas
    worksheet['!cols'] = [
      { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 16 }, { wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 15 },
      { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 22 },
      { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unidades");

    XLSX.writeFile(workbook, `template_unidades_${buildingData?.name || "edificio"}.xlsx`);
  };

  // Procesar archivo Excel
  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const XLSX = await import("xlsx");
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        const splitDual = (value) => {
          const raw = String(value ?? "");
          const [first = "", second = ""] = raw.split("/");
          return [first.trim(), second.trim()];
        };

        const parsed = jsonData.map(row => {
          const [propCedula, propCedula2] = splitDual(getExcelValue(row, ["prop_cedula", "prop_ci", "prop_documento", "propietario_cedula", "propietario_documento", "prop_cedula_si_hay_2_separar_con"]));
          const [propTipoDoc, propTipoDoc2] = splitDual(getExcelValue(row, ["prop_tipo_doc", "prop_tipo_documento", "propietario_tipo_doc", "propietario_tipo_documento", "prop_tipo_doc_si_hay_2_separar_con"]));
          const [propEmail, propEmail2] = splitDual(getExcelValue(row, ["prop_email", "propietario_email", "prop_correo", "prop_mail", "prop_email_si_hay_2_separar_con"]));
          const [propCelular, propCelular2] = splitDual(getExcelValue(row, ["prop_celular", "prop_telefono", "propietario_celular", "propietario_telefono", "prop_celular_si_hay_2_separar_con"]));
          const [propNombre, propNombre2] = splitDual(getExcelValue(row, ["prop_nombre", "propietario_nombre", "propietario", "prop_nombre_si_hay_2_separar_con"]));

          const [inqCedula, inqCedula2] = splitDual(getExcelValue(row, ["inq_cedula", "inq_ci", "inq_documento", "inquilino_cedula", "inquilino_documento", "inq_cedula_si_hay_2_separar_con"]));
          const [inqTipoDoc, inqTipoDoc2] = splitDual(getExcelValue(row, ["inq_tipo_doc", "inq_tipo_documento", "inquilino_tipo_doc", "inquilino_tipo_documento", "inq_tipo_doc_si_hay_2_separar_con"]));
          const [inqEmail, inqEmail2] = splitDual(getExcelValue(row, ["inq_email", "inquilino_email", "inq_correo", "inq_mail", "inq_email_si_hay_2_separar_con"]));
          const [inqCelular, inqCelular2] = splitDual(getExcelValue(row, ["inq_celular", "inq_telefono", "inquilino_celular", "inquilino_telefono", "inq_celular_si_hay_2_separar_con"]));
          const [inqNombre, inqNombre2] = splitDual(getExcelValue(row, ["inq_nombre", "inquilino_nombre", "inquilino", "inq_nombre_si_hay_2_separar_con"]));

          return ({
          number: String(getExcelValue(row, ["numero", "number", "unidad"])),
          floor: String(getExcelValue(row, ["piso", "floor"])),
          m2: String(getExcelValue(row, ["m2", "metros", "metros2"])),
          valorCatastral: String(getExcelValue(row, ["valor_catastral", "valorcatastral", "valor_cat", "catastral"])),
          bedroom: String(getExcelValue(row, ["dormitorios", "dormitorio", "dormitorios_cantidad", "dorm"])),
          bathroom: String(getExcelValue(row, ["banos", "bano", "banio", "baños", "bathroom"])),
          balcony: String(getExcelValue(row, ["balcon", "balcon_si_no", "balcony"])).toLowerCase(),
          garage: String(getExcelValue(row, ["garage", "garaje", "cochera"])).toLowerCase(),
          infront: String(getExcelValue(row, ["orientacion", "frente_contrafrente", "infront"])).toLowerCase(),
          propNombre,
          propNombre2,
          propTipoDoc,
          propTipoDoc2,
          propCedula,
          propCedula2,
          propCelular,
          propCelular2,
          propEmail,
          propEmail2,
          inqNombre,
          inqNombre2,
          inqTipoDoc,
          inqTipoDoc2,
          inqCedula,
          inqCedula2,
          inqCelular,
          inqCelular2,
          inqEmail,
          inqEmail2,
        });
        }).filter(u => u.number && u.floor);
        
        setExcelData(parsed);
      } catch (error) {
        console.error("Error leyendo Excel:", error);
        showToast("No se pudo leer el archivo. Asegurate de que sea un archivo Excel válido (.xlsx)", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Manejar archivos de IA (imágenes y PDFs)
  const handleAIFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      showToast("Algunos archivos no son válidos. Solo se aceptan imágenes (JPG, PNG, GIF, WEBP) y PDFs.", "warning");
    }
    
    setAiFiles(prev => [...prev, ...validFiles]);
  };

  // Convertir archivo a base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Eliminar archivo de IA
  const removeAiFile = (index) => {
    setAiFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Procesar texto/imágenes/PDFs con IA (ChatGPT)
  const processTextWithAI = async () => {
    if (!aiText.trim() && aiFiles.length === 0) {
      return showToast("Por favor ingresá texto o subí archivos para procesar.", "warning");
    }

    setProcessingAI(true);
    try {
      // Preparar archivos en base64 para enviar al backend
      const filesData = [];
      for (const file of aiFiles) {
        const base64 = await fileToBase64(file);
        filesData.push({
          name: file.name,
          data: base64,
          type: file.type
        });
      }

      // Llamar al endpoint de Habi
      const unitsAuthHeaders = await getAuthHeaders();
      const response = await fetch(`${Habi_API_URL}/api/process-units`, {
        method: "POST",
        headers: unitsAuthHeaders,
        body: JSON.stringify({
          text: aiText.trim(),
          files: filesData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Error ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !Array.isArray(data.units)) {
        throw new Error("Respuesta inválida del servidor");
      }
      if (data.units.length === 0) {
        showToast("La IA no encontró unidades en lo que subiste.", "warning");
        return;
      }
      // Lo detectado se suma al borrador (sin repetir unidades ya editadas) y
      // se abre la tabla para revisar y completar.
      const nuevas = data.units.map(filaDesdeUnidad);
      const filas = sumarAlBorrador(revision.filas, nuevas);
      const repetidas = revision.filas.length + nuevas.length - filas.length;
      setFilasRevision(filas);
      setAiText("");
      setAiFiles([]);
      setShowBulkModal(false);
      setShowRevision(true);
      if (repetidas > 0) {
        showToast(`${repetidas} unidades ya estaban en el borrador: se dejó lo que habías editado.`, "info");
      }
    } catch (error) {
      console.error("Error procesando con IA:", error);
      showToast(`Error al procesar con IA: ${error.message}`, "error");
    } finally {
      setProcessingAI(false);
    }
  };

  // ========== CARGA MASIVA POR importarUnidades ==========
  // Las personas nuevas quedan "Sin enviar" (sin token ni aviso) y se invitan
  // después desde Gestión de residentes. Sin fechaIngreso no se crean tenencias,
  // como hasta ahora.

  /**
   * Corre un lote y deja el resultado de cada fila en `resultados` (idx → fila).
   * Si la llamada entera se corta (red, tope de tiempo), las filas guardan el
   * lote para reintentarlo con el MISMO requestId y payload: el servidor
   * devuelve lo ya creado sin duplicarlo. Devuelve el error si no tiene
   * sentido seguir con los demás lotes (permisos, edificio inexistente…).
   */
  const correrLoteImportacion = async (lote, resultados) => {
    try {
      const res = await importarUnidades({
        buildingId: buildingData.buildingid,
        unidades: lote.filas.map((f) => f.unidad),
        requestId: lote.requestId,
      });
      const devueltas = new Set();
      (res?.filas || []).forEach((r) => {
        const f = lote.filas[r.fila];
        if (!f) return;
        devueltas.add(r.fila);
        resultados.set(f.idx, { ...r, idx: f.idx, etiqueta: f.etiqueta, unidad: f.unidad });
      });
      lote.filas.forEach((f, i) => {
        if (devueltas.has(i)) return;
        resultados.set(f.idx, {
          idx: f.idx,
          estado: "error",
          codigo: "unknown",
          error: "No recibimos el resultado de esta unidad. Reintentá.",
          etiqueta: f.etiqueta,
          unidad: f.unidad,
        });
      });
      return null;
    } catch (e) {
      console.error("Error importando unidades:", e);
      const transitorio = esErrorTransitorio(e);
      const error = mensajeDeError(e, "No se pudo procesar esta unidad.");
      lote.filas.forEach((f) =>
        resultados.set(f.idx, {
          idx: f.idx,
          estado: "error",
          codigo: codigoDeError(e) || "unknown",
          error,
          etiqueta: f.etiqueta,
          unidad: f.unidad,
          ...(transitorio ? { lote } : {}),
        })
      );
      return transitorio ? null : e;
    }
  };

  /** Filas en lotes de LOTE_IMPORTACION, cada lote con su requestId. */
  const importarFilasEnLotes = async (filas, resultados, avance) => {
    for (let i = 0; i < filas.length; i += LOTE_IMPORTACION) {
      const lote = { requestId: nuevoRequestId(), filas: filas.slice(i, i + LOTE_IMPORTACION) };
      const fatal = await correrLoteImportacion(lote, resultados);
      avance(lote.filas.length);
      if (fatal) return fatal;
    }
    return null;
  };

  /**
   * Filas frenadas porque el documento coincide con un residente ya cargado: se
   * pregunta, una por una, si es la misma persona; las confirmadas vuelven con
   * confirmarCoincidencia en ese ocupante.
   */
  const confirmarCoincidencias = async (resultados, preguntadas) => {
    const filas = [];
    const pendientes = [...resultados.values()].sort((a, b) => a.idx - b.idx);
    for (const r of pendientes) {
      const det = r.detalles || {};
      if (r.estado !== "error" || r.codigo !== "failed-precondition" || det.motivo !== "coincidencia_documento") continue;
      if (!det.userDocId || !det.slot) continue;
      const clave = `${r.idx}:${det.slot}:${det.userDocId}`;
      if (preguntadas.has(clave)) continue;
      preguntadas.add(clave);
      const ok = await confirmDialog(`${r.etiqueta} · ${ETIQUETA_SLOT[det.slot] || det.slot}\n\n${r.error}`, {
        title: "¿Es la misma persona?",
        confirmLabel: "Sí, es la misma persona",
        cancelLabel: "No",
      });
      if (!ok) continue;
      filas.push({
        idx: r.idx,
        etiqueta: r.etiqueta,
        unidad: {
          ...r.unidad,
          ocupantes: (r.unidad.ocupantes || []).map((o) =>
            o.slot === det.slot ? { ...o, confirmarCoincidencia: det.userDocId } : o
          ),
        },
      });
    }
    return filas;
  };

  const cerrarModalCarga = () => {
    if (bulkSaving) return;
    setShowBulkModal(false);
    setExcelData([]);
    setAiText("");
    setAiFiles([]);
    setBulkResult(null);
  };

  /**
   * Corre la carga: lotes a repetir (mismo requestId) + filas nuevas, después
   * las coincidencias de documento confirmadas, y deja el resultado por fila.
   */
  const ejecutarImportacion = async ({ filas = [], lotesARepetir = [], resultados, sinDatos = 0, preguntadas = new Set() }) => {
    const total = filas.length + lotesARepetir.reduce((s, l) => s + l.filas.length, 0);
    let hechas = 0;
    const avance = (n) => {
      hechas += n;
      setBulkProgress({ hechas, total });
    };
    setBulkSaving(true);
    setBulkProgress({ hechas: 0, total });
    let fatal = null;
    try {
      for (const lote of lotesARepetir) {
        fatal = await correrLoteImportacion(lote, resultados);
        avance(lote.filas.length);
        if (fatal) break;
      }
      if (!fatal) fatal = await importarFilasEnLotes(filas, resultados, avance);
      // Cada vuelta puede destrabar otro ocupante de la misma fila. Lo que ya se
      // preguntó (y se respondió que no) no se vuelve a preguntar al reintentar.
      for (let vuelta = 0; !fatal && vuelta < SLOTS_UNIDAD.length; vuelta += 1) {
        const confirmadas = await confirmarCoincidencias(resultados, preguntadas);
        if (!confirmadas.length) break;
        fatal = await importarFilasEnLotes(confirmadas, resultados, () => {});
      }
    } finally {
      setBulkSaving(false);
      setBulkProgress(null);
    }

    if (fatal) {
      const motivo = mensajeDeError(fatal, "No se pudo completar la carga.");
      [...filas, ...lotesARepetir.flatMap((l) => l.filas)].forEach((f) => {
        if (resultados.has(f.idx)) return;
        resultados.set(f.idx, {
          idx: f.idx,
          estado: "error",
          codigo: "no_procesada",
          error: `No se procesó: ${motivo}`,
          etiqueta: f.etiqueta,
          unidad: f.unidad,
        });
      });
    }

    const lista = [...resultados.values()].sort((a, b) => a.idx - b.idx);
    const creadas = lista.filter((r) => r.estado === "creada");
    const omitidas = lista.filter((r) => r.estado === "omitida").length;
    const errores = lista.filter((r) => r.estado === "error").length;
    // K2: las filas que quedan para un superadmin se informan aparte.
    const enRevision = lista.filter((r) => r.estado === "error" && esFilaEnRevision(r)).length;
    const ocupantes = creadas.flatMap((r) => r.ocupantes || []);
    const nuevas = ocupantes.filter((o) => o.outcome === "created").length;
    const asignadas = ocupantes.filter((o) => o.outcome === "assigned_existing").length;
    if (creadas.length) await recargarUnidades();

    const partes = [`Se guardaron ${creadas.length} unidades.`];
    if (omitidas) partes.push(`${omitidas} omitidas (ya existían).`);
    if (sinDatos) partes.push(`${sinDatos} sin número o piso.`);
    if (errores - enRevision) partes.push(`${errores - enRevision} con error.`);
    if (enRevision) partes.push(`${enRevision} en revisión de un superadmin (no se crearon).`);
    if (nuevas) partes.push("Las personas nuevas quedaron «Sin enviar» en Gestión de residentes: invitalas desde ahí.");
    showToast(partes.join(" "), errores ? "warning" : "success");

    if (!errores) {
      // Todo resuelto: se cierra como antes.
      setShowBulkModal(false);
      setExcelData([]);
      setAiText("");
      setAiFiles([]);
      setBulkResult(null);
      return { lista, errores };
    }
    setBulkResult({
      resultados,
      preguntadas,
      filas: lista,
      creadas: creadas.length,
      omitidas,
      errores,
      nuevas,
      asignadas,
      sinDatos,
      errorGeneral: fatal ? mensajeDeError(fatal, "No se pudo completar la carga.") : null,
    });
    return { lista, errores };
  };

  // Guardar unidades en lote desde Excel (las de IA se guardan desde la revisión)
  const handleBulkSave = async () => {
    if (bulkSaving) return;
    const origen = excelData;
    const filas = [];
    let sinDatos = 0;
    origen.forEach((u, idx) => {
      const unidad = unidadParaImportar(u || {});
      if (!unidad.number || !unidad.floor) {
        sinDatos += 1;
        return;
      }
      filas.push({ idx, etiqueta: etiquetaUnidad(unidad), unidad });
    });
    if (filas.length === 0) {
      showToast("No hay unidades para guardar.", "warning");
      return;
    }
    setBulkResult(null);
    await ejecutarImportacion({ filas, resultados: new Map(), sinDatos });
  };

  /** Filas con error que se arreglan reintentando (cortes, cambios concurrentes, no procesadas). */
  const filasReintentables = (resultado) =>
    (resultado?.filas || []).filter(
      (r) => r.estado === "error" && (r.lote || CODIGOS_TRANSITORIOS.has(r.codigo) || r.codigo === "no_procesada")
    );

  const reintentarImportacion = async () => {
    if (!bulkResult || bulkSaving) return;
    const resultados = new Map(bulkResult.resultados);
    const lotes = new Map();
    const filas = [];
    filasReintentables(bulkResult).forEach((r) => {
      if (r.lote) lotes.set(r.lote.requestId, r.lote);
      else filas.push({ idx: r.idx, etiqueta: r.etiqueta, unidad: r.unidad });
    });
    if (!lotes.size && !filas.length) return;
    lotes.forEach((l) => l.filas.forEach((f) => resultados.delete(f.idx)));
    filas.forEach((f) => resultados.delete(f.idx));
    await ejecutarImportacion({
      filas,
      lotesARepetir: [...lotes.values()],
      resultados,
      sinDatos: bulkResult.sinDatos,
      preguntadas: new Set(bulkResult.preguntadas || []),
    });
  };

  // ========== REVISIÓN DE LA CARGA CON IA (borrador editable) ==========

  const abrirRevision = () => {
    setShowBulkModal(false);
    setShowRevision(true);
  };

  const seguirRevisionDespues = () => {
    setShowRevision(false);
    if (revision.filas.length) {
      showToast("El borrador quedó guardado. Lo retomás desde Acciones → Cargar con IA.", "info");
    }
  };

  const descartarRevision = async () => {
    const ok = await confirmDialog(
      revision.filas.length === 1
        ? "Se borra la unidad del borrador y lo que completaste."
        : `Se borran las ${revision.filas.length} unidades del borrador y lo que completaste.`,
      { title: "¿Descartar el borrador?", confirmLabel: "Descartar", cancelLabel: "Volver" }
    );
    if (!ok) return;
    borrarBorrador(revision.bid);
    setRevision((r) => ({ ...r, filas: [], sucio: false }));
    setRevisionGuardadaEn(null);
    setShowRevision(false);
  };

  /**
   * Nuevas → importarUnidades (con sus ocupantes). Ya existentes → solo los
   * datos físicos que cambian, directo como el modal "Editar unidad". En el
   * borrador quedan únicamente las filas que no se pudieron guardar.
   */
  const guardarRevision = async () => {
    if (bulkSaving) return;
    const filas = revision.filas;
    const estados = estadosDeFilas(filas, units);
    const pendientes = new Set();

    const actualizaciones = [];
    filas.forEach((f) => {
      const e = estados.get(f._id);
      if (e?.estado !== "existe") return;
      const cambios = cambiosFisicosParaExistente(f, e.unidad);
      if (Object.keys(cambios).length) actualizaciones.push({ fila: f, unidad: e.unidad, cambios });
    });
    const guardadas = new Map();
    if (actualizaciones.length) {
      setBulkSaving(true);
      setBulkProgress({ hechas: 0, total: actualizaciones.length });
      try {
        for (const [i, a] of actualizaciones.entries()) {
          try {
            await updateDoc(doc(db, "apartments", a.unidad.id), { ...a.cambios, updatedAt: serverTimestamp() });
            guardadas.set(a.unidad.id, a.cambios);
          } catch (e) {
            console.error("Error actualizando unidad desde la revisión:", e);
            pendientes.add(a.fila._id);
          }
          setBulkProgress({ hechas: i + 1, total: actualizaciones.length });
        }
      } finally {
        setBulkSaving(false);
        setBulkProgress(null);
      }
      if (guardadas.size) {
        setUnits((prev) => prev.map((u) => (guardadas.has(u.id) ? { ...u, ...guardadas.get(u.id) } : u)));
        setAlicuotas((prev) => prev.map((a) => {
          const c = guardadas.get(a.unitId);
          if (!c) return a;
          return {
            ...a,
            ...("m2" in c ? { m2: c.m2 } : {}),
            ...("valorCatastral" in c ? { valorCatastral: c.valorCatastral } : {}),
          };
        }));
      }
    }

    const nuevas = [];
    filas.forEach((f, idx) => {
      if (estados.get(f._id)?.estado !== "nueva") return;
      const unidad = unidadParaImportar(f);
      nuevas.push({ idx, etiqueta: etiquetaUnidad(unidad), unidad });
    });
    let errores = 0;
    if (nuevas.length) {
      const res = await ejecutarImportacion({ filas: nuevas, resultados: new Map(), sinDatos: 0 });
      errores = res?.errores || 0;
      (res?.lista || []).forEach((r) => {
        if (r.estado === "error" && filas[r.idx]) pendientes.add(filas[r.idx]._id);
      });
    }

    setFilasRevision(filas.filter((f) => pendientes.has(f._id)));
    if (guardadas.size) {
      showToast(`Se actualizaron los datos de ${guardadas.size} ${guardadas.size === 1 ? "unidad existente" : "unidades existentes"}.`, "success");
    }
    if (guardadas.size < actualizaciones.length) {
      showToast(`${actualizaciones.length - guardadas.size} unidades existentes no se pudieron actualizar: quedaron en el borrador.`, "error");
    }
    if (errores) {
      // El detalle por fila (y el reintento) se ve en el modal de carga.
      setShowRevision(false);
      setBulkMode("ai");
      setShowBulkModal(true);
    } else if (!pendientes.size) {
      setShowRevision(false);
    }
  };

  // ─── Personal: alta, cambios y baja por callables; horario y sueldo, directo ───
  const nombreDeEmpleado = (emp) =>
    `${emp?.userData?.NombreCompleto || "N/A"} ${emp?.userData?.ApellidosCompleto || ""}`.trim();

  const openEditEmployee = (emp) => {
    let horarioInicio = "";
    let horarioFin = "";
    if (emp.horario) {
      const parts = String(emp.horario).split(" - ");
      if (parts.length === 2) {
        horarioInicio = parts[0].trim();
        horarioFin = parts[1].trim();
      }
    }
    let diasTrabajoArr = [];
    if (emp.diasTrabajo) {
      diasTrabajoArr = (Array.isArray(emp.diasTrabajo) ? emp.diasTrabajo : String(emp.diasTrabajo).split(","))
        .map((d) => String(d).trim())
        .filter(Boolean);
    }
    const base = emp.userData || {};
    setEditingEmployee({
      ...emp,
      horarioInicio,
      horarioFin,
      diasTrabajoArr,
      datos: {
        NombreCompleto: base.NombreCompleto || "",
        ApellidosCompleto: base.ApellidosCompleto || "",
        tipoDocumento: base.tipoDocumento || "CI",
        Documento: base.Documento != null ? String(base.Documento) : "",
        celular: base.celular || "",
      },
    });
    setShowEmployeeModal(true);
  };

  const cerrarModalEmpleado = () => {
    if (savingEmployeeEdit) return;
    setShowEmployeeModal(false);
  };

  const setDatoEmpleado = (campo, valor) =>
    setEditingEmployee((prev) => ({ ...prev, datos: { ...(prev?.datos || {}), [campo]: valor } }));

  /**
   * Guardar empleado (botón Guardar, no por tecla): datos personales que
   * cambiaron por updateStaffUser (el servidor actualiza users y el espejo
   * employee); horario, días, sueldo e inicio por updateDoc de employeebuilding.
   */
  const handleSaveEmployee = async () => {
    if (!editingEmployee || savingEmployeeEdit) return;
    const { horarioInicio, horarioFin, diasTrabajoArr, sueldo, inicioActividad, datos } = editingEmployee;
    if (!horarioInicio || !horarioFin || !diasTrabajoArr.length) {
      showToast("Completá horario y días de trabajo.", "warning");
      return;
    }

    const ficha = editingEmployee.fichaEditable ? editingEmployee.userData : null;
    const campos = {};
    if (ficha && datos) {
      for (const k of CAMPOS_STAFF_EDITABLES) {
        const nuevo = textoLimpio(datos[k]);
        const actual = k === "tipoDocumento" ? textoLimpio(ficha.tipoDocumento) || "CI" : textoLimpio(ficha[k]);
        if (nuevo === actual) continue;
        if (!nuevo) {
          showToast("Nombre, apellidos, documento y celular no pueden quedar vacíos.", "warning");
          return;
        }
        campos[k] = nuevo;
      }
    }

    const original = employees.find((e) => e.id === editingEmployee.id) || {};
    const diasOriginal = Array.isArray(original.diasTrabajo) ? original.diasTrabajo.join(", ") : textoLimpio(original.diasTrabajo);
    const laborales = {};
    const horario = `${horarioInicio} - ${horarioFin}`;
    const diasTrabajo = diasTrabajoArr.join(", ");
    const sueldoNum = parseFloat(sueldo) || 0;
    if (horario !== textoLimpio(original.horario)) laborales.horario = horario;
    if (diasTrabajo !== diasOriginal) laborales.diasTrabajo = diasTrabajo;
    if (sueldoNum !== (parseFloat(original.sueldo) || 0)) laborales.sueldo = sueldoNum;
    if ((inicioActividad || "") !== (original.inicioActividad || "")) laborales.inicioActividad = inicioActividad || "";

    if (!Object.keys(campos).length && !Object.keys(laborales).length) {
      setShowEmployeeModal(false);
      return;
    }

    setSavingEmployeeEdit(true);
    try {
      let datosCambiaron = false;
      if (Object.keys(campos).length) {
        let res;
        try {
          res = await llamarConConfirmaciones(
            updateStaffUser,
            { userDocId: ficha.id, campos },
            { clave: `updateStaffUser:${ficha.id}` }
          );
        } catch (e) {
          console.error("Error actualizando datos del empleado:", e);
          showToast(mensajeDeError(e, "No se pudieron guardar los datos del empleado."), "error");
          return;
        }
        if (!res) return;
        datosCambiaron = (res.cambios || []).length > 0;
      }

      if (Object.keys(laborales).length) {
        try {
          await updateDoc(doc(db, "employeebuilding", editingEmployee.id), {
            ...laborales,
            updatedAt: serverTimestamp(),
          });
        } catch (e) {
          console.error("Error actualizando el horario del empleado:", e);
          if (datosCambiaron) await recargarPersonal();
          showToast(
            datosCambiaron
              ? "Se guardaron los datos personales, pero no el horario ni el sueldo. Reintentá."
              : "Error al actualizar el empleado.",
            "error"
          );
          return;
        }
      }

      // El servidor normaliza documento y celular: se vuelve a leer la ficha.
      if (datosCambiaron) await recargarPersonal();
      else setEmployees((prev) => prev.map((e) => (e.id === editingEmployee.id ? { ...e, ...laborales } : e)));
      setShowEmployeeModal(false);
      showToast("Empleado actualizado correctamente.", "success");
    } finally {
      setSavingEmployeeEdit(false);
    }
  };

  /** Baja: removeStaffUser por el vínculo con este edificio (nunca se borra la ficha users). */
  const handleDeleteEmployee = async (emp) => {
    if (removingEmployeeId) return;
    const ok = await confirmDialog(
      `¿Quitar a ${nombreDeEmpleado(emp)} del personal de este edificio? Si no trabaja en otro edificio, su cuenta queda archivada y sin acceso.`,
      { title: "Eliminar empleado", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;
    setRemovingEmployeeId(emp.id);
    try {
      const res = await llamarConConfirmaciones(
        removeStaffUser,
        { employeebuildingId: emp.id },
        { clave: `removeStaffUser:${emp.id}` }
      );
      if (!res) return;
      setEmployees((prev) => prev.filter((e) => e.id !== emp.id));
      if (res.auth === "error") {
        showToast("Se quitó del personal, pero no pudimos cerrar el acceso de su cuenta. Avisá a soporte.", "warning");
      } else if (res.archivado) {
        showToast("Empleado eliminado. Como no trabaja en otros edificios, su cuenta quedó archivada y sin acceso.", "success");
      } else if (res.sigueEnOtrosEdificios) {
        showToast("Empleado eliminado de este edificio. Sigue en el personal de otros edificios.", "success");
      } else {
        showToast("Empleado eliminado correctamente.", "success");
      }
    } catch (e) {
      if (codigoDeError(e) === "not-found") {
        setEmployees((prev) => prev.filter((x) => x.id !== emp.id));
        showToast("Ese empleado ya no estaba en el edificio.", "info");
        return;
      }
      console.error("Error eliminando empleado:", e);
      showToast(mensajeDeError(e, "No se pudo eliminar el empleado."), "error");
    } finally {
      setRemovingEmployeeId(null);
    }
  };

  /**
   * Alta de colaborador por createStaffUser: el servidor crea (o reusa) la
   * cuenta de acceso, la ficha users, employee y employeebuilding. Si hace falta,
   * el mail para definir la contraseña sale desde acá con la sesión normal.
   */
  const handleAddEmployee = async () => {
    if (savingEmployee) return;
    const { NombreCompleto, ApellidosCompleto, tipoDocumento, Documento, email, celular, horarioInicio, horarioFin, diasTrabajo, sueldo, inicioActividad } = newEmployee;

    const ee = {};
    if (!NombreCompleto) ee.NombreCompleto = "Requerido";
    if (!ApellidosCompleto) ee.ApellidosCompleto = "Requerido";
    if (!tipoDocumento) ee.tipoDocumento = "Requerido";
    if (!Documento) ee.Documento = "Requerido";
    if (!email) ee.email = "Requerido";
    if (!celular) ee.celular = "Requerido";
    if (!horarioInicio || !horarioFin) ee.horario = "Requerido";
    if (!diasTrabajo.length) ee.diasTrabajo = "Requerido";
    if (!sueldo) ee.sueldo = "Requerido";
    if (!inicioActividad) ee.inicioActividad = "Requerido";
    if (Object.keys(ee).length > 0) {
      setEmployeeErrors(ee);
      showToast("Completá todos los campos obligatorios (*)", "warning");
      return;
    }

    setEmployeeErrors({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmployeeErrors({ email: "Email inválido" });
      showToast("Por favor ingresá un email válido.", "warning");
      return;
    }

    const emailNorm = email.trim().toLowerCase();
    const params = {
      tipo: "colaborador",
      NombreCompleto: NombreCompleto.trim(),
      ApellidosCompleto: ApellidosCompleto.trim(),
      tipoDocumento: tipoDocumento || "CI",
      Documento: String(Documento).trim(),
      email: emailNorm,
      celular: celular.trim(),
      buildingid: buildingData.buildingid,
      horario: `${horarioInicio} - ${horarioFin}`,
      diasTrabajo,
      sueldo: Number.isFinite(Number(sueldo)) ? Number(sueldo) : sueldo,
      inicioActividad,
    };

    setSavingEmployee(true);
    try {
      const res = await llamarConConfirmaciones(createStaffUser, params, { clave: "altaColaborador" });
      if (!res) return;

      let correo = null;
      if (res.definirContrasena) {
        try {
          const ajustes = getOptionalPasswordResetActionCodeSettings();
          if (ajustes) await sendPasswordResetEmail(auth, emailNorm, ajustes);
          else await sendPasswordResetEmail(auth, emailNorm);
          correo = "enviado";
        } catch (err) {
          console.error("sendPasswordResetEmail:", err?.code, err?.message);
          correo = "fallido";
        }
      }

      if (res.outcome === "vinculado") {
        showToast(
          "Esa persona ya era colaborador en Habitia: la sumamos al personal de este edificio. Sus datos no se cambiaron con los del formulario." +
            (correo === "enviado" ? ` Le enviamos un correo a ${emailNorm} para que defina su contraseña.` : ""),
          "success"
        );
      } else if (correo === "enviado") {
        showToast(`Se creó la cuenta exitosamente. Se envió un correo electrónico a ${emailNorm} para que pueda definir su contraseña.`, "success");
      } else if (!res.definirContrasena) {
        showToast(`Se dio de alta a ${params.NombreCompleto}. Ya tenía cuenta en Habitia: entra con su email y su contraseña de siempre.`, "success");
      } else {
        showToast("Se dio de alta al empleado.", "success");
      }
      if (correo === "fallido") {
        showToast("No pudimos enviar el correo para definir la contraseña. Pedile que use «Olvidé mi contraseña» al iniciar sesión.", "warning");
      }
      if (res.cuentaDeshabilitada) {
        showToast("Su cuenta de acceso está deshabilitada: no va a poder entrar hasta que un superadmin la reactive.", "warning");
      }

      setNewEmployee({
        NombreCompleto: "",
        ApellidosCompleto: "",
        tipoDocumento: "CI",
        Documento: "",
        email: "",
        celular: "",
        horarioInicio: "",
        horarioFin: "",
        diasTrabajo: [],
        sueldo: "",
        inicioActividad: "",
      });
      await recargarPersonal();
    } catch (error) {
      console.error("Error creando empleado:", error);
      showToast(mensajeDeError(error, "No se pudo crear el empleado."), "error");
    } finally {
      setSavingEmployee(false);
    }
  };

  // ─── Finanzas Handlers ───
  // isFinanzasDirty debe considerar tanto finanzasData (saldos, cuentas, etc.) como customFunds,
  // ya que los fondos personalizados viven en un state separado pero se guardan juntos.
  const isFinanzasDirty = !!originalFinanzasData && (
    JSON.stringify(finanzasData) !== JSON.stringify(originalFinanzasData) ||
    JSON.stringify(customFunds) !== JSON.stringify(originalCustomFunds || [])
  );

  // Guard de navegacion: avisar si hay cambios sin guardar en finanzas
  useEffect(() => {
    if (!setNavigationGuard) return;
    if (tab === "finanzas" && isFinanzasDirty) {
      setNavigationGuard(async () => {
        const leave = await confirmDialog(
          "Tenés cambios sin guardar en la configuración financiera. Si salís ahora, los cambios se van a perder.",
          { title: "Cambios sin guardar", danger: true, confirmLabel: "Salir sin guardar", cancelLabel: "Quedarme" }
        );
        return leave;
      });
    } else {
      setNavigationGuard(null);
    }
    return () => setNavigationGuard(null);
  }, [tab, isFinanzasDirty, setNavigationGuard, confirmDialog]);

  const handleSaveFinanzas = async () => {
    // Ley 19.604: la tasa de mora no puede superar el 12% anual. Topear y avisar.
    if (Number(finanzasData.interestRate) > 12) {
      finanzasData.interestRate = 12;
      showToast("La tasa de mora se topeó a 12% anual (Ley 19.604).", "warning");
    }

    // FIN-042: Bloquear guardado si alicuotas fuera de tolerancia
    const hasAnyAlicuota = alicuotas.some(a => a.alicuota !== "" && a.alicuota != null);
    if (hasAnyAlicuota) {
      const sum = alicuotas.reduce((s, a) => s + (Number(a.alicuota) || 0), 0);
      if (Math.abs(sum - 1000) > 1) {
        showToast(`La suma de alícuotas es ${sum.toFixed(4)}‰. Debe ser 1000‰ (tolerancia ±1‰). Ajustá antes de guardar.`, "error");
        return;
      }
    }

    // FIN-043: Validar rango de fondo de reserva
    if (finanzasData.reserveFundMethod === "percentage") {
      const pct = Number(finanzasData.reserveFundPercentage);
      if (pct < 0 || pct > 100) {
        showToast("El porcentaje del fondo de reserva debe estar entre 0% y 100%.", "error");
        return;
      }
    }
    if (finanzasData.reserveFundMethod === "fixed") {
      const fixed = Number(finanzasData.reserveFundFixedAmount);
      if (fixed < 0) {
        showToast("El monto fijo del fondo de reserva no puede ser negativo.", "error");
        return;
      }
    }

    setSavingFinanzas(true);
    try {
      // FIN-040: Verificar si las alicuotas cambiaron y ya existen cierres
      const alicuotasChanged = alicuotas.some((a, i) => {
        const unit = units[i];
        if (!unit) return false;
        return String(a.alicuota) !== String(unit.alicuota ?? "");
      });

      if (alicuotasChanged && selectedBuilding?.buildingid) {
        const cierresSnap = await getDocs(
          query(
            collection(db, "montlyaccount"),
            where("buildingid", "==", Number(selectedBuilding.buildingid))
          )
        );
        if (!cierresSnap.empty) {
          const ok = await confirmDialog(
            `Este edificio tiene ${cierresSnap.size} cierre(s) de mes generado(s) con las alícuotas anteriores.\n\nCambiar las alícuotas NO afecta los cierres ya ejecutados, pero sí los futuros.\n\n¿Desea continuar?`,
            { title: "Alícuotas ya utilizadas en cierres", danger: true, confirmLabel: "Guardar de todas formas" }
          );
          if (!ok) {
            setSavingFinanzas(false);
            return;
          }
        }
      }

      const updates = {};
      Object.keys(finanzasData).forEach(k => {
        const v = finanzasData[k];
        if (typeof v === "boolean") { updates[k] = v; return; }
        if (Array.isArray(v)) { updates[k] = v; return; }
        if (v === "" || v === null || v === undefined) { updates[k] = null; return; }
        const num = Number(v);
        updates[k] = isNaN(num) ? v : num;
      });
      // FIN-019: Incluir customFunds en la actualizacion del building
      updates.customFunds = customFunds;

      await updateDoc(doc(db, "building", selectedBuilding.id), updates);

      // Guardar alicuotas en apartments
      const batch = writeBatch(db);
      alicuotas.forEach(a => {
        const ref = doc(db, "apartments", a.unitId);
        batch.update(ref, {
          alicuota: a.alicuota === "" ? null : Number(a.alicuota),
          valorCatastral: a.valorCatastral === "" ? null : Number(a.valorCatastral),
          numeroPadron: a.numeroPadron || null,
        });
      });
      await batch.commit();

      // Actualizar units state para que las cards reflejen las nuevas alícuotas
      setUnits(prev => prev.map(u => {
        const a = alicuotas.find(al => al.unitId === u.id);
        if (!a) return u;
        return {
          ...u,
          alicuota: a.alicuota === "" ? null : Number(a.alicuota),
          valorCatastral: a.valorCatastral === "" ? null : Number(a.valorCatastral),
        };
      }));

      setOriginalFinanzasData({ ...finanzasData });
      // Resetear snapshot de customFunds para que isFinanzasDirty vuelva a false tras guardar
      setOriginalCustomFunds(customFunds);
      // Refrescar el context del edificio para que pantallas como PagosEdificio vean los
      // customFunds recién creados/modificados sin necesidad de recargar la app.
      if (refreshBuildings) refreshBuildings();
      showToast("Configuración financiera guardada.", "success");
    } catch (err) {
      console.error(err);
      showToast("Error al guardar configuración financiera.", "error");
    } finally {
      setSavingFinanzas(false);
    }
  };

  const alicuotaSum = alicuotas.reduce((s, a) => s + (Number(a.alicuota) || 0), 0);

  // Defensivo: asegurar que bankAccounts siempre sea array aunque el state venga de un state legacy (hot-reload o doc sin el campo)
  const bankAccountsList = Array.isArray(finanzasData.bankAccounts) ? finanzasData.bankAccounts : [];
  // Cuentas por moneda para los selectores de saldos (las UYU/legacy van con pesos, las USD con dólares)
  const uyuAccounts = bankAccountsList.filter((a) => (a.currency || "UYU") === "UYU");
  const usdAccounts = bankAccountsList.filter((a) => a.currency === "USD");

  // Formatea una cuenta bancaria para mostrar en los selectores de asociación
  const formatAccountLabel = (acc) => {
    if (!acc) return "";
    const parts = [acc.bank, acc.accountType, acc.accountNumber].filter(Boolean);
    const base = parts.join(" · ") || "Cuenta";
    return `${base} (${acc.currency || "UYU"})`;
  };

  // Las unidades "Alquiler del edificio" (fija) no entran en la estimación: quedan en 0.
  const estimateFromM2 = () => {
    const totalM2 = alicuotas.filter(a => !a.fija).reduce((s, a) => s + (Number(a.m2) || 0), 0);
    if (totalM2 <= 0) { showToast("Todas las unidades deben tener m² cargado.", "warning"); return; }
    setAlicuotas(estimarAlicuotas(alicuotas, "m2"));
    showToast("Alícuotas estimadas desde m². Verifique y ajuste.", "info");
  };

  const estimateFromValorCatastral = () => {
    const missing = alicuotas.filter(a => !a.fija && (!a.valorCatastral || Number(a.valorCatastral) <= 0));
    if (missing.length > 0) {
      showToast(`Falta valor catastral en: ${missing.map(a => a.number).join(", ")}`, "warning");
      return;
    }
    setAlicuotas(estimarAlicuotas(alicuotas, "valorCatastral"));
    showToast("Alícuotas calculadas desde valor catastral. Verifique y ajuste.", "info");
  };

  // --- Consulta catastral ---

  // Buscar job activo para este edificio al cargar
  useEffect(() => {
    if (!buildingData?.buildingid) return;
    const fetchActiveJob = async () => {
      const q = query(
        collection(db, "catastro_jobs"),
        where("buildingId", "==", buildingData.buildingid),
        where("state", "in", ["pending", "processing"])
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const jobDoc = snap.docs[0];
        setCatastroJobId(jobDoc.id);
        setCatastroJobData(jobDoc.data());
      }
    };
    fetchActiveJob();
  }, [buildingData?.buildingid]);

  // Escuchar cambios del job activo en tiempo real
  useEffect(() => {
    if (!catastroJobId) return;
    const unsub = onSnapshot(doc(db, "catastro_jobs", catastroJobId), (snap) => {
      if (!snap.exists()) { setCatastroJobId(null); setCatastroJobData(null); return; }
      const data = snap.data();
      setCatastroJobData(data);
      setCatastroProgress(data.progress || 0);
      if (data.results) setCatastroResults(data.results);
      if (data.state === "completed" || data.state === "failed") {
        setCatastroLoading(false);
      }
    });
    return () => unsub();
  }, [catastroJobId]);

  const handleCatastroStart = () => {
    if (!buildingData.padron) {
      showToast("Configurá el padrón del edificio en la pestaña Datos antes de consultar Catastro", "error");
      return;
    }
    if (units.length === 0) {
      showToast("No hay unidades cargadas para consultar", "error");
      return;
    }
    setCatastroResults(null);
    setCatastroLoading(false);
    setCatastroProgress(0);
    setCatastroSelectedUnits(units.map(u => u.id));
    setShowCatastroModal(true);
  };

  // Ejecutar consultas en background — no bloquea la UI
  const executeCatastroQuery = async () => {
    if (catastroSelectedUnits.length === 0) {
      showToast("Seleccioná al menos una unidad para consultar", "warning");
      return;
    }

    const selectedUnits = units.filter(u => catastroSelectedUnits.includes(u.id));
    const total = selectedUnits.length;
    catastroAbortRef.current = false;

    // Crear job en Firestore
    const jobData = {
      buildingId: buildingData.buildingid,
      padron: Number(buildingData.padron),
      unidades: selectedUnits.map(u => Number(u.number)),
      state: "processing",
      progress: 0,
      results: [],
      total,
      completed: 0,
      createdAt: serverTimestamp(),
    };
    const jobRef = await addDoc(collection(db, "catastro_jobs"), jobData);
    setCatastroJobId(jobRef.id);
    setCatastroJobData(jobData);
    setCatastroLoading(true);
    setCatastroProgress(0);
    setCatastroResults([]);
    setShowCatastroModal(false); // Cerrar modal — corre en background
    showToast("Consulta catastral iniciada en segundo plano", "info");

    // Procesar unidades secuencialmente en background
    const results = [];
    let consecutiveErrors = 0;

    for (let i = 0; i < total; i++) {
      if (catastroAbortRef.current) {
        for (let j = i; j < total; j++) {
          results.push({ padron: Number(buildingData.padron), unidad: Number(selectedUnits[j].number), status: "skipped", error: "Cancelado por el usuario" });
        }
        break;
      }

      if (consecutiveErrors >= 3) {
        for (let j = i; j < total; j++) {
          results.push({ padron: Number(buildingData.padron), unidad: Number(selectedUnits[j].number), status: "skipped", error: "Abortado: 3 errores consecutivos" });
        }
        break;
      }

      const unit = selectedUnits[i];
      try {
        const controller = new AbortController();
        catastroAbortControllerRef.current = controller;
        const catastroAuthHeaders = await getAuthHeaders();
        const resp = await fetch(`${CATASTRO_API_URL}/api/catastro/query-unit`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...catastroAuthHeaders },
          body: JSON.stringify({ padron: Number(buildingData.padron), unidad: Number(unit.number) }),
          signal: controller.signal,
        });
        if (!resp.ok) throw new Error("Error del servicio");
        const data = await resp.json();
        results.push(data);
        if (data.status === "ok") consecutiveErrors = 0;
        else consecutiveErrors++;
      } catch (err) {
        consecutiveErrors++;
        results.push({ padron: Number(buildingData.padron), unidad: Number(unit.number), status: "error", error: err.message });
      }

      // Actualizar progreso en Firestore
      const progress = Math.round(((i + 1) / total) * 100);
      await updateDoc(doc(db, "catastro_jobs", jobRef.id), {
        progress,
        completed: i + 1,
        results: [...results],
      }).catch(() => {});
    }

    // Marcar job como completado
    const ok = results.filter(r => r.status === "ok").length;
    const failed = results.filter(r => r.status !== "ok").length;
    await updateDoc(doc(db, "catastro_jobs", jobRef.id), {
      state: "completed",
      progress: 100,
      completed: total,
      results,
    }).catch(() => {});

    if (ok > 0) {
      showToast(`Consulta catastral finalizada: ${ok} obtenidos, ${failed} con error. Revisá los resultados.`, "info");
    } else {
      showToast("No se pudieron obtener datos catastrales", "error");
    }
  };

  // Cancelar consulta en curso
  const cancelCatastroJob = async () => {
    catastroAbortRef.current = true;
    // Abortar el fetch HTTP activo inmediatamente
    if (catastroAbortControllerRef.current) {
      catastroAbortControllerRef.current.abort();
      catastroAbortControllerRef.current = null;
    }
    // Marcar job como cancelado en Firestore
    if (catastroJobId) {
      await updateDoc(doc(db, "catastro_jobs", catastroJobId), { state: "completed" }).catch(() => {});
    }
    setCatastroLoading(false);
    showToast("Consulta catastral cancelada", "info");
  };

  // Abrir modal de resultados para revisar
  const reviewCatastroResults = () => {
    if (catastroJobData?.results) {
      setCatastroResults(catastroJobData.results);
    }
    setShowCatastroModal(true);
  };

  // Limpiar job completado
  const dismissCatastroJob = async () => {
    if (catastroJobId) {
      await deleteDoc(doc(db, "catastro_jobs", catastroJobId)).catch(() => {});
    }
    setCatastroJobId(null);
    setCatastroJobData(null);
    setCatastroResults(null);
  };

  // Aplicar los resultados de catastro en Firestore (solo los exitosos)
  const applyCatastroResults = async () => {
    if (!catastroResults) return;
    const okResults = catastroResults.filter(r => r.status === "ok");
    if (okResults.length === 0) {
      showToast("No hay resultados exitosos para aplicar", "warning");
      return;
    }

    try {
      const batch = writeBatch(db);
      let updated = 0;

      for (const r of okResults) {
        const unit = units.find(u => Number(u.number) === Number(r.unidad));
        if (!unit) continue;

        const unitRef = doc(db, "apartments", unit.id);
        const updateData = { valorCatastral: r.valorCatastral };
        if (r.areaUnidad && (!unit.m2 || unit.m2 === "")) {
          updateData.m2 = r.areaUnidad;
        }
        batch.update(unitRef, updateData);
        updated++;
      }

      if (updated > 0) await batch.commit();

      // Actualizar estado local
      setUnits(prev => prev.map(u => {
        const r = okResults.find(res => Number(res.unidad) === Number(u.number));
        if (!r) return u;
        const upd = { ...u, valorCatastral: r.valorCatastral };
        if (r.areaUnidad && (!u.m2 || u.m2 === "")) upd.m2 = r.areaUnidad;
        return upd;
      }));

      setAlicuotas(prev => prev.map(a => {
        const r = okResults.find(res => Number(res.unidad) === Number(a.number));
        if (!r) return a;
        return { ...a, valorCatastral: r.valorCatastral };
      }));

      await logAudit("catastro_update", {
        padron: buildingData.padron,
        buildingId: selectedBuilding.buildingid,
        updated,
        failed: catastroResults.filter(r => r.status !== "ok").length,
      });

      showToast(`${updated} valores catastrales guardados correctamente`, "success");
      setShowCatastroModal(false);
      setCatastroResults(null);
      // Limpiar job de Firestore
      if (catastroJobId) {
        await deleteDoc(doc(db, "catastro_jobs", catastroJobId)).catch(() => {});
        setCatastroJobId(null);
        setCatastroJobData(null);
      }
    } catch (err) {
      showToast("Error al guardar: " + err.message, "error");
    }
  };

  // Estado local para guardar el toggle de templates por edificio mientras
  // se persiste a Firestore. Evita re-fetch completo cada toggle.
  const [savingTemplateKey, setSavingTemplateKey] = useState(null);
  // Lista de templates desde Firestore (notificationTemplates). El admin del
  // edificio los puede habilitar/deshabilitar individualmente — incluso los
  // pendientes de Meta (cuando se aprueben, el envío automático arranca).
  const [firestoreTemplates, setFirestoreTemplates] = useState(null); // null = cargando, [] = vacío
  useEffect(() => {
    if (tab !== "comunicaciones") return;
    const loadTemplates = async () => {
      try {
        const snap = await getDocs(collection(db, "notificationTemplates"));
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        arr.sort((a, b) => {
          // Ordenar por categoria, luego stage, luego key
          const cat = (a.category || "").localeCompare(b.category || "");
          if (cat !== 0) return cat;
          const stA = a.stage ?? 99;
          const stB = b.stage ?? 99;
          if (stA !== stB) return stA - stB;
          return (a.key || a.id || "").localeCompare(b.key || b.id || "");
        });
        setFirestoreTemplates(arr);
      } catch (e) {
        console.warn("[Comunicaciones] No se pudieron cargar notificationTemplates:", e);
        setFirestoreTemplates([]);
      }
    };
    loadTemplates();
  }, [tab]);

  const toggleCommunicationTemplate = async (templateKey, newEnabled) => {
    const buildingId = buildingData?.id || selectedBuilding?.id;
    if (!buildingId) {
      showToast("No se pudo identificar el edificio. Recargá la pantalla.", "error");
      return;
    }
    setSavingTemplateKey(templateKey);
    try {
      const prevTemplates = buildingData.communicationTemplates || {};
      const updated = {
        ...prevTemplates,
        [templateKey]: {
          enabled: newEnabled,
          updated_at: new Date().toISOString(),
          updated_by: auth.currentUser?.uid || "unknown",
        },
      };
      await updateDoc(doc(db, "building", buildingId), {
        communicationTemplates: updated,
      });
      setBuildingData((prev) => ({ ...prev, communicationTemplates: updated }));
      // logAudit(acción, detalles): la regla de audit exige action string.
      logAudit(newEnabled ? "enable_communication_template" : "disable_communication_template", {
        entityType: "building",
        entityId: buildingId,
        meta: { templateKey },
      });
      showToast(
        newEnabled
          ? "Template habilitado para este edificio."
          : "Template deshabilitado para este edificio.",
        "success"
      );
    } catch (err) {
      console.error("[Comunicaciones] toggle falló:", err);
      showToast("No se pudo actualizar el template: " + err.message, "error");
    } finally {
      setSavingTemplateKey(null);
    }
  };

  if (!buildingData) return <Loader />;

  const tabs = [
    { id: "datos", label: "Datos", icon: FaBuilding },
    // Solo superadmin: los links de administrador los asigna setBuildingAdmin.
    ...(esSuperadmin === true ? [{ id: "admins", label: "Administradores", icon: FaUsers }] : []),
    { id: "unidades", label: "Unidades", icon: FaDoorOpen },
    { id: "empleado", label: "Personal", icon: FaUserTie },
    { id: "amenities", label: "Amenities", icon: FaSwimmingPool },
    { id: "finanzas", label: "Finanzas", icon: FaMoneyBillWave },
    { id: "saldosproveedores", label: "Saldos Proveedores", icon: FaTruck },
    { id: "comision", label: "Comisión", icon: FaUserTie },
    { id: "comunicaciones", label: "Comunicaciones", icon: FaComments },
  ];

  // ¿El modal de unidad cambia algún ocupante? Entonces pide la fecha del cambio.
  const unidadEnEdicionOriginal = editingUnit ? units.find((u) => u.id === editingUnit.id) : null;
  const cambianOcupantesEnModal =
    !!editingUnit &&
    SLOTS_UNIDAD.some((f) => valorSlot(editingUnit[f]) !== valorSlot(unidadEnEdicionOriginal?.[f]));
  const reintentablesCarga = filasReintentables(bulkResult).length;

  return (
    <div style={styles.page}>
      <ScreenHeader icon={FaBuilding} title="Editar Edificio" onBack={handleBack} />
      {hasPendingDeletion && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          borderRadius: 8,
          backgroundColor: colors.warningLight,
          border: `1px solid ${colors.warning}`,
          marginBottom: 12,
          fontSize: 13,
          color: colors.warningDark,
        }}>
          <FaExclamationTriangle size={14} />
          {AVISO_BAJA[bajaAbierta] || AVISO_BAJA.pendiente}
        </div>
      )}
      <div style={styles.deleteBuildingRow}>
        <button
          type="button"
          style={{
            ...styles.deleteBuildingBtn,
            opacity: hasPendingDeletion || deletingBuilding ? 0.5 : 1,
            cursor: hasPendingDeletion || deletingBuilding ? "not-allowed" : "pointer",
          }}
          onClick={handleDeleteBuilding}
          disabled={hasPendingDeletion || deletingBuilding}
        >
          <FaTrash size={14} />
          {bajaAbierta === "procesando"
            ? "Eliminación en curso"
            : bajaAbierta === "error"
              ? "Eliminación con error"
              : hasPendingDeletion
                ? "Eliminación pendiente"
                : "Eliminar edificio"}
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer} data-tutorial="editar-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? undefined : "habitia-btn-secondary"}
            style={tab === t.id ? styles.tabActive : styles.tab}
            onClick={() => handleTabChange(t.id)}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ ...styles.content, ...(hasPendingDeletion ? { pointerEvents: "none", opacity: 0.5 } : {}) }} data-tutorial="editar-content">
        {tab === "datos" && (
          <div style={styles.card} data-tutorial="editar-tab-datos">
            <h3 style={styles.cardTitle}>Información del Edificio</h3>
            <div style={styles.formGrid}>
              {/* Nombre */}
              <FormField label="Nombre" required error={buildingErrors.name}>
                <input
                  className="habitia-input"
                  style={{ ...styles.input, ...(buildingErrors.name ? { borderColor: colors.error } : {}) }}
                  type="text"
                  value={buildingData.name ?? ""}
                  onChange={(e) => { setBuildingData({ ...buildingData, name: e.target.value }); setBuildingErrors((prev) => ({ ...prev, name: undefined })); }}
                />
              </FormField>
              {/* Dirección con autocomplete Google Places */}
              <FormField label="Dirección" required error={buildingErrors.address}>
                <input
                  ref={addressInputRef}
                  className="habitia-input"
                  style={{ ...styles.input, ...(buildingErrors.address ? { borderColor: colors.error } : {}) }}
                  type="text"
                  placeholder="Buscar dirección..."
                  defaultValue={buildingData.address ?? ""}
                  key={`addr-${selectedBuilding?.id}`}
                  onChange={(e) => { setBuildingData((prev) => ({ ...prev, address: e.target.value })); setBuildingErrors((prev) => ({ ...prev, address: undefined })); }}
                />
              </FormField>
              {/* Ciudad - se autocompleta desde Google Places */}
              <FormField label="Ciudad" required error={buildingErrors.city}>
                <input
                  className="habitia-input"
                  style={{ ...styles.input, ...(buildingErrors.city ? { borderColor: colors.error } : {}) }}
                  type="text"
                  value={buildingData.city ?? ""}
                  onChange={(e) => { setBuildingData({ ...buildingData, city: e.target.value }); setBuildingErrors((prev) => ({ ...prev, city: undefined })); }}
                />
              </FormField>
              {/* Departamento - dropdown, se autocompleta desde Google Places */}
              <FormField label="Departamento" required error={buildingErrors.department}>
                <select
                  className="habitia-input"
                  style={{ ...styles.input, ...(buildingErrors.department ? { borderColor: colors.error } : {}) }}
                  value={buildingData.department ?? ""}
                  onChange={(e) => { setBuildingData({ ...buildingData, department: e.target.value }); setBuildingErrors((prev) => ({ ...prev, department: undefined })); }}
                >
                  <option value="">Seleccionar...</option>
                  {["Artigas", "Canelones", "Cerro Largo", "Colonia", "Durazno", "Flores", "Florida", "Lavalleja", "Maldonado", "Montevideo", "Paysandú", "Río Negro", "Rivera", "Rocha", "Salto", "San José", "Soriano", "Tacuarembó", "Treinta y Tres"].map((dep) => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              </FormField>
              {/* País - dropdown */}
              <FormField label="País" required error={buildingErrors.country}>
                <select
                  className="habitia-input"
                  style={{ ...styles.input, ...(buildingErrors.country ? { borderColor: colors.error } : {}) }}
                  value={buildingData.country ?? "Uruguay"}
                  onChange={(e) => { setBuildingData({ ...buildingData, country: e.target.value }); setBuildingErrors((prev) => ({ ...prev, country: undefined })); }}
                >
                  <option value="Uruguay">Uruguay</option>
                </select>
              </FormField>
              {/* Padrón */}
              <FormField label="Padrón">
                <input
                  className="habitia-input"
                  style={styles.input}
                  type="text"
                  value={buildingData.padron ?? ""}
                  onChange={(e) => setBuildingData({ ...buildingData, padron: e.target.value })}
                />
                <VisBadge padron={buildingData.padron} />
              </FormField>
              {/* Pisos */}
              <FormField label="Pisos">
                <input className="habitia-input" style={styles.input} type="number" min={0} value={buildingData.floors ?? ""} onChange={(e) => setBuildingData({ ...buildingData, floors: e.target.value === "" ? "" : Number(e.target.value) })} />
              </FormField>
              {/* Ascensores */}
              <FormField label="Ascensores">
                <input className="habitia-input" style={styles.input} type="number" min={0} value={buildingData.elevators ?? ""} onChange={(e) => setBuildingData({ ...buildingData, elevators: e.target.value === "" ? "" : Number(e.target.value) })} />
              </FormField>
            </div>
            {/* Es complejo - checkbox */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
              <input
                type="checkbox"
                id="complex-edit"
                checked={!!buildingData.complex}
                onChange={(e) => setBuildingData({ ...buildingData, complex: e.target.checked })}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
              <label htmlFor="complex-edit" style={{ fontSize: 14, color: colors.gray700, cursor: "pointer" }}>Es complejo</label>
            </div>
            <button
              onClick={handleSaveBuilding}
              style={{
                ...styles.primaryBtn,
                ...(!isBuildingDirty && !savingBuilding ? { opacity: 0.5, cursor: "not-allowed" } : {}),
              }}
              disabled={savingBuilding || !isBuildingDirty}
            >
              <FaSave size={14} />
              {savingBuilding ? "Guardando..." : "Guardar"}
            </button>
          </div>
        )}

        {tab === "admins" && esSuperadmin === true && (
          <div style={styles.card} data-tutorial="editar-tab-admins">
            <h3 style={styles.cardTitle}>Administradores del Edificio</h3>
            <div style={styles.chipContainer}>
              {admins.map((a) => {
                const user = adminUsers.find((u) => String(u.userid) === String(a.userid));
                return (
                  <div key={String(a.userid)} style={styles.chip}>
                    <span>{user ? `${user.name} (${user.email})` : `ID: ${a.userid}`}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAdmin(a.userid)}
                      style={{ ...styles.chipRemove, ...(savingAdmin ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                      disabled={savingAdmin}
                      aria-label="Quitar administrador"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                );
              })}
              {admins.length === 0 && (
                <div style={styles.warningBanner}>
                  Este edificio no tiene administradores asignados
                </div>
              )}
            </div>
            <div style={styles.addRow}>
              <select
                style={styles.input}
                value={newAdminId}
                onChange={(e) => setNewAdminId(e.target.value)}
                disabled={savingAdmin}
                aria-label="Administrador a agregar"
              >
                <option value="">Seleccionar usuario...</option>
                {adminUsers
                  .filter((u) => !u.archivedAt && u.mergedIntoUserid == null)
                  .filter((u) => !admins.some((a) => String(a.userid) === String(u.userid)))
                  .map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
              </select>
              <button
                type="button"
                onClick={handleAddAdmin}
                style={{ ...styles.addBtn, ...(savingAdmin || !newAdminId ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                disabled={savingAdmin || !newAdminId}
              >
                <FaPlusCircle size={16} />
                {savingAdmin ? "Guardando..." : "Agregar"}
              </button>
            </div>
          </div>
        )}

        {tab === "unidades" && (
          <div style={styles.card} data-tutorial="editar-tab-unidades">
            <h3 style={styles.cardTitle}>Unidades del Edificio</h3>
            
            {/* SECCIÓN AGREGAR */}
            <div style={styles.addSectionTop}>
              <div style={styles.addSectionHeader}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button onClick={() => { setNewUnit({ ...defaultNewUnit }); setShowAddUnitModal(true); }} style={styles.addBtn}>
                    <FaPlusCircle size={14} /> Agregar Unidad
                  </button>
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowUnitActionsMenu((prev) => !prev)}
                      style={styles.bulkBtn}
                    >
                      <FaEllipsisH size={12} /> Acciones
                    </button>
                    {showUnitActionsMenu && (
                      <>
                        <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShowUnitActionsMenu(false)} />
                        <div style={{
                          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 100,
                          background: colors.white, borderRadius: radius.sm, boxShadow: shadows.lg,
                          minWidth: 220, overflow: "hidden",
                        }}>
                          <button
                            onClick={() => { setShowBulkModal(true); setBulkMode("excel"); setShowUnitActionsMenu(false); }}
                            style={styles.dropdownItem}
                          >
                            <FaFileExcel size={12} /> Cargar Excel
                          </button>
                          <button
                            onClick={() => { setShowBulkModal(true); setBulkMode("ai"); setShowUnitActionsMenu(false); }}
                            style={styles.dropdownItem}
                          >
                            <FaRobot size={12} /> Cargar con IA
                          </button>
                          {units.length > 0 && (
                            <button
                              onClick={() => { setShowAlicuotasModal(true); setShowUnitActionsMenu(false); }}
                              style={styles.dropdownItem}
                            >
                              <FaCalculator size={12} /> Calcular Alícuotas
                            </button>
                          )}
                          {units.length > 0 && (
                            <button
                              onClick={() => { handleCatastroStart(); setShowUnitActionsMenu(false); }}
                              style={styles.dropdownItem}
                            >
                              <FaSync size={12} /> Actualizar Valor Catastral
                            </button>
                          )}
                          {units.length > 0 && (
                            <button
                              onClick={() => { setShowEditPropietariosModal(true); setShowUnitActionsMenu(false); }}
                              style={styles.dropdownItem}
                            >
                              <FaUsers size={12} /> Editar propietarios/inquilinos
                            </button>
                          )}
                          {units.length > 0 && (
                            <button
                              onClick={() => {
                                setInitialBalancesData(units.map(u => ({
                                  id: u.id,
                                  number: u.number,
                                  owner: u.owner,
                                  tenant: u.tenant,
                                  ownerGastos: u.initialBalanceOwner || 0,
                                  ownerReserva: u.initialBalanceOwnerReserva || 0,
                                  tenantGastos: u.initialBalanceTenant || 0,
                                })));
                                setShowInitialBalancesModal(true);
                                setShowUnitActionsMenu(false);
                              }}
                              style={styles.dropdownItem}
                            >
                              <FaMoneyBillWave size={12} /> Saldos Iniciales
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* BORRADOR DE CARGA CON IA SIN TERMINAR */}
            {revision.filas.length > 0 && !showRevision && (
              <div style={styles.borradorBanner} role="status">
                <span style={styles.borradorBannerTexto}>
                  <FaMagic size={12} /> Tenés una carga con IA sin terminar: {revision.filas.length}{" "}
                  {revision.filas.length === 1 ? "unidad" : "unidades"} en borrador
                  {revisionGuardadaEn ? ` (guardado ${new Date(revisionGuardadaEn).toLocaleString("es-UY", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })})` : ""}.
                </span>
                <button type="button" style={styles.primaryBtn} onClick={abrirRevision}>
                  Revisar y completar
                </button>
              </div>
            )}

            {/* BANNER DE CONSULTA CATASTRAL EN CURSO */}
            {catastroJobData && (
              <div style={{
                background: catastroJobData.state === "completed" ? colors.successLight : colors.infoLight,
                border: `1px solid ${catastroJobData.state === "completed" ? colors.success : colors.info}`,
                borderRadius: radius.sm,
                padding: "10px 16px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                  <FaSync size={14} color={catastroJobData.state === "completed" ? colors.successDark : colors.infoDark}
                    style={catastroJobData.state === "processing" ? { animation: "spin 2s linear infinite" } : {}} />
                  <span style={{ fontSize: typography.sm, color: catastroJobData.state === "completed" ? colors.successDark : colors.infoDark }}>
                    {catastroJobData.state === "processing"
                      ? `Consultando Catastro... ${catastroJobData.completed || 0} de ${catastroJobData.total} unidades`
                      : `Consulta catastral completada — ${(catastroJobData.results || []).filter(r => r.status === "ok").length} de ${catastroJobData.total} obtenidos`
                    }
                  </span>
                  {catastroJobData.state === "processing" && (
                    <div style={{ flex: 1, maxWidth: 200, background: colors.gray200, borderRadius: radius.full, height: 6, overflow: "hidden" }}>
                      <div style={{ background: colors.primary, height: "100%", width: `${catastroJobData.progress || 0}%`, borderRadius: radius.full, transition: transitions.normal }} />
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {catastroJobData.state === "processing" && (
                    <button onClick={cancelCatastroJob} style={{ ...btnGhost, fontSize: typography.xs, padding: "4px 10px", color: colors.errorDark }}>
                      Cancelar
                    </button>
                  )}
                  {catastroJobData.state === "completed" && (
                    <>
                      <button onClick={reviewCatastroResults} style={{ ...btnPrimary, fontSize: typography.xs, padding: "4px 12px" }}>
                        Revisar resultados
                      </button>
                      <button onClick={dismissCatastroJob} style={{ ...btnGhost, fontSize: typography.xs, padding: "4px 10px" }}>
                        Descartar
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* CONTADOR DE UNIDADES */}
            <div style={styles.unitsCount}>
              <FaDoorOpen size={14} color={colors.gray600} />
              <span>{units.length} unidades registradas</span>
            </div>

            {/* GRID DE UNIDADES */}
            <div style={styles.unitGrid}>
              {units.map((u) => (
                <div key={u.id} style={styles.unitCard}>
                  <div style={styles.unitHeader}>
                    <h4 style={styles.unitTitle}>Unidad {u.number}</h4>
                    <div style={styles.unitActions}>
                      <button type="button" style={styles.iconBtn} onClick={() => openEditUnit(u)} aria-label={`Editar unidad ${u.number}`}>
                        <FaPen size={12} />
                      </button>
                      <button
                        type="button"
                        style={{ ...styles.iconBtnDanger, ...(deletingUnitId ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                        onClick={() => handleDeleteUnit(u.id)}
                        disabled={!!deletingUnitId}
                        aria-label={`Eliminar unidad ${u.number}`}
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>
                  <div style={styles.unitInfo}>
                    <p>Piso: {u.floor}</p>
                    <p>Dormitorios: {u.bedroom ?? "-"}</p>
                    <p>Baños: {u.bathroom ?? "-"}</p>
                    <p>M²: {u.m2 ?? "-"}</p>
                    <p>V. Catastral: {u.valorCatastral ? Number(u.valorCatastral).toLocaleString("es-UY") : <span style={{ color: colors.gray400 }}>sin cargar</span>}</p>
                    <p>Alícuota: {esAlquilerDelEdificio(u) ? "0 (no paga gastos comunes)" : u.alicuota ? u.alicuota : <span style={{ color: colors.gray400 }}>sin calcular</span>}</p>
                    <p>Balcón: {yesNoLabel(u.balcony)}</p>
                    <p>Garage: {yesNoLabel(u.garage)}</p>
                    {esAlquilerDelEdificio(u) ? (
                      <>
                        <p>Propietario: {buildingData?.name || "el edificio"}</p>
                        <p>Tipo: alquiler del edificio</p>
                      </>
                    ) : hasAssignedUser(u.owner) ? (
                      <p>Propietario: {getResidentLabel(u.owner)}</p>
                    ) : (
                      <p style={{ color: colors.warningDark, fontWeight: 400, fontSize: typography.sizes.sm, background: colors.warningLight, padding: "4px 8px", borderRadius: 6, display: "inline-block", marginTop: 4 }}>
                        ⚠ Sin propietario asignado
                      </p>
                    )}
                    {hasAssignedUser(u.ownerSecondary) && (
                      <p>Propietario complementario: {getResidentLabel(u.ownerSecondary)}</p>
                    )}
                    {hasAssignedUser(u.tenant) ? (
                      <p>Inquilino: {getResidentLabel(u.tenant)}</p>
                    ) : (
                      <p style={{ color: colors.warningDark, fontWeight: 400, fontSize: typography.sizes.sm, background: colors.warningLight, padding: "4px 8px", borderRadius: 6, display: "inline-block", marginTop: 4 }}>
                        ⚠ Sin inquilino asignado
                      </p>
                    )}
                    {hasAssignedUser(u.tenantSecondary) && (
                      <p>Inquilino complementario: {getResidentLabel(u.tenantSecondary)}</p>
                    )}
                    {(u.initialBalanceOwner || u.initialBalanceOwnerReserva || u.initialBalanceTenant) && (
                      <div style={{ borderTop: `1px solid ${colors.gray200}`, paddingTop: 6, marginTop: 6 }}>
                        <p style={{ fontSize: typography.sizes.xs, color: colors.gray400, margin: "0 0 2px" }}>Saldos iniciales:</p>
                        {(u.initialBalanceOwner || u.initialBalanceOwnerReserva) && (
                          <p style={{ fontSize: typography.sizes.sm }}>
                            Propietario: G.C. ${u.initialBalanceOwner || 0}{u.initialBalanceOwnerReserva ? ` / F.R. $${u.initialBalanceOwnerReserva}` : ""}
                          </p>
                        )}
                        {u.initialBalanceTenant > 0 && (
                          <p style={{ fontSize: typography.sizes.sm }}>
                            Inquilino: G.C. ${u.initialBalanceTenant}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {setUnidadSeleccionada && (
                    <button
                      type="button"
                      onClick={() => {
                        if (setUnidadBackTo) setUnidadBackTo("editaredificio");
                        if (setInitialTab) setInitialTab("unidades");
                        setUnidadSeleccionada(u.id);
                        setSelected("unidad");
                      }}
                      style={{
                        marginTop: 10,
                        width: "100%",
                        padding: "6px 10px",
                        background: "transparent",
                        color: colors.primary,
                        border: `1px solid ${colors.gray200}`,
                        borderRadius: 6,
                        fontSize: typography.sizes.sm,
                        fontWeight: 400,
                        cursor: "pointer",
                      }}
                      title="Ver historial de ocupantes (quién estuvo y desde/hasta qué fecha) y detalle de la unidad"
                    >
                      Ver ocupantes / historial
                    </button>
                  )}
                </div>
              ))}
            </div>

            <AdminModal
              open={showUnitModal}
              onClose={cerrarModalUnidad}
              title={`Editar Unidad ${editingUnit?.number || ""}`}
              size="medium"
              icon={<FaDoorOpen />}
              footer={
                <>
                  <button type="button" style={styles.secondaryBtn} onClick={cerrarModalUnidad} disabled={savingUnit}>Cancelar</button>
                  <button
                    type="button"
                    style={{ ...styles.primaryBtn, ...(savingUnit ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                    onClick={handleSaveUnit}
                    disabled={savingUnit}
                  >
                    <FaSave size={14} /> {savingUnit ? "Guardando..." : "Guardar"}
                  </button>
                </>
              }
            >
              <div style={styles.formGrid}>
                {[
                  { field: "number", label: "Número" },
                  { field: "floor", label: "Piso" },
                  { field: "m2", label: "Metros²", type: "number" },
                  { field: "bathroom", label: "Baños", type: "number" },
                  { field: "bedroom", label: "Dormitorios", type: "number" },
                  { field: "valorCatastral", label: "Valor Catastral", type: "number" },
                ].map(({ field, label, type }) => (
                  <div key={field} style={styles.formGroup}>
                    <label style={styles.label} htmlFor={`unidad-editar-${field}`}>{label}</label>
                    <input
                      id={`unidad-editar-${field}`}
                      style={styles.input}
                      type={type || "text"}
                      min={type === "number" ? 0 : undefined}
                      value={editingUnit?.[field] ?? ""}
                      onChange={(e) => setEditingUnit({ ...editingUnit, [field]: e.target.value })}
                    />
                  </div>
                ))}
                {/* Balcón */}
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="unidad-editar-balcony">Balcón</label>
                  <select
                    id="unidad-editar-balcony"
                    style={styles.input}
                    value={editingUnit?.balcony === true || editingUnit?.balcony === "si" ? "true" : editingUnit?.balcony === false || editingUnit?.balcony === "no" ? "false" : ""}
                    onChange={(e) => setEditingUnit({ ...editingUnit, balcony: e.target.value === "" ? "" : e.target.value === "true" })}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </div>
                {/* Garaje */}
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="unidad-editar-garage">Garaje</label>
                  <select
                    id="unidad-editar-garage"
                    style={styles.input}
                    value={editingUnit?.garage === true || editingUnit?.garage === "si" ? "true" : editingUnit?.garage === false || editingUnit?.garage === "no" ? "false" : ""}
                    onChange={(e) => setEditingUnit({ ...editingUnit, garage: e.target.value === "" ? "" : e.target.value === "true" })}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </div>
                {/* Orientación */}
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="unidad-editar-infront">Orientación</label>
                  <select
                    id="unidad-editar-infront"
                    style={styles.input}
                    value={editingUnit?.infront ?? ""}
                    onChange={(e) => setEditingUnit({ ...editingUnit, infront: e.target.value })}
                  >
                    <option value="">Seleccionar...</option>
                    {["Frente", "Contrafrente", "Lateral", "Interno"].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                {/* R3: Tipo de unidad — portería y alquiler del edificio no participan del reparto de gastos comunes */}
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="unidad-editar-unitKind">Tipo de unidad</label>
                  <select
                    id="unidad-editar-unitKind"
                    style={styles.input}
                    value={normKind(editingUnit)}
                    onChange={(e) => setEditingUnit({ ...editingUnit, unitKind: e.target.value })}
                  >
                    <option value="normal">Normal</option>
                    <option value="porteria">Portería (no paga gastos comunes)</option>
                    <option value={UNIT_KIND_ALQUILER}>Alquiler del edificio (no paga gastos comunes; el inquilino sí entra)</option>
                  </select>
                </div>
                {esAlquilerDelEdificio(editingUnit) && (
                  <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 }}>
                    El dueño es el edificio: la unidad no tiene propietario, no paga gastos comunes ni fondos y su alícuota es 0. Asigná solo inquilinos; el contrato y los montos del alquiler se cargan en la ficha de la unidad.
                  </p>
                )}
                {noBuildingResidents && (
                  <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 }}>
                    Solo podés asignar como propietario o inquilino a residentes que ya pertenecen a este edificio. Para dar de alta a un residente nuevo, usá Gestión de residentes o la carga masiva.
                  </p>
                )}
                {!esAlquilerDelEdificio(editingUnit) && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Propietario *</label>
                  <SearchableSelect
                    value={editingUnit?.owner ?? ""}
                    onChange={(v) => setEditingUnit({ ...editingUnit, owner: v })}
                    options={buildingResidentOptions}
                    placeholder="Seleccionar..."
                  />
                </div>
                )}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Inquilino</label>
                  <SearchableSelect
                    value={editingUnit?.tenant ?? ""}
                    onChange={(v) => setEditingUnit({ ...editingUnit, tenant: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin inquilino"
                    emptyLabel="— Sin inquilino —"
                  />
                </div>
                {!esAlquilerDelEdificio(editingUnit) && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Propietario complementario</label>
                  <SearchableSelect
                    value={editingUnit?.ownerSecondary ?? ""}
                    onChange={(v) => setEditingUnit({ ...editingUnit, ownerSecondary: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin propietario complementario"
                    emptyLabel="— Sin propietario complementario —"
                  />
                </div>
                )}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Inquilino complementario</label>
                  <SearchableSelect
                    value={editingUnit?.tenantSecondary ?? ""}
                    onChange={(v) => setEditingUnit({ ...editingUnit, tenantSecondary: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin inquilino complementario"
                    emptyLabel="— Sin inquilino complementario —"
                  />
                </div>
                {/* Cambio de ocupantes: se registra la tenencia desde esta fecha (el saliente queda hasta el día anterior). */}
                {cambianOcupantesEnModal && (
                  <FormField
                    label="Fecha del cambio de ocupantes"
                    htmlFor="fecha-cambio-ocupantes"
                    required
                    hint="Desde esta fecha cuenta quién ocupa la unidad para el reparto de gastos comunes; quien sale queda hasta el día anterior."
                  >
                    <input
                      id="fecha-cambio-ocupantes"
                      className="habitia-input"
                      style={styles.input}
                      type="date"
                      max={hoyMontevideo()}
                      value={fechaCambioOcupantes}
                      onChange={(e) => setFechaCambioOcupantes(e.target.value)}
                    />
                  </FormField>
                )}
              </div>
            </AdminModal>

            {/* MODAL AGREGAR UNIDAD */}
            <AdminModal
              open={showAddUnitModal}
              onClose={() => { if (!addingUnit) setShowAddUnitModal(false); }}
              title="Agregar Nueva Unidad"
              size="medium"
              icon={<FaDoorOpen />}
              footer={
                <>
                  <button type="button" style={styles.secondaryBtn} onClick={() => setShowAddUnitModal(false)} disabled={addingUnit}>Cancelar</button>
                  <button
                    type="button"
                    style={{ ...styles.primaryBtn, ...(addingUnit ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                    onClick={handleAddUnit}
                    disabled={addingUnit}
                  >
                    <FaPlusCircle size={14} /> {addingUnit ? "Agregando..." : "Agregar"}
                  </button>
                </>
              }
            >
              <div style={styles.formGrid}>
                {[
                  { field: "number", label: "Número", required: true },
                  { field: "floor", label: "Piso", required: true },
                  { field: "m2", label: "Metros²", type: "number" },
                  { field: "bathroom", label: "Baños", type: "number" },
                  { field: "bedroom", label: "Dormitorios", type: "number" },
                  { field: "valorCatastral", label: "Valor Catastral", type: "number" },
                ].map(({ field, label, required, type }) => (
                  <FormField key={field} label={label} htmlFor={`unidad-nueva-${field}`} required={required} error={unitErrors[field]}>
                    <input
                      id={`unidad-nueva-${field}`}
                      className="habitia-input"
                      style={{ ...styles.input, ...(unitErrors[field] ? { borderColor: colors.error } : {}) }}
                      type={type || "text"}
                      min={type === "number" ? 0 : undefined}
                      value={newUnit[field] ?? ""}
                      onChange={(e) => { setNewUnit({ ...newUnit, [field]: e.target.value }); if (unitErrors[field]) setUnitErrors((prev) => ({ ...prev, [field]: undefined })); }}
                      placeholder={label}
                    />
                  </FormField>
                ))}
                {[
                  { field: "balcony", label: "Balcón", options: [{ label: "Sí", value: true }, { label: "No", value: false }] },
                  { field: "garage", label: "Garaje", options: [{ label: "Sí", value: true }, { label: "No", value: false }] },
                ].map(({ field, label, options }) => (
                  <FormField key={field} label={label} htmlFor={`unidad-nueva-${field}`}>
                    <select
                      id={`unidad-nueva-${field}`}
                      className="habitia-input"
                      style={styles.input}
                      value={String(newUnit[field] ?? "")}
                      onChange={(e) => setNewUnit({ ...newUnit, [field]: e.target.value === "true" })}
                    >
                      <option value="">Seleccionar...</option>
                      {options.map((opt) => (
                        <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
                      ))}
                    </select>
                  </FormField>
                ))}
                {/* Orientación */}
                <FormField label="Orientación" htmlFor="unidad-nueva-orientation">
                  <select
                    id="unidad-nueva-orientation"
                    className="habitia-input"
                    style={styles.input}
                    value={newUnit.orientation || ""}
                    onChange={(e) => setNewUnit({ ...newUnit, orientation: e.target.value })}
                  >
                    <option value="">Seleccionar...</option>
                    {["Frente", "Contrafrente", "Lateral", "Interno"].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </FormField>
                {/* Tipo de unidad: portería y alquiler del edificio no participan del reparto de gastos comunes */}
                <FormField label="Tipo de unidad" htmlFor="unidad-nueva-unitKind">
                  <select
                    id="unidad-nueva-unitKind"
                    className="habitia-input"
                    style={styles.input}
                    value={normKind(newUnit)}
                    onChange={(e) => {
                      const unitKind = e.target.value;
                      setNewUnit(unitKind === UNIT_KIND_ALQUILER
                        ? { ...newUnit, unitKind, owner: "", ownerSecondary: "" }
                        : { ...newUnit, unitKind });
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="porteria">Portería (no paga gastos comunes)</option>
                    <option value={UNIT_KIND_ALQUILER}>Alquiler del edificio (no paga gastos comunes; el inquilino sí entra)</option>
                  </select>
                </FormField>
                {esAlquilerDelEdificio(newUnit) && (
                  <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 }}>
                    El dueño es el edificio: la unidad no tiene propietario, no paga gastos comunes ni fondos y su alícuota es 0. Asigná solo inquilinos; el contrato y los montos del alquiler se cargan en la ficha de la unidad.
                  </p>
                )}
                {noBuildingResidents && (
                  <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 }}>
                    Solo podés asignar como propietario o inquilino a residentes que ya pertenecen a este edificio. Para dar de alta a un residente nuevo, usá Gestión de residentes o la carga masiva.
                  </p>
                )}
                {!esAlquilerDelEdificio(newUnit) && (
                <FormField label="Propietario">
                  <SearchableSelect
                    value={newUnit.owner}
                    onChange={(v) => setNewUnit({ ...newUnit, owner: v })}
                    options={buildingResidentOptions}
                    placeholder="Seleccionar..."
                    emptyLabel="— Sin propietario —"
                  />
                </FormField>
                )}
                <FormField label="Inquilino">
                  <SearchableSelect
                    value={newUnit.tenant}
                    onChange={(v) => setNewUnit({ ...newUnit, tenant: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin inquilino"
                    emptyLabel="— Sin inquilino —"
                  />
                </FormField>
                {!esAlquilerDelEdificio(newUnit) && (
                <FormField label="Propietario complementario">
                  <SearchableSelect
                    value={newUnit.ownerSecondary}
                    onChange={(v) => setNewUnit({ ...newUnit, ownerSecondary: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin propietario complementario"
                    emptyLabel="— Sin propietario complementario —"
                  />
                </FormField>
                )}
                <FormField label="Inquilino complementario">
                  <SearchableSelect
                    value={newUnit.tenantSecondary}
                    onChange={(v) => setNewUnit({ ...newUnit, tenantSecondary: v })}
                    options={buildingResidentOptions}
                    placeholder="Sin inquilino complementario"
                    emptyLabel="— Sin inquilino complementario —"
                  />
                </FormField>
              </div>
            </AdminModal>

            {/* MODAL CARGA MASIVA */}
            {showBulkModal && (
              <div style={styles.modalOverlay}>
                <div style={styles.bulkModal}>
                  <div style={styles.modalHeader}>
                    <h4 style={styles.modalTitle}>
                      {bulkMode === "excel" ? (
                        <><FaFileExcel size={16} /> Cargar Unidades desde Excel</>
                      ) : (
                        <><FaRobot size={16} /> Cargar Unidades con IA</>
                      )}
                    </h4>
                    <button
                      type="button"
                      style={{ ...styles.closeBtn, ...(bulkSaving ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                      onClick={cerrarModalCarga}
                      disabled={bulkSaving}
                      aria-label="Cerrar"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {bulkResult ? (
                    /* RESULTADO POR FILA (quedaron unidades con error) */
                    <div style={styles.bulkModalBody}>
                      <div style={styles.bulkResultSummary}>
                        <p style={{ margin: 0 }}>
                          Se guardaron {bulkResult.creadas} unidades.
                          {bulkResult.omitidas > 0 ? ` ${bulkResult.omitidas} omitidas (ya existían).` : ""}
                          {bulkResult.sinDatos > 0 ? ` ${bulkResult.sinDatos} sin número o piso.` : ""}
                          {` ${bulkResult.errores} con error.`}
                        </p>
                        {bulkResult.nuevas > 0 && (
                          <p style={{ margin: `${spacing[2]}px 0 0`, color: colors.gray600 }}>
                            {bulkResult.nuevas} personas nuevas quedaron «Sin enviar» en Gestión de residentes: invitalas desde ahí.
                            {bulkResult.asignadas > 0 ? ` ${bulkResult.asignadas} ocupantes se asignaron a residentes ya cargados.` : ""}
                          </p>
                        )}
                      </div>
                      {bulkResult.errorGeneral && (
                        <div style={styles.bulkErrorBanner} role="alert">
                          La carga se cortó: {bulkResult.errorGeneral}
                        </div>
                      )}
                      <DataTable
                        columns={[
                          { key: "etiqueta", label: "Unidad" },
                          {
                            key: "estado",
                            label: "Estado",
                            render: (estado, fila) =>
                              estado === "creada" ? (
                                <StatusBadge status="completo" label="Creada" size="sm" />
                              ) : estado === "omitida" ? (
                                <StatusBadge status="cerrada" label="Ya existía" size="sm" />
                              ) : esFilaEnRevision(fila) ? (
                                <StatusBadge status="pendiente" label="En revisión" size="sm" />
                              ) : (
                                <StatusBadge status="error" label="Error" size="sm" />
                              ),
                          },
                          {
                            key: "error",
                            label: "Detalle",
                            render: (error, fila) =>
                              fila.estado === "omitida"
                                ? "Ya hay una unidad con ese número y piso."
                                : esFilaEnRevision(fila)
                                  ? textoFilaEnRevision(fila)
                                  : error || "—",
                          },
                        ]}
                        data={bulkResult.filas
                          .filter((r) => r.estado !== "creada")
                          .map((r) => ({ ...r, id: `fila-${r.idx}` }))}
                        maxHeight="320px"
                        emptyTitle="No quedaron unidades con error"
                      />
                    </div>
                  ) : (
                  <>
                  {/* TABS DE MODO */}
                  <div style={styles.bulkTabs}>
                    <button
                      style={bulkMode === "excel" ? styles.bulkTabActive : styles.bulkTab}
                      onClick={() => setBulkMode("excel")}
                      disabled={bulkSaving}
                    >
                      <FaFileExcel size={12} /> Desde Excel
                    </button>
                    <button
                      style={bulkMode === "ai" ? styles.bulkTabActive : styles.bulkTab}
                      onClick={() => setBulkMode("ai")}
                      disabled={bulkSaving}
                    >
                      <FaRobot size={12} /> Con IA
                    </button>
                  </div>

                  <div style={styles.bulkModalBody}>
                    {bulkMode === "excel" ? (
                      /* MODO EXCEL */
                      <div style={styles.csvSection}>
                        <div style={styles.csvInstructions}>
                          <p><strong>Instrucciones:</strong></p>
                          <ol>
                            <li>Descargá el template Excel haciendo clic en "Descargar Template"</li>
                            <li>Completá los datos de unidades y, opcionalmente, propietario/inquilino</li>
                            <li>Subí el archivo completado (.xlsx)</li>
                          </ol>
                          <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 6 }}>
                            Las personas nuevas quedan como «Sin enviar» en <strong style={{ fontWeight: 400 }}>Gestión de residentes</strong>, para mandarles la invitación desde ahí.
                          </p>
                          <button onClick={downloadExcelTemplate} style={styles.downloadTemplateBtn}>
                            <FaDownload size={12} /> Descargar Template Excel
                          </button>
                        </div>
                        
                        <div style={styles.uploadSection}>
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            aria-label="Archivo Excel de unidades"
                            onChange={handleExcelUpload}
                            ref={fileInputRef}
                            style={{ display: "none" }}
                          />
                          <button 
                            onClick={() => fileInputRef.current?.click()} 
                            style={styles.uploadBtn}
                          >
                            <FaUpload size={14} /> Seleccionar archivo Excel
                          </button>
                        </div>

                        {excelData.length > 0 && (
                          <div style={styles.previewSection}>
                            <h5 style={styles.previewTitle}>
                              Vista previa ({excelData.length} unidades detectadas)
                            </h5>
                            {(() => {
                              const totalWithAnyResident = excelData.filter(
                                (u) =>
                                  hasValue(u.propNombre) ||
                                  hasValue(u.propNombre2) ||
                                  hasValue(u.inqNombre) ||
                                  hasValue(u.inqNombre2)
                              ).length;
                              if (totalWithAnyResident > 0) return null;
                              return (
                                <div style={styles.excelParseWarning}>
                                  No se detectaron propietarios/inquilinos en ninguna fila. Revisá los encabezados del Excel
                                  (ej: <strong>prop_nombre</strong>, <strong>prop_cedula</strong>, <strong>inq_nombre</strong>).
                                </div>
                              );
                            })()}
                            <div style={styles.previewTable}>
                              <table style={styles.miniTable}>
                                <thead>
                                  <tr>
                                    <th style={styles.miniTableTh}>Número</th>
                                    <th style={styles.miniTableTh}>Piso</th>
                                    <th style={styles.miniTableTh}>M²</th>
                                    <th style={styles.miniTableTh}>V. Catastral</th>
                                    <th style={styles.miniTableTh}>Dorm.</th>
                                    <th style={styles.miniTableTh}>Baños</th>
                                    <th style={styles.miniTableTh}>Propietario</th>
                                    <th style={styles.miniTableTh}>Prop. compl.</th>
                                    <th style={styles.miniTableTh}>Inquilino</th>
                                    <th style={styles.miniTableTh}>Inq. compl.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {excelData.slice(0, 10).map((u, i) => (
                                    <tr key={i}>
                                      <td style={styles.miniTableTd}>{u.number}</td>
                                      <td style={styles.miniTableTd}>{u.floor}</td>
                                      <td style={styles.miniTableTd}>{u.m2 || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.valorCatastral || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.bedroom || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.bathroom || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.propNombre || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.propNombre2 || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.inqNombre || "-"}</td>
                                      <td style={styles.miniTableTd}>{u.inqNombre2 || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {excelData.length > 10 && (
                                <p style={styles.moreItems}>... y {excelData.length - 10} más</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* MODO IA */
                      <div style={styles.aiSection}>
                        <div style={styles.aiInstructions}>
                          <p><strong>¿Cómo funciona?</strong></p>
                          <p>La IA puede extraer información de unidades desde:</p>
                          <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
                            <li><FaFile size={10} /> <strong>Texto:</strong> Pegá mensajes de WhatsApp, emails, etc.</li>
                            <li><FaImage size={10} /> <strong>Imágenes:</strong> Fotos de listados, planillas, planos</li>
                            <li><FaFilePdf size={10} /> <strong>PDFs:</strong> Documentos con información de unidades</li>
                          </ul>
                        </div>

                        {/* Área de texto */}
                        <div style={styles.aiInputSection}>
                          <label style={styles.aiInputLabel}>
                            <FaFile size={12} /> Texto (opcional)
                          </label>
                          <textarea
                            style={styles.aiTextarea}
                            placeholder="Pegá aquí el texto con la información de las unidades (de WhatsApp, email, etc.)..."
                            value={aiText}
                            onChange={(e) => setAiText(e.target.value)}
                            rows={5}
                          />
                        </div>

                        {/* Área de archivos */}
                        <div style={styles.aiInputSection}>
                          <label style={styles.aiInputLabel}>
                            <FaImage size={12} /> Imágenes y PDFs (opcional)
                          </label>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                            onChange={handleAIFileUpload}
                            ref={aiFileInputRef}
                            style={{ display: "none" }}
                            multiple
                          />
                          <div style={styles.fileUploadZone} onClick={() => aiFileInputRef.current?.click()}>
                            <FaUpload size={20} color={colors.gray600} />
                            <p style={styles.fileUploadText}>
                              Clic para subir imágenes o PDFs
                            </p>
                            <p style={styles.fileUploadHint}>
                              JPG, PNG, GIF, WEBP, PDF
                            </p>
                          </div>

                          {/* Lista de archivos subidos */}
                          {aiFiles.length > 0 && (
                            <div style={styles.fileList}>
                              {aiFiles.map((file, index) => (
                                <div key={index} style={styles.fileItem}>
                                  {file.type.startsWith('image/') ? (
                                    <FaImage size={14} color={colors.primary} />
                                  ) : (
                                    <FaFilePdf size={14} color={colors.errorDark} />
                                  )}
                                  <span style={styles.fileName}>{file.name}</span>
                                  <button
                                    style={styles.fileRemoveBtn}
                                    onClick={() => removeAiFile(index)}
                                    aria-label="Quitar archivo"
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <button 
                          onClick={processTextWithAI} 
                          style={styles.processAIBtn}
                          disabled={processingAI || (!aiText.trim() && aiFiles.length === 0)}
                        >
                          {processingAI ? (
                            <><span style={styles.spinner}></span> Procesando con IA...</>
                          ) : (
                            <><FaMagic size={14} /> Procesar con IA</>
                          )}
                        </button>

                        {revision.filas.length > 0 && (
                          <div style={styles.previewSection}>
                            <h5 style={styles.previewTitle}>
                              <FaMagic size={12} /> Tenés un borrador con {revision.filas.length}{" "}
                              {revision.filas.length === 1 ? "unidad" : "unidades"}
                            </h5>
                            <p style={styles.borradorNota}>
                              Seguí completándolo, o procesá otro documento: lo nuevo se suma al borrador sin repetir unidades.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  </>
                  )}

                  <div style={styles.modalFooter}>
                    {bulkResult ? (
                      <>
                        <button type="button" style={styles.secondaryBtn} onClick={cerrarModalCarga} disabled={bulkSaving}>
                          Cerrar
                        </button>
                        {reintentablesCarga > 0 && (
                          <button
                            type="button"
                            style={{ ...styles.primaryBtn, ...(bulkSaving ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                            onClick={reintentarImportacion}
                            disabled={bulkSaving}
                          >
                            <FaSync size={14} />{" "}
                            {bulkSaving
                              ? `Guardando${bulkProgress ? ` ${bulkProgress.hechas}/${bulkProgress.total}` : ""}...`
                              : `Reintentar ${reintentablesCarga} ${reintentablesCarga === 1 ? "unidad" : "unidades"}`}
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button type="button" style={styles.secondaryBtn} onClick={cerrarModalCarga} disabled={bulkSaving}>
                          Cancelar
                        </button>
                        {bulkMode === "excel" ? (
                          <button
                            type="button"
                            style={{ ...styles.primaryBtn, ...(bulkSaving ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                            onClick={handleBulkSave}
                            disabled={bulkSaving || excelData.length === 0}
                          >
                            {bulkSaving ? `Guardando${bulkProgress ? ` ${bulkProgress.hechas}/${bulkProgress.total}` : ""}...` : (
                              <>
                                <FaSave size={14} /> Guardar {excelData.length} unidades
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            style={{ ...styles.primaryBtn, ...(revision.filas.length === 0 ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                            onClick={abrirRevision}
                            disabled={bulkSaving || revision.filas.length === 0}
                          >
                            <FaMagic size={14} /> Revisar y completar{revision.filas.length ? ` (${revision.filas.length})` : ""}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* REVISIÓN DE LA CARGA CON IA */}
            <RevisionUnidadesModal
              open={showRevision}
              filas={revision.filas}
              onChange={setFilasRevision}
              unidadesExistentes={units}
              actualizadoEn={revisionGuardadaEn}
              guardando={bulkSaving}
              progreso={bulkProgress}
              onGuardar={guardarRevision}
              onCerrar={seguirRevisionDespues}
              onDescartar={descartarRevision}
            />

            {/* MODAL ALÍCUOTAS */}
            <AdminModal
              open={showAlicuotasModal}
              onClose={() => setShowAlicuotasModal(false)}
              title="Alícuotas (Milésimas)"
              icon={<FaCalculator />}
              size="wide"
              footer={
                <>
                  <button style={styles.secondaryBtn} onClick={() => setShowAlicuotasModal(false)}>Cerrar</button>
                  <button
                    style={{ ...styles.primaryBtn, opacity: savingFinanzas ? 0.6 : 1 }}
                    onClick={async () => {
                      await handleSaveFinanzas();
                      setShowAlicuotasModal(false);
                    }}
                    disabled={savingFinanzas}
                  >
                    <FaSave size={14} />
                    {savingFinanzas ? "Guardando..." : "Guardar alícuotas"}
                  </button>
                </>
              }
            >
              <div style={{
                background: colors.infoLight,
                border: `1px solid ${colors.info}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                fontSize: 12,
                color: colors.infoDark,
              }}>
                La alícuota de cada unidad se calcula como: (valor catastral de la unidad / suma de todos los valores catastrales) × 1000.
                El valor real debe basarse en el valor catastral según la DNC (Dirección Nacional de Catastro). Consulte <a href="https://catastro.gub.uy" target="_blank" rel="noopener noreferrer" style={{ color: colors.infoDark, fontWeight: 400 }}>catastro.gub.uy</a>.
              </div>

              {(() => {
                const missing = alicuotas.filter(a => !a.fija && (!a.valorCatastral || Number(a.valorCatastral) <= 0));
                if (missing.length === 0) return null;
                return (
                  <div style={{
                    background: colors.warningLight,
                    border: `1px solid ${colors.warning}`,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    fontSize: 12,
                    color: colors.warningDark,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}>
                    <FaExclamationTriangle size={14} />
                    <span>
                      {missing.length === alicuotas.filter(a => !a.fija).length
                        ? "Ninguna unidad tiene valor catastral cargado."
                        : `Faltan valores catastrales en: ${missing.map(a => `Unidad ${a.number}`).join(", ")}.`
                      }
                      {" "}Completá los valores en la tabla para poder calcular las alícuotas.
                    </span>
                  </div>
                );
              })()}

              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <button
                  style={styles.addBtn}
                  onClick={() => {
                    const missing = alicuotas.filter(a => !a.fija && (!a.valorCatastral || Number(a.valorCatastral) <= 0));
                    if (missing.length > 0) {
                      showToast(`Falta valor catastral en: ${missing.map(a => `Unidad ${a.number}`).join(", ")}. Completá los valores primero.`, "warning");
                      return;
                    }
                    setAlicuotas(estimarAlicuotas(alicuotas, "valorCatastral"));
                    showToast("Alícuotas calculadas desde valor catastral.", "success");
                  }}
                >
                  <FaCalculator size={12} /> Calcular desde valor catastral
                </button>
                <button style={styles.addBtn} onClick={estimateFromM2}>
                  <FaCalculator size={12} /> Estimar desde m²
                </button>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={styles.thFinanzas}>Unidad</th>
                      <th style={styles.thFinanzas}>m²</th>
                      <th style={styles.thFinanzas}>Valor Catastral</th>
                      <th style={styles.thFinanzas}>Alícuota (‰)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alicuotas.map((a, idx) => (
                      <tr key={a.unitId} className="habitia-table-row">
                        <td style={styles.tdFinanzas}>{a.number}</td>
                        <td style={styles.tdFinanzas}>{a.m2 || "-"}</td>
                        <td style={styles.tdFinanzas}>
                          <input
                            className="habitia-input"
                            style={{ ...styles.input, padding: "4px 8px", fontSize: 12 }}
                            type="number"
                            value={a.valorCatastral}
                            onChange={e => {
                              const updated = [...alicuotas];
                              updated[idx] = { ...updated[idx], valorCatastral: e.target.value };
                              setAlicuotas(updated);
                            }}
                            placeholder="Ingresar valor"
                          />
                        </td>
                        <td style={styles.tdFinanzas}>
                          <input
                            className="habitia-input"
                            style={{ ...styles.input, padding: "4px 8px", fontSize: 12 }}
                            type="number"
                            step="0.0001"
                            value={a.alicuota}
                            disabled={a.fija}
                            aria-label={`Alícuota de la unidad ${a.number}`}
                            onChange={e => {
                              const updated = [...alicuotas];
                              updated[idx] = { ...updated[idx], alicuota: e.target.value };
                              setAlicuotas(updated);
                            }}
                            placeholder="0.0000"
                          />
                          {a.fija && (
                            <span style={{ display: "block", marginTop: 2, fontSize: typography.sizes.xs, color: colors.gray600 }}>
                              Alquiler del edificio: no paga gastos comunes, queda en 0.
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: colors.gray50, fontWeight: 400 }}>
                      <td style={styles.tdFinanzas} colSpan={3}>Total</td>
                      <td style={{
                        ...styles.tdFinanzas,
                        color: Math.abs(alicuotaSum - 1000) <= 1 ? colors.success : colors.errorDark,
                      }}>
                        {alicuotaSum.toFixed(4)} / 1000
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {Math.abs(alicuotaSum - 1000) > 1 && alicuotaSum > 0 && (
                <div style={{
                  background: colors.errorLight,
                  border: `1px solid ${colors.error}`,
                  borderRadius: 8,
                  padding: 10,
                  marginTop: 8,
                  fontSize: 12,
                  color: colors.errorDark,
                }}>
                  La suma de alícuotas debe ser 1000. Diferencia: {(alicuotaSum - 1000).toFixed(4)}
                </div>
              )}

              {Math.abs(alicuotaSum - 1000) <= 1 && alicuotaSum > 0 && (
                <div style={{
                  background: colors.successLight,
                  border: `1px solid ${colors.success}`,
                  borderRadius: 8,
                  padding: 10,
                  marginTop: 8,
                  fontSize: 12,
                  color: colors.successDark,
                }}>
                  Las alícuotas suman correctamente 1000 milésimas.
                </div>
              )}
            </AdminModal>

            {/* MODAL EDITAR PROPIETARIOS */}
            <EditarPropietariosModal
              open={showEditPropietariosModal}
              onClose={() => setShowEditPropietariosModal(false)}
              units={units}
              usersMap={Object.fromEntries(residents.map((r) => [String(r.userid), r]))}
              buildingid={buildingData?.id}
              onSaved={(savedRows) => {
                setUnits((prev) => prev.map((u) => {
                  const r = savedRows.find((s) => s.id === u.id);
                  if (!r) return u;
                  return { ...u, ownerData: r.ownerData, tenantData: r.tenantData };
                }));
              }}
            />

            {/* MODAL SALDOS INICIALES */}
            <AdminModal
              open={showInitialBalancesModal}
              onClose={() => setShowInitialBalancesModal(false)}
              title="Saldos Iniciales por Unidad"
              icon={<FaMoneyBillWave />}
              size="wide"
              footer={
                <>
                  <button style={styles.secondaryBtn} onClick={() => setShowInitialBalancesModal(false)}>Cancelar</button>
                  <button
                    style={{ ...styles.primaryBtn, opacity: savingInitialBalances ? 0.6 : 1 }}
                    onClick={async () => {
                      if (!migrationMaturity) {
                        showToast("Elegí la fecha del saldo.", "warning");
                        return;
                      }
                      // La fecha del saldo es la fuente de verdad: el mes/año del
                      // payment se derivan de ella (parseamos a mediodía para evitar
                      // corrimientos por zona horaria).
                      const matDate = new Date(`${migrationMaturity}T12:00:00`);
                      const refMonth = matDate.getMonth() + 1;
                      const refYear = matDate.getFullYear();

                      setSavingInitialBalances(true);
                      try {
                        // Bloqueo modo demo: mensaje claro en vez del error de permisos.
                        if (await isDemoUser()) {
                          showToast(DEMO_BLOCK_MESSAGE, "warning");
                          setSavingInitialBalances(false);
                          return;
                        }
                        const bId = Number(buildingData?.buildingid);
                        const maturity = migrationMaturity;

                        // Buscar payments de migración existentes para este edificio
                        const migQ = query(
                          collection(db, "payments"),
                          where("buildingid", "==", bId),
                          where("isMigration", "==", true)
                        );
                        const migSnap = await getDocs(migQ);
                        const existingMig = migSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                        window.__saldosOp = "init";
                        for (const row of initialBalancesData) {
                          const ownerTotal = (Number(row.ownerGastos) || 0) + (Number(row.ownerReserva) || 0);
                          const tenantTotal = Number(row.tenantGastos) || 0;
                          const ownerId = row.owner ? String(row.owner) : `unit_${row.id}`;
                          const tenantId = row.tenant ? String(row.tenant) : null;

                          // Guardar en apartment
                          const unitRef = doc(db, "apartments", row.id);
                          window.__saldosOp = `apartments.update id=${row.id}`;
                          await updateDoc(unitRef, {
                            initialBalanceOwner: Number(row.ownerGastos) || 0,
                            initialBalanceOwnerReserva: Number(row.ownerReserva) || 0,
                            initialBalanceTenant: Number(row.tenantGastos) || 0,
                          });

                          // Payment de migración para propietario
                          const existingOwnerPay = existingMig.find(p => p.unitId === row.id && p.userid === ownerId);
                          if (ownerTotal > 0) {
                            const payData = {
                              userid: ownerId,
                              buildingid: bId,
                              amount: ownerTotal,
                              remainingAmount: ownerTotal,
                              paidAmount: 0,
                              paid: false,
                              status: "pendiente",
                              month: refMonth,
                              year: refYear,
                              maturity,
                              description: "Saldo inicial — migración",
                              breakdown: {
                                gastos_comunes: Number(row.ownerGastos) || 0,
                                fondo_reserva: Number(row.ownerReserva) || 0,
                              },
                              isMigration: true,
                              unitId: row.id,
                              createdAt: serverTimestamp(),
                            };
                            if (existingOwnerPay) {
                              window.__saldosOp = `payments.update(owner) id=${existingOwnerPay.id} userid=${ownerId} bid=${bId}`;
                              await updateDoc(doc(db, "payments", existingOwnerPay.id), payData);
                            } else {
                              window.__saldosOp = `payments.create(owner) userid=${ownerId} bid=${bId}`;
                              await addDoc(collection(db, "payments"), payData);
                            }
                          } else if (existingOwnerPay) {
                            window.__saldosOp = `payments.delete(owner) id=${existingOwnerPay.id}`;
                            await deleteDoc(doc(db, "payments", existingOwnerPay.id));
                          }

                          // Payment de migración para inquilino
                          if (tenantId) {
                            const existingTenantPay = existingMig.find(p => p.unitId === row.id && p.userid === tenantId);
                            if (tenantTotal > 0) {
                              const payData = {
                                userid: tenantId,
                                buildingid: bId,
                                amount: tenantTotal,
                                remainingAmount: tenantTotal,
                                paidAmount: 0,
                                paid: false,
                                status: "pendiente",
                                month: refMonth,
                                year: refYear,
                                maturity,
                                description: "Saldo inicial — migración",
                                breakdown: { gastos_comunes: tenantTotal },
                                isMigration: true,
                                unitId: row.id,
                                createdAt: serverTimestamp(),
                              };
                              if (existingTenantPay) {
                                window.__saldosOp = `payments.update(tenant) id=${existingTenantPay.id} userid=${tenantId} bid=${bId}`;
                                await updateDoc(doc(db, "payments", existingTenantPay.id), payData);
                              } else {
                                window.__saldosOp = `payments.create(tenant) userid=${tenantId} bid=${bId}`;
                                await addDoc(collection(db, "payments"), payData);
                              }
                            } else if (existingTenantPay) {
                              window.__saldosOp = `payments.delete(tenant) id=${existingTenantPay.id}`;
                              await deleteDoc(doc(db, "payments", existingTenantPay.id));
                            }
                          }
                        }

                        // Actualizar estado local de unidades
                        setUnits(units.map(u => {
                          const row = initialBalancesData.find(r => r.id === u.id);
                          if (!row) return u;
                          return {
                            ...u,
                            initialBalanceOwner: Number(row.ownerGastos) || 0,
                            initialBalanceOwnerReserva: Number(row.ownerReserva) || 0,
                            initialBalanceTenant: Number(row.tenantGastos) || 0,
                          };
                        }));

                        showToast("Saldos iniciales guardados y payments de migración creados.", "success");
                        setShowInitialBalancesModal(false);
                      } catch (err) {
                        console.error("Error guardando saldos iniciales:", err);
                        // DIAGNÓSTICO TEMPORAL: leer el claim real (forzando refresh)
                        // y mostrarlo junto al error para entender el permission-denied.
                        let claimDemo = "n/d";
                        try {
                          const tr = await auth.currentUser?.getIdTokenResult(true);
                          claimDemo = String(tr?.claims?.habitia_demo);
                        } catch (e2) {
                          claimDemo = "error:" + (e2?.message || e2);
                        }
                        window.alert(
                          "[SALDOS DEBUG]\n" +
                            JSON.stringify(
                              {
                                email: auth.currentUser?.email,
                                habitia_demo: claimDemo,
                                opQueFallo: window.__saldosOp,
                                errorCode: err?.code,
                                errorMsg: err?.message,
                              },
                              null,
                              2
                            )
                        );
                        const demo = claimDemo === "true";
                        const msg = demo
                          ? DEMO_BLOCK_MESSAGE
                          : `Error al guardar saldos iniciales${err?.code ? ` (${err.code})` : ""}.`;
                        showToast(msg, "error");
                      } finally {
                        setSavingInitialBalances(false);
                      }
                    }}
                    disabled={savingInitialBalances}
                  >
                    <FaSave size={14} />
                    {savingInitialBalances ? "Guardando..." : "Guardar saldos"}
                  </button>
                </>
              }
            >
              <div style={{
                background: colors.infoLight,
                border: `1px solid ${colors.info}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                fontSize: 12,
                color: colors.infoDark,
              }}>
                Cargá los saldos iniciales de cada unidad. La <strong>fecha del saldo</strong> (no es el
                vencimiento del gasto común) define en qué mes queda contabilizado; por defecto, el último día del
                mes actual. Los inquilinos no pagan fondo de reserva.
              </div>

              {/* Fecha del saldo (define el mes en que se contabiliza) */}
              <div style={{ display: "flex", gap: 12, marginBottom: 4, alignItems: "center" }}>
                <label style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Fecha del saldo:</label>
                <input
                  type="date"
                  style={{ ...styles.input, width: 170, padding: "4px 8px", fontSize: 13 }}
                  value={migrationMaturity}
                  onChange={e => setMigrationMaturity(e.target.value)}
                />
              </div>
              <p style={{ fontSize: typography.sizes.xs, color: colors.gray600, margin: "0 0 16px", lineHeight: 1.5 }}>
                Vencimiento configurado en Finanzas: día {buildingData?.paymentDueDay || "no configurado"}. Cargá el
                saldo con una fecha posterior al vencimiento para que cuente en el mes correcto.
              </p>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={styles.thFinanzas} rowSpan={2}>Unidad</th>
                      <th style={{ ...styles.thFinanzas, textAlign: "center" }} colSpan={2}>Propietario ($)</th>
                      <th style={{ ...styles.thFinanzas, textAlign: "center" }} colSpan={1}>Inquilino ($)</th>
                    </tr>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={styles.thFinanzas}>Gastos Com.</th>
                      <th style={styles.thFinanzas}>Fdo. Reserva</th>
                      <th style={styles.thFinanzas}>Gastos Com.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initialBalancesData.map((row, idx) => (
                      <tr key={row.id} className="habitia-table-row">
                        <td style={styles.tdFinanzas}>{row.number}</td>
                        <td style={styles.tdFinanzas}>
                          <input
                            className="habitia-input"
                            style={{ ...styles.input, padding: "4px 8px", fontSize: 12, width: 90 }}
                            type="number"
                            value={row.ownerGastos}
                            onChange={e => {
                              const updated = [...initialBalancesData];
                              updated[idx] = { ...updated[idx], ownerGastos: e.target.value === "" ? "" : Number(e.target.value) };
                              setInitialBalancesData(updated);
                            }}
                          />
                        </td>
                        <td style={styles.tdFinanzas}>
                          <input
                            className="habitia-input"
                            style={{ ...styles.input, padding: "4px 8px", fontSize: 12, width: 90 }}
                            type="number"
                            value={row.ownerReserva}
                            onChange={e => {
                              const updated = [...initialBalancesData];
                              updated[idx] = { ...updated[idx], ownerReserva: e.target.value === "" ? "" : Number(e.target.value) };
                              setInitialBalancesData(updated);
                            }}
                          />
                        </td>
                        <td style={styles.tdFinanzas}>
                          <input
                            className="habitia-input"
                            style={{ ...styles.input, padding: "4px 8px", fontSize: 12, width: 90 }}
                            type="number"
                            value={row.tenantGastos}
                            onChange={e => {
                              const updated = [...initialBalancesData];
                              updated[idx] = { ...updated[idx], tenantGastos: e.target.value === "" ? "" : Number(e.target.value) };
                              setInitialBalancesData(updated);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdminModal>

            {/* MODAL CONSULTA CATASTRAL */}
            <AdminModal
              open={showCatastroModal}
              onClose={() => { if (!catastroLoading) setShowCatastroModal(false); }}
              title="Actualizar Valores Catastrales"
              icon={<FaSync />}
              size="wide"
              footer={
                !catastroLoading && !catastroResults ? (
                  <>
                    <button style={styles.secondaryBtn} onClick={() => setShowCatastroModal(false)}>Cancelar</button>
                    <button
                      style={{ ...styles.primaryBtn, opacity: catastroSelectedUnits.length === 0 ? 0.5 : 1 }}
                      onClick={executeCatastroQuery}
                      disabled={catastroSelectedUnits.length === 0}
                    >
                      <FaSync size={14} /> Consultar {catastroSelectedUnits.length} unidades
                    </button>
                  </>
                ) : catastroResults ? (
                  <>
                    <button style={styles.secondaryBtn} onClick={() => { setShowCatastroModal(false); setCatastroResults(null); }}>
                      Cancelar
                    </button>
                    {catastroResults.some(r => r.status === "ok") && (
                      <button style={styles.primaryBtn} onClick={applyCatastroResults}>
                        <FaSave size={14} /> Aplicar Cambios ({catastroResults.filter(r => r.status === "ok").length} unidades)
                      </button>
                    )}
                  </>
                ) : null
              }
            >
              {/* Estado: Confirmación con selección de unidades */}
              {!catastroLoading && !catastroResults && (
                <>
                  <div style={{
                    background: colors.infoLight,
                    border: `1px solid ${colors.info}`,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    fontSize: typography.sm,
                    color: colors.infoDark,
                  }}>
                    Padrón <strong>{buildingData?.padron}</strong> — Seleccioná las unidades a consultar.
                    Tiempo estimado: ~{Math.ceil(catastroSelectedUnits.length * 8 / 60)} min
                    ({catastroSelectedUnits.length} de {units.length} unidades).
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <button
                      type="button"
                      style={{ ...btnGhost, fontSize: typography.xs, padding: "4px 10px" }}
                      onClick={() => setCatastroSelectedUnits(units.map(u => u.id))}
                    >
                      Seleccionar todas
                    </button>
                    <button
                      type="button"
                      style={{ ...btnGhost, fontSize: typography.xs, padding: "4px 10px" }}
                      onClick={() => setCatastroSelectedUnits([])}
                    >
                      Deseleccionar todas
                    </button>
                  </div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 6,
                    maxHeight: 260,
                    overflowY: "auto",
                    padding: 4,
                  }}>
                    {units.map(u => {
                      const selected = catastroSelectedUnits.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderRadius: radius.sm,
                            border: `1px solid ${selected ? colors.primary : colors.gray300}`,
                            background: selected ? colors.primaryLight || "#e8f4ff" : colors.gray50,
                            cursor: "pointer",
                            fontSize: typography.sm,
                            transition: transitions.normal,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              setCatastroSelectedUnits(prev =>
                                selected ? prev.filter(id => id !== u.id) : [...prev, u.id]
                              );
                            }}
                            style={{ accentColor: colors.primary }}
                          />
                          <span style={{ color: colors.gray800 }}>Unidad {u.number}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Estado: Progreso */}
              {catastroLoading && (
                <div style={{ padding: "12px 0" }}>
                  <div style={{
                    background: colors.gray200,
                    borderRadius: radius.full,
                    height: 12,
                    overflow: "hidden",
                    marginBottom: 12,
                  }}>
                    <div style={{
                      background: colors.primary,
                      height: "100%",
                      width: `${catastroProgress}%`,
                      borderRadius: radius.full,
                      transition: transitions.normal,
                    }} />
                  </div>
                  <p style={{ fontSize: typography.base, color: colors.gray700, marginBottom: 8, textAlign: "center" }}>
                    Consultando unidad {(catastroResults?.length || 0) + 1} de {catastroSelectedUnits.length}...
                  </p>
                  <p style={{ fontSize: typography.xs, color: colors.gray500, textAlign: "center" }}>
                    No cierres esta ventana
                  </p>
                </div>
              )}

              {/* Estado: Resultados (parciales durante carga, finales al terminar) */}
              {catastroResults && catastroResults.length > 0 && (
                <>
                  {!catastroLoading && (() => {
                    const ok = catastroResults.filter(r => r.status === "ok").length;
                    const fail = catastroResults.filter(r => r.status === "error").length;
                    const skipped = catastroResults.filter(r => r.status === "skipped").length;

                    return (
                      <>
                        {ok > 0 && (
                          <div style={{
                            background: colors.successLight,
                            border: `1px solid ${colors.success}`,
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 8,
                            fontSize: typography.sm,
                            color: colors.successDark,
                          }}>
                            {ok} {ok === 1 ? "unidad obtenida" : "unidades obtenidas"} correctamente — revisá y hacé click en "Aplicar Cambios" para guardar.
                          </div>
                        )}
                        {fail > 0 && (
                          <div style={{
                            background: colors.errorLight,
                            border: `1px solid ${colors.error}`,
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 8,
                            fontSize: typography.sm,
                            color: colors.errorDark,
                          }}>
                            {fail} {fail === 1 ? "unidad" : "unidades"} con error — no se actualizarán.
                            {skipped > 0 ? ` ${skipped} omitidas por 3 errores consecutivos.` : ""}
                          </div>
                        )}
                        {ok === 0 && (
                          <div style={{
                            background: colors.warningLight,
                            border: `1px solid ${colors.warning}`,
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 8,
                            fontSize: typography.sm,
                            color: colors.warningDark,
                          }}>
                            No se pudieron obtener datos para ninguna unidad.
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={styles.thFinanzas}>Unidad</th>
                          <th style={styles.thFinanzas}>V. Catastral</th>
                          <th style={styles.thFinanzas}>Área m²</th>
                          <th style={styles.thFinanzas}>Vigencia</th>
                          <th style={styles.thFinanzas}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catastroResults.map((r, i) => (
                          <tr key={i} style={{ opacity: r.status === "skipped" ? 0.5 : 1 }}>
                            <td style={styles.tdFinanzas}>{r.unidad ?? "-"}</td>
                            <td style={styles.tdFinanzas}>
                              {r.status === "ok" && r.valorCatastral
                                ? `$ ${Number(r.valorCatastral).toLocaleString("es-UY")}`
                                : "-"}
                            </td>
                            <td style={styles.tdFinanzas}>
                              {r.status === "ok" && r.areaUnidad ? `${r.areaUnidad} m²` : "-"}
                            </td>
                            <td style={styles.tdFinanzas}>
                              {r.status === "ok" && r.vigencia ? r.vigencia : "-"}
                            </td>
                            <td style={styles.tdFinanzas}>
                              {r.status === "ok" ? (
                                <span style={{
                                  background: colors.successLight,
                                  color: colors.successDark,
                                  padding: "2px 8px",
                                  borderRadius: radius.full,
                                  fontSize: typography.xs,
                                }}>OK</span>
                              ) : r.status === "skipped" ? (
                                <span style={{
                                  background: colors.gray200,
                                  color: colors.gray600,
                                  padding: "2px 8px",
                                  borderRadius: radius.full,
                                  fontSize: typography.xs,
                                }}>Omitida</span>
                              ) : (
                                <span style={{
                                  background: colors.errorLight,
                                  color: colors.errorDark,
                                  padding: "2px 8px",
                                  borderRadius: radius.full,
                                  fontSize: typography.xs,
                                }}>Error</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </AdminModal>
          </div>
        )}

        {tab === "empleado" && (
          <div style={styles.card} data-tutorial="editar-tab-personal">
            <h3 style={styles.cardTitle}>Personal del Edificio</h3>

            <div style={styles.addSection}>
              <h4 style={styles.addSectionTitle}>Agregar Personal</h4>
              <div style={styles.formGrid}>
                <FormField label="Tipo de documento" htmlFor="emp-nuevo-tipoDocumento" required error={employeeErrors.tipoDocumento}>
                  <select
                    id="emp-nuevo-tipoDocumento"
                    style={{ ...styles.input, ...(employeeErrors.tipoDocumento ? { borderColor: colors.error } : {}) }}
                    className="habitia-input"
                    value={newEmployee.tipoDocumento || "CI"}
                    onChange={(e) => { setNewEmployee({ ...newEmployee, tipoDocumento: e.target.value }); if (employeeErrors.tipoDocumento) setEmployeeErrors((prev) => ({ ...prev, tipoDocumento: undefined })); }}
                  >
                    <option value="CI">CI</option>
                    <option value="DNI">DNI</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="RUT">RUT</option>
                    <option value="Otro">Otro</option>
                  </select>
                </FormField>
                {[
                  { field: "Documento", label: "Documento", type: "text" },
                  { field: "NombreCompleto", label: "Nombre", type: "text" },
                  { field: "ApellidosCompleto", label: "Apellidos", type: "text" },
                  { field: "email", label: "Email", type: "email" },
                  { field: "celular", label: "Celular", type: "tel", inputMode: "tel" },
                  { field: "sueldo", label: "Sueldo", type: "number" },
                  { field: "inicioActividad", label: "Inicio Actividad", type: "date" },
                ].map(({ field, label, type, placeholder, inputMode }) => (
                  <FormField key={field} label={label} htmlFor={`emp-nuevo-${field}`} required error={employeeErrors[field]}>
                    <input
                      id={`emp-nuevo-${field}`}
                      style={{ ...styles.input, ...(employeeErrors[field] ? { borderColor: colors.error } : {}) }}
                      className="habitia-input"
                      type={type}
                      placeholder={placeholder}
                      value={newEmployee[field]}
                      onChange={(e) => { setNewEmployee({ ...newEmployee, [field]: e.target.value }); if (employeeErrors[field]) setEmployeeErrors((prev) => ({ ...prev, [field]: undefined })); }}
                      {...(inputMode ? { inputMode } : {})}
                    />
                  </FormField>
                ))}
                {/* Días - toggle buttons */}
                <FormField label="Días" required error={employeeErrors.diasTrabajo} style={{ gridColumn: "span 2" }}>
                  <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                    {DIAS_SEMANA.map((dia) => (
                      <button
                        key={dia}
                        type="button"
                        onClick={() => {
                          const selected = newEmployee.diasTrabajo.includes(dia)
                            ? newEmployee.diasTrabajo.filter((d) => d !== dia)
                            : [...newEmployee.diasTrabajo, dia];
                          setNewEmployee({ ...newEmployee, diasTrabajo: selected });
                          if (employeeErrors.diasTrabajo) setEmployeeErrors((prev) => ({ ...prev, diasTrabajo: undefined }));
                        }}
                        style={{
                          padding: `${spacing[1]}px ${spacing[3]}px`,
                          borderRadius: radius.sm,
                          border: `1px solid ${newEmployee.diasTrabajo.includes(dia) ? colors.primary : colors.gray300}`,
                          background: newEmployee.diasTrabajo.includes(dia) ? colors.primarySubtle : colors.white,
                          color: newEmployee.diasTrabajo.includes(dia) ? colors.primary : colors.gray600,
                          cursor: "pointer",
                          fontSize: typography.sizes.base,
                          fontWeight: 400,
                          transition: `all ${transitions.normal}`,
                        }}
                      >
                        {dia}
                      </button>
                    ))}
                  </div>
                </FormField>
                {/* Horario - time pickers */}
                <FormField label="Horario" htmlFor="emp-nuevo-horarioInicio" required error={employeeErrors.horario} style={{ gridColumn: "span 2" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <input
                      id="emp-nuevo-horarioInicio"
                      type="time"
                      className="habitia-input"
                      style={{ ...styles.input, width: 130, ...(employeeErrors.horario ? { borderColor: colors.error } : {}) }}
                      value={newEmployee.horarioInicio}
                      onChange={(e) => { setNewEmployee({ ...newEmployee, horarioInicio: e.target.value }); if (employeeErrors.horario) setEmployeeErrors((prev) => ({ ...prev, horario: undefined })); }}
                    />
                    <span style={{ color: colors.gray600 }}>a</span>
                    <input
                      id="emp-nuevo-horarioFin"
                      aria-label="Horario hasta"
                      type="time"
                      className="habitia-input"
                      style={{ ...styles.input, width: 130, ...(employeeErrors.horario ? { borderColor: colors.error } : {}) }}
                      value={newEmployee.horarioFin}
                      onChange={(e) => { setNewEmployee({ ...newEmployee, horarioFin: e.target.value }); if (employeeErrors.horario) setEmployeeErrors((prev) => ({ ...prev, horario: undefined })); }}
                    />
                  </div>
                </FormField>
              </div>
              <button onClick={handleAddEmployee} style={styles.addBtn} disabled={savingEmployee} className="habitia-btn-primary">
                <FaPlusCircle size={16} />
                {savingEmployee ? "Creando..." : "Agregar"}
              </button>
            </div>

            {employees.length === 0 ? (
              <EmptyState
                icon={FaUserTie}
                title="No hay personal registrado"
                description="Agregá personal del edificio usando el formulario de arriba."
              />
            ) : (
              <div style={{ ...styles.unitGrid, marginTop: spacing[5] }}>
                {employees.map((emp) => (
                  <div key={emp.id} style={styles.employeeCard}>
                    <div style={styles.unitHeader}>
                      <h4 style={styles.employeeName}>
                        {emp.userData?.NombreCompleto || "N/A"} {emp.userData?.ApellidosCompleto || ""}
                      </h4>
                      <div style={styles.unitActions}>
                        <button type="button" style={styles.iconBtn} onClick={() => openEditEmployee(emp)} aria-label="Editar" className="habitia-btn-ghost">
                          <FaPen size={12} />
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.iconBtnDanger, ...(removingEmployeeId ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                          onClick={() => handleDeleteEmployee(emp)}
                          disabled={!!removingEmployeeId}
                          aria-label="Eliminar"
                          className="habitia-btn-danger"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                    <div style={styles.employeeInfo}>
                      <p>Email: {emp.userData?.email || "-"}</p>
                      <p>Documento: {formatDocumentoDisplay(emp.userData)}</p>
                      <p>Celular: {emp.userData?.celular || "-"}</p>
                      <p>Horario: {emp.horario || "-"}</p>
                      <p>Días: {emp.diasTrabajo || "-"}</p>
                      <p>Sueldo: ${emp.sueldo || "-"}</p>
                      <p>Inicio: {emp.inicioActividad || "-"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Modal editar empleado */}
            <AdminModal
              open={showEmployeeModal}
              onClose={cerrarModalEmpleado}
              title="Editar Empleado"
              size="medium"
              icon={<FaUserTie />}
              footer={
                <>
                  <button type="button" style={styles.secondaryBtn} onClick={cerrarModalEmpleado} disabled={savingEmployeeEdit} className="habitia-btn-secondary">Cancelar</button>
                  <button
                    type="button"
                    style={{ ...styles.primaryBtn, ...(savingEmployeeEdit ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                    onClick={handleSaveEmployee}
                    disabled={savingEmployeeEdit}
                    className="habitia-btn-primary"
                  >
                    <FaSave size={14} /> {savingEmployeeEdit ? "Guardando..." : "Guardar"}
                  </button>
                </>
              }
            >
              <div style={styles.formGrid}>
                {editingEmployee && !editingEmployee.fichaEditable && (
                  <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.5 }}>
                    Este colaborador no tiene una ficha de colaborador vigente: acá solo se pueden cambiar horario, días, sueldo e inicio. Para corregir sus datos personales, pedíselo a soporte.
                  </p>
                )}
                {[
                  { field: "NombreCompleto", label: "Nombre", id: "emp-edit-nombre" },
                  { field: "ApellidosCompleto", label: "Apellidos", id: "emp-edit-apellidos" },
                ].map(({ field, label, id }) => (
                  <FormField key={field} label={label} htmlFor={id}>
                    <input
                      id={id}
                      className="habitia-input"
                      style={{ ...styles.input, ...(!editingEmployee?.fichaEditable ? { opacity: 0.6 } : {}) }}
                      type="text"
                      value={editingEmployee?.datos?.[field] ?? ""}
                      onChange={(e) => setDatoEmpleado(field, e.target.value)}
                      disabled={!editingEmployee?.fichaEditable || savingEmployeeEdit}
                    />
                  </FormField>
                ))}
                <FormField label="Tipo de documento" htmlFor="emp-edit-tipodoc">
                  <select
                    id="emp-edit-tipodoc"
                    className="habitia-input"
                    style={{ ...styles.input, ...(!editingEmployee?.fichaEditable ? { opacity: 0.6 } : {}) }}
                    value={editingEmployee?.datos?.tipoDocumento || "CI"}
                    onChange={(e) => setDatoEmpleado("tipoDocumento", e.target.value)}
                    disabled={!editingEmployee?.fichaEditable || savingEmployeeEdit}
                  >
                    {TIPOS_DOCUMENTO.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Documento" htmlFor="emp-edit-documento">
                  <input
                    id="emp-edit-documento"
                    className="habitia-input"
                    style={{ ...styles.input, ...(!editingEmployee?.fichaEditable ? { opacity: 0.6 } : {}) }}
                    type="text"
                    value={editingEmployee?.datos?.Documento ?? ""}
                    onChange={(e) => setDatoEmpleado("Documento", e.target.value)}
                    disabled={!editingEmployee?.fichaEditable || savingEmployeeEdit}
                  />
                </FormField>
                <FormField label="Email" htmlFor="emp-edit-email">
                  <input id="emp-edit-email" className="habitia-input" style={{ ...styles.input, opacity: 0.6 }} type="email" value={editingEmployee?.userData?.email || ""} disabled />
                </FormField>
                <FormField label="Celular" htmlFor="emp-edit-celular">
                  <input
                    id="emp-edit-celular"
                    className="habitia-input"
                    style={{ ...styles.input, ...(!editingEmployee?.fichaEditable ? { opacity: 0.6 } : {}) }}
                    type="tel"
                    inputMode="tel"
                    value={editingEmployee?.datos?.celular ?? ""}
                    onChange={(e) => setDatoEmpleado("celular", e.target.value)}
                    disabled={!editingEmployee?.fichaEditable || savingEmployeeEdit}
                  />
                </FormField>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="emp-edit-sueldo">Sueldo</label>
                  <input
                    id="emp-edit-sueldo"
                    className="habitia-input"
                    type="number"
                    style={styles.input}
                    value={editingEmployee?.sueldo ?? ""}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, sueldo: e.target.value })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="emp-edit-inicio">Inicio Actividad</label>
                  <input
                    id="emp-edit-inicio"
                    className="habitia-input"
                    type="date"
                    style={styles.input}
                    value={editingEmployee?.inicioActividad ?? ""}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, inicioActividad: e.target.value })}
                  />
                </div>
                {/* Días - toggle buttons */}
                <div style={{ ...styles.formGroup, gridColumn: "span 2" }}>
                  <label style={styles.label}>Días</label>
                  <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                    {DIAS_SEMANA.map((dia) => (
                      <button
                        key={dia}
                        type="button"
                        onClick={() => {
                          const arr = editingEmployee?.diasTrabajoArr || [];
                          const selected = arr.includes(dia) ? arr.filter((d) => d !== dia) : [...arr, dia];
                          setEditingEmployee({ ...editingEmployee, diasTrabajoArr: selected });
                        }}
                        style={{
                          padding: `${spacing[1]}px ${spacing[3]}px`,
                          borderRadius: radius.sm,
                          border: `1px solid ${(editingEmployee?.diasTrabajoArr || []).includes(dia) ? colors.primary : colors.gray300}`,
                          background: (editingEmployee?.diasTrabajoArr || []).includes(dia) ? colors.primarySubtle : colors.white,
                          color: (editingEmployee?.diasTrabajoArr || []).includes(dia) ? colors.primary : colors.gray600,
                          cursor: "pointer",
                          fontSize: typography.sizes.base,
                          fontWeight: 400,
                          transition: `all ${transitions.fast}`,
                        }}
                      >
                        {dia}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Horario - time pickers */}
                <div style={{ ...styles.formGroup, gridColumn: "span 2" }}>
                  <label style={styles.label} htmlFor="emp-edit-horarioInicio">Horario</label>
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                    <input
                      id="emp-edit-horarioInicio"
                      type="time"
                      className="habitia-input"
                      style={{ ...styles.input, width: 130 }}
                      value={editingEmployee?.horarioInicio || ""}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, horarioInicio: e.target.value })}
                    />
                    <span style={{ color: colors.gray600 }}>a</span>
                    <input
                      id="emp-edit-horarioFin"
                      aria-label="Horario hasta"
                      type="time"
                      className="habitia-input"
                      style={{ ...styles.input, width: 130 }}
                      value={editingEmployee?.horarioFin || ""}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, horarioFin: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </AdminModal>
          </div>
        )}

        {tab === "amenities" && (
          <div style={styles.card} data-tutorial="editar-tab-amenities">
            <AmenityConfigTab buildingData={buildingData} />
          </div>
        )}

        {tab === "saldosproveedores" && (
          <div style={styles.card} data-tutorial="editar-tab-saldosproveedores">
            <SaldosProveedoresTab buildingData={buildingData} />
          </div>
        )}

        {tab === "finanzas" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-tutorial="editar-tab-finanzas">

            {/* Título de la sección */}
            <h3 style={{ fontSize: 18, fontWeight: 400, color: colors.primary, margin: 0 }}>Configuración Financiera</h3>

            {/* Aviso cambios sin guardar */}
            {isFinanzasDirty && (
              <div style={{
                background: colors.warningLight,
                border: `1px solid ${colors.warning}`,
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 13,
                color: colors.warningDark,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <FaExclamationTriangle size={12} />
                Tenés cambios sin guardar.
              </div>
            )}

            {/* Card 1: Distribución de Gastos */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Distribución de Gastos</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Método de distribución</label>
                  <select className="habitia-input" style={styles.input} value={finanzasData.distributionMethod}
                    onChange={e => setFinanzasData({ ...finanzasData, distributionMethod: e.target.value })}>
                    <option value="partes_iguales">Partes iguales (1/N)</option>
                    <option value="superficie">Por superficie (m²)</option>
                    <option value="alicuotas">Por alícuotas (milésimas)</option>
                  </select>
                </div>
              </div>
              {finanzasData.distributionMethod === "alicuotas" && (
                <div style={{
                  background: colors.infoLight,
                  border: `1px solid ${colors.info}`,
                  borderRadius: 8,
                  padding: 12,
                  marginTop: 12,
                  fontSize: 12,
                  color: colors.infoDark,
                }}>
                  Las alícuotas se configuran desde la tab Unidades con el botón "Calcular Alícuotas".
                </div>
              )}
            </div>

            {/* Card 2: Mora y Vencimientos */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Mora y Vencimientos</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tipo de mora</label>
                  <select className="habitia-input" style={styles.input} value={finanzasData.interestMethod}
                    onChange={e => setFinanzasData({ ...finanzasData, interestMethod: e.target.value })}>
                    <option value="porcentaje">Porcentaje anual</option>
                    <option value="monto_fijo">Monto fijo</option>
                  </select>
                </div>
                {finanzasData.interestMethod === "monto_fijo" ? (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Monto fijo de mora</label>
                    <input className="habitia-input" style={styles.input} type="number" step="1" min="0"
                      value={finanzasData.interestFixedAmount}
                      onChange={e => setFinanzasData({ ...finanzasData, interestFixedAmount: e.target.value })}
                      placeholder="Tope legal: 12% anual equivalente" />
                  </div>
                ) : (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Tasa de interés por mora (% anual)</label>
                    <input className="habitia-input" style={styles.input} type="number" step="0.1" max="12" min="0"
                      value={finanzasData.interestRate}
                      onChange={e => setFinanzasData({ ...finanzasData, interestRate: e.target.value })}
                      placeholder="Máx. 12% (Ley 19.604)" />
                  </div>
                )}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Gracia (sin mora ni multa)</label>
                  <select className="habitia-input" style={styles.input}
                    value={finanzasData.gracePeriodMode || "dias"}
                    onChange={e => setFinanzasData({ ...finanzasData, gracePeriodMode: e.target.value })}>
                    <option value="dias">Días corridos desde el vencimiento</option>
                    <option value="fin_de_mes">Hasta fin del mes del vencimiento</option>
                    <option value="dia_fijo">Hasta un día fijo del mes</option>
                  </select>
                  {(finanzasData.gracePeriodMode || "dias") === "dias" && (
                    <input className="habitia-input" style={{ ...styles.input, marginTop: 8 }} type="number" min="0" max="90"
                      value={finanzasData.gracePeriodDays}
                      onChange={e => setFinanzasData({ ...finanzasData, gracePeriodDays: e.target.value })}
                      placeholder="0 = sin gracia" />
                  )}
                  {finanzasData.gracePeriodMode === "dia_fijo" && (
                    <input className="habitia-input" style={{ ...styles.input, marginTop: 8 }} type="number" min="1" max="31"
                      value={finanzasData.graceUntilDay}
                      onChange={e => setFinanzasData({ ...finanzasData, graceUntilDay: e.target.value })}
                      placeholder="Día del mes hasta el que hay gracia (ej. 30)" />
                  )}
                  {finanzasData.gracePeriodMode === "fin_de_mes" && (
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: colors.gray600 }}>
                      Si vence el 15, se paga sin mora hasta el último día del mes.
                    </p>
                  )}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Día de vencimiento</label>
                  <input className="habitia-input" style={styles.input} type="number" min="1" max="28"
                    value={finanzasData.paymentDueDay}
                    onChange={e => setFinanzasData({ ...finanzasData, paymentDueDay: e.target.value })}
                    placeholder="Día del mes (1-28)" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Día de corte del cierre</label>
                  <input className="habitia-input" style={styles.input} type="number" min="1" max="28"
                    value={finanzasData.diaCorte}
                    onChange={e => setFinanzasData({ ...finanzasData, diaCorte: e.target.value })}
                    placeholder="Día del mes (1-28) — vacío = mes calendario" />
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: colors.gray600 }}>
                    Define la ventana del cierre: ej. día 5 → el cierre de septiembre toma del 05/09 al 05/10.
                    Vacío = mes calendario. Es independiente del día de vencimiento.
                  </p>
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                  <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!finanzasData.moraIndexationEnabled}
                      onChange={e => setFinanzasData({
                        ...finanzasData,
                        moraIndexationEnabled: e.target.checked,
                        moraStartDate: e.target.checked && !finanzasData.moraStartDate
                          ? new Date().toLocaleDateString("en-CA")
                          : finanzasData.moraStartDate,
                      })}
                    />
                    Aplicar indexación por UI (Decreto-Ley 14.500)
                  </label>
                  <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, margin: "4px 0 0", lineHeight: 1.5 }}>
                    La deuda vencida se reajusta por la variación de la Unidad Indexada y el 12% corre sobre el capital
                    reajustado. La mora se computa desde la fecha de activación (no retroactivo).
                  </p>
                  {finanzasData.moraIndexationEnabled && (
                    <div style={{ marginTop: 6 }}>
                      <label style={styles.label}>Mora a partir de</label>
                      <input
                        className="habitia-input"
                        style={styles.input}
                        type="date"
                        value={finanzasData.moraStartDate || ""}
                        onChange={e => setFinanzasData({ ...finanzasData, moraStartDate: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Fondo de Reserva */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Fondo de Reserva</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Método</label>
                  <select className="habitia-input" style={styles.input} value={finanzasData.reserveFundMethod}
                    onChange={e => setFinanzasData({ ...finanzasData, reserveFundMethod: e.target.value })}>
                    <option value="">Sin fondo de reserva</option>
                    <option value="percentage">Porcentaje del gasto común</option>
                    <option value="fixed">Monto fijo</option>
                  </select>
                </div>
                {finanzasData.reserveFundMethod === "percentage" && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Porcentaje (%)</label>
                    <input className="habitia-input" style={styles.input} type="number" step="0.1"
                      value={finanzasData.reserveFundPercentage}
                      onChange={e => setFinanzasData({ ...finanzasData, reserveFundPercentage: e.target.value })} />
                  </div>
                )}
                {finanzasData.reserveFundMethod === "fixed" && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Monto fijo ($)</label>
                    <input className="habitia-input" style={styles.input} type="number"
                      value={finanzasData.reserveFundFixedAmount}
                      onChange={e => setFinanzasData({ ...finanzasData, reserveFundFixedAmount: e.target.value })} />
                  </div>
                )}
              </div>
              {finanzasData.reserveFundMethod && (
                <div style={{
                  background: colors.infoLight,
                  border: `1px solid ${colors.info}`,
                  borderRadius: radius.sm,
                  padding: spacing[3],
                  marginTop: spacing[3],
                  fontSize: typography.sizes.sm,
                  color: colors.infoDark,
                }}>
                  Este es el monto total a recaudar para el fondo de reserva. Se dividirá entre las unidades según el criterio de distribución configurado, no es la cuota por unidad.
                </div>
              )}
            </div>

            {/* Card 4: Fondos Personalizados */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Fondos Personalizados</h3>
              {customFunds.length === 0 ? (
                <p style={{ fontSize: 13, color: colors.gray600, margin: "0 0 12px 0" }}>
                  No hay fondos personalizados. Podés crear fondos como Calefacción, Obra, etc.
                </p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
                  <thead>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={styles.thFinanzas}>Nombre</th>
                      <th style={styles.thFinanzas}>Método</th>
                      <th style={styles.thFinanzas}>Valor</th>
                      <th style={styles.thFinanzas}>Saldo Inicial</th>
                      <th style={styles.thFinanzas}>Cuenta Asociada</th>
                      <th style={styles.thFinanzas}>Estado</th>
                      <th style={styles.thFinanzas}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customFunds.map((fund, idx) => {
                      // Resolver cuenta bancaria asociada al fondo (por id)
                      const linkedAccount = fund.bankAccountId
                        ? bankAccountsList.find(a => a.id === fund.bankAccountId)
                        : null;
                      return (
                      <tr key={fund.id} className="habitia-table-row">
                        <td style={styles.tdFinanzas}>
                          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 9999, background: fund.color || CUSTOM_FUND_COLORS[idx % CUSTOM_FUND_COLORS.length], marginRight: 8 }} />
                          {fund.name}
                        </td>
                        <td style={styles.tdFinanzas}>{fund.method === "percentage" ? "Porcentaje" : "Monto fijo"}</td>
                        <td style={styles.tdFinanzas}>{fund.method === "percentage" ? `${fund.percentage}%` : `${(fund.currency || "UYU") === "USD" ? "US$" : "$"}${Number(fund.fixedAmount || 0).toLocaleString("es-UY")}`}</td>
                        <td style={styles.tdFinanzas}>{(fund.currency || "UYU") === "USD" ? `US$${Number(fund.initialBalanceUSD || 0).toLocaleString("es-UY")}` : `$${Number(fund.initialBalance || 0).toLocaleString("es-UY")}`}</td>
                        <td style={styles.tdFinanzas}>
                          {linkedAccount
                            ? formatAccountLabel(linkedAccount)
                            : <span style={{ color: colors.gray400 }}>— Sin asociar —</span>}
                        </td>
                        <td style={styles.tdFinanzas}>
                          <span style={{ fontSize: 12, color: fund.active !== false ? colors.success : colors.gray400 }}>
                            {fund.active !== false ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td style={styles.tdFinanzas}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" aria-label="Editar" className="habitia-btn-ghost"
                              style={styles.iconBtn}
                              onClick={() => {
                                setEditingFund(fund);
                                // Inferir moneda desde fund.currency o desde qué saldo viene poblado (retrocompat)
                                const inferredCurrency = fund.currency || (Number(fund.initialBalanceUSD) > 0 && !Number(fund.initialBalance) ? "USD" : "UYU");
                                const inferredBalance = inferredCurrency === "USD"
                                  ? (fund.initialBalanceUSD ?? fund.initialBalance ?? "")
                                  : (fund.initialBalance ?? "");
                                setFundForm({
                                  name: fund.name, method: fund.method || "percentage",
                                  percentage: fund.percentage ?? "", fixedAmount: fund.fixedAmount ?? "",
                                  currency: inferredCurrency, initialBalance: inferredBalance,
                                  bankAccountId: fund.bankAccountId ?? "",
                                });
                                setFundErrors({});
                                setShowFundModal(true);
                              }}>
                              <FaPen size={12} />
                            </button>
                            <button type="button"
                              aria-label={fund.active !== false ? "Desactivar" : "Activar"}
                              className="habitia-btn-ghost"
                              style={fund.active !== false ? styles.iconBtnDanger : styles.iconBtnSuccess}
                              onClick={() => {
                                setCustomFunds(customFunds.map(f => f.id === fund.id ? { ...f, active: f.active === false ? true : false } : f));
                              }}>
                              {fund.active !== false ? <FaToggleOn size={14} /> : <FaToggleOff size={14} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <button type="button" style={{ ...styles.addBtn, fontSize: 13, padding: "8px 16px" }}
                onClick={() => {
                  setEditingFund(null);
                  setFundForm({ name: "", method: "percentage", percentage: "", fixedAmount: "", currency: "UYU", initialBalance: "", bankAccountId: "" });
                  setFundErrors({});
                  setShowFundModal(true);
                }}>
                + Agregar Fondo
              </button>
            </div>

            {/* Modal de fondo personalizado */}
            <AdminModal
              open={showFundModal}
              onClose={() => { setShowFundModal(false); setFundErrors({}); }}
              title={editingFund ? "Editar Fondo" : "Nuevo Fondo Personalizado"}
              size="narrow"
              hideClose
              footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={styles.secondaryBtn} onClick={() => { setShowFundModal(false); setFundErrors({}); }}>Cancelar</button>
                  <button type="button" style={styles.addBtn} onClick={() => {
                    // Validaciones de campos obligatorios — marcar errores inline estilo "Requerido"
                    const fe = {};
                    if (!fundForm.name.trim()) fe.name = "Requerido";
                    if (fundForm.method === "percentage") {
                      if (fundForm.percentage === "" || fundForm.percentage === null || fundForm.percentage === undefined) {
                        fe.percentage = "Requerido";
                      } else {
                        const pct = Number(fundForm.percentage);
                        if (Number.isNaN(pct) || pct <= 0 || pct > 100) fe.percentage = "Debe ser mayor a 0 y hasta 100";
                      }
                    }
                    if (fundForm.method === "fixed") {
                      if (fundForm.fixedAmount === "" || fundForm.fixedAmount === null || fundForm.fixedAmount === undefined) {
                        fe.fixedAmount = "Requerido";
                      } else {
                        const amt = Number(fundForm.fixedAmount);
                        if (Number.isNaN(amt) || amt <= 0) fe.fixedAmount = "Debe ser mayor a 0";
                      }
                    }
                    if (!fundForm.currency) fe.currency = "Requerido";
                    if (fundForm.initialBalance === "" || fundForm.initialBalance === null || fundForm.initialBalance === undefined) {
                      fe.initialBalance = "Requerido";
                    } else {
                      const saldo = Number(fundForm.initialBalance);
                      if (Number.isNaN(saldo) || saldo < 0) fe.initialBalance = "No puede ser negativo";
                    }
                    if (Object.keys(fe).length > 0) { setFundErrors(fe); return; }

                    // Validaciones de unicidad/reserva del slug
                    const slug = slugifyFundName(fundForm.name);
                    if (!slug) { setFundErrors({ name: "Nombre inválido" }); return; }
                    if (RESERVED_FUND_IDS.includes(slug)) { setFundErrors({ name: `"${fundForm.name}" está reservado. Elegí otro.` }); return; }

                    const saldo = Number(fundForm.initialBalance);
                    // Mapear saldo al campo correspondiente según moneda (retrocompat con estructura previa)
                    const balanceUYU = fundForm.currency === "UYU" ? saldo : 0;
                    const balanceUSD = fundForm.currency === "USD" ? saldo : 0;
                    if (editingFund) {
                      setCustomFunds(customFunds.map(f => f.id === editingFund.id ? {
                        ...f, name: fundForm.name.trim(), method: fundForm.method,
                        percentage: Number(fundForm.percentage) || 0, fixedAmount: Number(fundForm.fixedAmount) || 0,
                        currency: fundForm.currency,
                        initialBalance: balanceUYU, initialBalanceUSD: balanceUSD,
                        bankAccountId: fundForm.bankAccountId || "",
                      } : f));
                    } else {
                      if (customFunds.some(f => f.id === slug)) { setFundErrors({ name: `Ya existe un fondo con ID "${slug}"` }); return; }
                      setCustomFunds([...customFunds, {
                        id: slug, name: fundForm.name.trim(), method: fundForm.method,
                        percentage: Number(fundForm.percentage) || 0, fixedAmount: Number(fundForm.fixedAmount) || 0,
                        currency: fundForm.currency,
                        initialBalance: balanceUYU, initialBalanceUSD: balanceUSD,
                        bankAccountId: fundForm.bankAccountId || "",
                        color: CUSTOM_FUND_COLORS[customFunds.length % CUSTOM_FUND_COLORS.length],
                        active: true, createdAt: new Date(),
                      }]);
                    }
                    setFundErrors({});
                    setShowFundModal(false);
                  }}>
                    {editingFund ? "Guardar" : "Crear Fondo"}
                  </button>
                </div>
              }
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <FormField label="Nombre del fondo" htmlFor="fund-name" required error={fundErrors.name}>
                  <input id="fund-name" type="text" className="habitia-input"
                    style={{ ...styles.input, ...(fundErrors.name ? { borderColor: colors.error } : {}) }}
                    placeholder="Ej: Calefacción, Obra Fachada"
                    value={fundForm.name}
                    onChange={e => { setFundForm({ ...fundForm, name: e.target.value }); setFundErrors(prev => ({ ...prev, name: undefined })); }} />
                </FormField>
                <FormField label="Método de cobro" htmlFor="fund-method" required>
                  <select id="fund-method" className="habitia-input" style={styles.input}
                    value={fundForm.method}
                    onChange={e => { setFundForm({ ...fundForm, method: e.target.value }); setFundErrors(prev => ({ ...prev, percentage: undefined, fixedAmount: undefined })); }}>
                    <option value="percentage">Porcentaje del gasto común</option>
                    <option value="fixed">Monto fijo mensual</option>
                  </select>
                </FormField>
                {fundForm.method === "percentage" && (
                  <FormField label="Porcentaje (%)" htmlFor="fund-pct" required error={fundErrors.percentage}>
                    <input id="fund-pct" type="number" min="0" max="100" step="0.1" className="habitia-input"
                      style={{ ...styles.input, ...(fundErrors.percentage ? { borderColor: colors.error } : {}) }}
                      value={fundForm.percentage}
                      onChange={e => { setFundForm({ ...fundForm, percentage: e.target.value }); setFundErrors(prev => ({ ...prev, percentage: undefined })); }} />
                  </FormField>
                )}
                {fundForm.method === "fixed" && (
                  <FormField label="Monto fijo mensual ($)" htmlFor="fund-fixed" required error={fundErrors.fixedAmount}>
                    <input id="fund-fixed" type="number" min="0" className="habitia-input"
                      style={{ ...styles.input, ...(fundErrors.fixedAmount ? { borderColor: colors.error } : {}) }}
                      value={fundForm.fixedAmount}
                      onChange={e => { setFundForm({ ...fundForm, fixedAmount: e.target.value }); setFundErrors(prev => ({ ...prev, fixedAmount: undefined })); }} />
                  </FormField>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FormField label="Moneda" htmlFor="fund-currency" required error={fundErrors.currency}>
                    <select id="fund-currency" className="habitia-input"
                      style={{ ...styles.input, ...(fundErrors.currency ? { borderColor: colors.error } : {}) }}
                      value={fundForm.currency}
                      onChange={e => { setFundForm({ ...fundForm, currency: e.target.value }); setFundErrors(prev => ({ ...prev, currency: undefined })); }}>
                      <option value="UYU">UYU ($)</option>
                      <option value="USD">USD (US$)</option>
                    </select>
                  </FormField>
                  <FormField label="Saldo inicial" htmlFor="fund-init" required error={fundErrors.initialBalance}>
                    <input id="fund-init" type="number" min="0" className="habitia-input"
                      style={{ ...styles.input, ...(fundErrors.initialBalance ? { borderColor: colors.error } : {}) }}
                      placeholder={fundForm.currency === "USD" ? "US$" : "$"}
                      value={fundForm.initialBalance}
                      onChange={e => { setFundForm({ ...fundForm, initialBalance: e.target.value }); setFundErrors(prev => ({ ...prev, initialBalance: undefined })); }} />
                  </FormField>
                </div>
                <FormField label="Cuenta bancaria asociada" htmlFor="fund-account"
                  hint={bankAccountsList.length === 0 ? "No hay cuentas cargadas todavía. Podés crearlas desde la sección Cuentas Bancarias." : undefined}>
                  <select id="fund-account" className="habitia-input" style={styles.input}
                    value={fundForm.bankAccountId || ""}
                    onChange={e => setFundForm({ ...fundForm, bankAccountId: e.target.value })}
                    disabled={bankAccountsList.length === 0}>
                    <option value="">— Sin asociar —</option>
                    {bankAccountsList.map(a => (
                      <option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>
                    ))}
                  </select>
                </FormField>
              </div>
            </AdminModal>

            {/* Card 5: Multas Automáticas */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Multas Automáticas</h3>

              {/* Card informativa valores BCU */}
              {!bcuLoading && bcuRate && (bcuRate.ui || bcuRate.ur) && (
                <div style={{ background: colors.primaryLighter, borderRadius: radius.sm, padding: spacing[4], marginBottom: spacing[4] }}>
                  <div style={{ fontSize: typography.sizes.base, color: colors.gray900, marginBottom: spacing[2] }}>Valores de referencia BCU — {bcuRate.date}</div>
                  <div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
                    {bcuRate.ui && (
                      <div>
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>UI: </span>
                        <span style={{ fontSize: typography.sizes.md, color: colors.black }}>$ {bcuRate.ui.toFixed(4)}</span>
                      </div>
                    )}
                    {bcuRate.ur && (
                      <div>
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>UR: </span>
                        <span style={{ fontSize: typography.sizes.md, color: colors.black }}>$ {bcuRate.ur.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <input type="checkbox" checked={finanzasData.autoFineEnabled}
                      onChange={e => setFinanzasData({ ...finanzasData, autoFineEnabled: e.target.checked })}
                      style={{ marginRight: 8 }} />
                    Habilitar multas automáticas
                  </label>
                </div>
                {finanzasData.autoFineEnabled && (
                  <>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Días de atraso para multa</label>
                      <input className="habitia-input" style={styles.input} type="number"
                        value={finanzasData.autoFineDaysThreshold}
                        onChange={e => setFinanzasData({ ...finanzasData, autoFineDaysThreshold: e.target.value })} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Moneda de la multa</label>
                      <select className="habitia-input" style={styles.input}
                        value={finanzasData.autoFineCurrency}
                        onChange={e => setFinanzasData({ ...finanzasData, autoFineCurrency: e.target.value })}>
                        <option value="UYU">UYU (Pesos)</option>
                        <option value="USD">USD (Dólares)</option>
                        <option value="UI">UI (Unidad Indexada)</option>
                        <option value="UR">UR (Unidad Reajustable)</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Monto multa ({finanzasData.autoFineCurrency || "$"})</label>
                      <input className="habitia-input" style={styles.input} type="number" step="0.01"
                        value={finanzasData.autoFineAmount}
                        onChange={e => setFinanzasData({ ...finanzasData, autoFineAmount: e.target.value })} />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Card 6: Cuentas Bancarias y Saldos */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Cuentas Bancarias y Saldos</h3>

              {/* Listado de cuentas bancarias */}
              <h4 style={{ fontSize: 14, fontWeight: 400, color: colors.black, margin: "0 0 12px 0" }}>Cuentas bancarias</h4>
              {bankAccountsList.length === 0 ? (
                <p style={{ fontSize: 13, color: colors.gray600, margin: "0 0 12px 0" }}>
                  No hay cuentas bancarias cargadas. Agregá al menos una para poder asociarla a los fondos.
                </p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
                  <thead>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={styles.thFinanzas}>Banco</th>
                      <th style={styles.thFinanzas}>Tipo de cuenta</th>
                      <th style={styles.thFinanzas}>Número</th>
                      <th style={styles.thFinanzas}>Moneda</th>
                      <th style={styles.thFinanzas}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankAccountsList.map((acc) => (
                      <tr key={acc.id} className="habitia-table-row">
                        <td style={styles.tdFinanzas}>{acc.bank}</td>
                        <td style={styles.tdFinanzas}>{acc.accountType || "-"}</td>
                        <td style={styles.tdFinanzas}>{acc.accountNumber || "-"}</td>
                        <td style={styles.tdFinanzas}>{acc.currency || "UYU"}</td>
                        <td style={styles.tdFinanzas}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" aria-label="Editar" className="habitia-btn-ghost"
                              style={styles.iconBtn}
                              onClick={() => {
                                setEditingAccount(acc);
                                setAccountForm({
                                  bank: acc.bank || "",
                                  accountType: acc.accountType || "",
                                  accountNumber: acc.accountNumber || "",
                                  currency: acc.currency || "UYU",
                                });
                                setAccountErrors({});
                                setShowAccountModal(true);
                              }}>
                              <FaPen size={12} />
                            </button>
                            <button type="button" aria-label="Eliminar" className="habitia-btn-danger"
                              style={styles.iconBtnDanger}
                              onClick={() => {
                                // Al eliminar, desasociar esta cuenta de cualquier fondo que la referencie
                                const newAccounts = bankAccountsList.filter(a => a.id !== acc.id);
                                setFinanzasData({
                                  ...finanzasData,
                                  bankAccounts: newAccounts,
                                  cajaBankAccountId: finanzasData.cajaBankAccountId === acc.id ? "" : finanzasData.cajaBankAccountId,
                                  reserveFundBankAccountId: finanzasData.reserveFundBankAccountId === acc.id ? "" : finanzasData.reserveFundBankAccountId,
                                  cajaBankAccountIdUSD: finanzasData.cajaBankAccountIdUSD === acc.id ? "" : finanzasData.cajaBankAccountIdUSD,
                                  reserveFundBankAccountIdUSD: finanzasData.reserveFundBankAccountIdUSD === acc.id ? "" : finanzasData.reserveFundBankAccountIdUSD,
                                });
                                setCustomFunds(customFunds.map(f => f.bankAccountId === acc.id ? { ...f, bankAccountId: "" } : f));
                              }}>
                              <FaTrash size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button type="button" style={{ ...styles.addBtn, fontSize: 13, padding: "8px 16px", marginBottom: 20 }}
                onClick={() => {
                  setEditingAccount(null);
                  setAccountForm({ bank: "", accountType: "", accountNumber: "", currency: "UYU" });
                  setAccountErrors({});
                  setShowAccountModal(true);
                }}>
                + Agregar Cuenta
              </button>

              {/* Saldos iniciales + asociación por fondo */}
              <h4 style={{ fontSize: 14, fontWeight: 400, color: colors.black, margin: "0 0 12px 0" }}>Saldos Iniciales (UYU)</h4>
              <div style={{ ...styles.formGrid, marginBottom: 20 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Saldo Caja ($)</label>
                  <input className="habitia-input" style={styles.input} type="number"
                    value={finanzasData.initialBankBalance}
                    onChange={e => setFinanzasData({ ...finanzasData, initialBankBalance: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cuenta asociada a Caja</label>
                  <select className="habitia-input" style={styles.input}
                    value={finanzasData.cajaBankAccountId || ""}
                    onChange={e => setFinanzasData({ ...finanzasData, cajaBankAccountId: e.target.value })}>
                    <option value="">— Sin asociar —</option>
                    {uyuAccounts.map(a => (
                      <option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Saldo Fondo Reserva ($)</label>
                  <input className="habitia-input" style={styles.input} type="number"
                    value={finanzasData.initialReserveFund}
                    onChange={e => setFinanzasData({ ...finanzasData, initialReserveFund: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cuenta asociada a Fondo Reserva</label>
                  <select className="habitia-input" style={styles.input}
                    value={finanzasData.reserveFundBankAccountId || ""}
                    onChange={e => setFinanzasData({ ...finanzasData, reserveFundBankAccountId: e.target.value })}>
                    <option value="">— Sin asociar —</option>
                    {uyuAccounts.map(a => (
                      <option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>
                    ))}
                  </select>
                </div>
                {finanzasData.cajaBankAccountId &&
                  finanzasData.cajaBankAccountId === finanzasData.reserveFundBankAccountId && (
                  <div style={{ gridColumn: "1 / -1", background: colors.infoLight, border: `1px solid ${colors.info}40`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: colors.infoDark, lineHeight: 1.5 }}>
                    Caja y Fondo de Reserva comparten la misma cuenta bancaria. Está permitido:
                    la segregación entre fondos se mantiene contable y el Estado de Cuenta mostrará
                    el <strong>saldo bancario consolidado</strong> de esa cuenta.
                  </div>
                )}
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 400, color: colors.black, margin: "0 0 12px 0" }}>Saldos Iniciales (USD)</h4>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Saldo Caja (US$)</label>
                  <input className="habitia-input" style={styles.input} type="number"
                    value={finanzasData.initialBankBalanceUSD}
                    onChange={e => setFinanzasData({ ...finanzasData, initialBankBalanceUSD: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cuenta asociada a Caja</label>
                  <select className="habitia-input" style={styles.input}
                    value={finanzasData.cajaBankAccountIdUSD || ""}
                    onChange={e => setFinanzasData({ ...finanzasData, cajaBankAccountIdUSD: e.target.value })}>
                    <option value="">— Sin asociar —</option>
                    {usdAccounts.map(a => (
                      <option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Saldo Fondo Reserva (US$)</label>
                  <input className="habitia-input" style={styles.input} type="number"
                    value={finanzasData.initialReserveFundUSD}
                    onChange={e => setFinanzasData({ ...finanzasData, initialReserveFundUSD: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cuenta asociada a Fondo Reserva</label>
                  <select className="habitia-input" style={styles.input}
                    value={finanzasData.reserveFundBankAccountIdUSD || ""}
                    onChange={e => setFinanzasData({ ...finanzasData, reserveFundBankAccountIdUSD: e.target.value })}>
                    <option value="">— Sin asociar —</option>
                    {usdAccounts.map(a => (
                      <option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>
                    ))}
                  </select>
                </div>
              </div>
              {usdAccounts.length === 0 && (
                <p style={{ fontSize: typography.sizes.xs, color: colors.gray600, margin: "8px 0 0", lineHeight: 1.5 }}>
                  No hay cuentas en USD. Creá una cuenta con moneda USD desde "+ Agregar Cuenta" para poder asociarla.
                </p>
              )}
            </div>

            {/* Tipo de Cambio y Política USD */}
            <div style={{ ...cardBase, padding: spacing[6], marginTop: spacing[6] }}>
              <h4 style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, margin: `0 0 ${spacing[5]}px 0` }}>Tipo de Cambio y Conversión</h4>

              {/* Cotizaciones BCU */}
              <div style={{ background: colors.primaryLighter, borderRadius: radius.md, padding: spacing[5], marginBottom: spacing[6] }}>
                <div style={{ display: "flex", alignItems: "center", gap: spacing[3], marginBottom: spacing[4] }}>
                  <div style={{ width: 40, height: 40, borderRadius: radius.sm, background: colors.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FaMoneyBillWave size={18} color={colors.primary} />
                  </div>
                  <div>
                    <div style={{ fontSize: typography.sizes.md, color: colors.black }}>Cotizaciones BCU</div>
                    <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Actualizadas automáticamente todos los días a las 07:00 (UY)</div>
                  </div>
                </div>
                {bcuLoading ? (
                  <div style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>Cargando cotizaciones...</div>
                ) : bcuRate ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: spacing[5] }}>
                      {(bcuRate.sell || bcuRate.buy) && (
                        <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                          <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Dólar (USD)</div>
                          <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {(bcuRate.sell || bcuRate.buy)?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      )}
                      {bcuRate.ui && (
                        <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                          <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Unidad Indexada (UI)</div>
                          <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {bcuRate.ui.toFixed(4)}</div>
                        </div>
                      )}
                      {bcuRate.ur && (
                        <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                          <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Unidad Reajustable (UR)</div>
                          <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {bcuRate.ur?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: spacing[3] }}>
                      Fecha: {bcuRate.date}{bcuRate.carriedFrom ? ` (último día hábil: ${bcuRate.carriedFrom})` : ""} — Fuente: Banco Central del Uruguay
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: typography.sizes.base, color: colors.warning }}>Sin datos del BCU disponibles. Configurá un tipo de cambio manual abajo.</div>
                )}
              </div>

              {/* Configuración */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[5], marginBottom: spacing[4] }}>
                <FormField label="Tipo de cambio manual (opcional)" htmlFor="tc-manual">
                  <input id="tc-manual" className="habitia-input" style={styles.input} type="number" step="0.01"
                    value={finanzasData.exchangeRateUSD}
                    onChange={e => setFinanzasData({ ...finanzasData, exchangeRateUSD: e.target.value })}
                    placeholder="Dejar vacío para usar el TC automático del BCU" />
                  <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: spacing[1] }}>
                    Si se completa, este valor se usa en lugar del tipo de cambio automático del BCU
                  </div>
                </FormField>
                <FormField label="Al pagar gastos en dólares" htmlFor="usd-policy">
                  <select id="usd-policy" className="habitia-input" style={styles.input}
                    value={finanzasData.usdExpensePolicy}
                    onChange={e => setFinanzasData({ ...finanzasData, usdExpensePolicy: e.target.value })}>
                    <option value="convert_to_uyu">Convertir a pesos al TC del día anterior</option>
                    {(finanzasData.bankAccounts || []).some(a => a.currency === "USD") && (
                      <option value="keep_usd">Mantener en dólares (descontar de cuenta USD)</option>
                    )}
                  </select>
                  <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: spacing[1] }}>
                    {finanzasData.usdExpensePolicy === "convert_to_uyu"
                      ? "Al pagar un gasto en USD se convierte automáticamente a pesos usando el TC del BCU"
                      : "Los gastos en USD se mantienen en dólares y se descuentan de la cuenta USD del edificio"}
                  </div>
                </FormField>
              </div>
            </div>

            {/* Modal cuenta bancaria */}
            <AdminModal
              open={showAccountModal}
              onClose={() => { setShowAccountModal(false); setAccountErrors({}); }}
              title={editingAccount ? "Editar Cuenta Bancaria" : "Nueva Cuenta Bancaria"}
              size="narrow"
              hideClose
              footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={styles.secondaryBtn} onClick={() => { setShowAccountModal(false); setAccountErrors({}); }}>Cancelar</button>
                  <button type="button" style={styles.addBtn} onClick={() => {
                    const ae = {};
                    if (!accountForm.bank.trim()) ae.bank = "Requerido";
                    if (!accountForm.accountType) ae.accountType = "Requerido";
                    if (!accountForm.accountNumber.trim()) ae.accountNumber = "Requerido";
                    if (!accountForm.currency) ae.currency = "Requerido";
                    if (Object.keys(ae).length > 0) { setAccountErrors(ae); return; }

                    if (editingAccount) {
                      setFinanzasData({
                        ...finanzasData,
                        bankAccounts: bankAccountsList.map(a => a.id === editingAccount.id ? {
                          ...a,
                          bank: accountForm.bank.trim(),
                          accountType: accountForm.accountType,
                          accountNumber: accountForm.accountNumber.trim(),
                          currency: accountForm.currency,
                        } : a),
                      });
                    } else {
                      const newAcc = {
                        id: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        bank: accountForm.bank.trim(),
                        accountType: accountForm.accountType,
                        accountNumber: accountForm.accountNumber.trim(),
                        currency: accountForm.currency,
                      };
                      setFinanzasData({
                        ...finanzasData,
                        bankAccounts: [...bankAccountsList, newAcc],
                      });
                    }
                    setAccountErrors({});
                    setShowAccountModal(false);
                  }}>
                    {editingAccount ? "Guardar" : "Crear Cuenta"}
                  </button>
                </div>
              }
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <FormField label="Banco" htmlFor="acc-bank" required error={accountErrors.bank}>
                  <select id="acc-bank" className="habitia-input"
                    style={{ ...styles.input, ...(accountErrors.bank ? { borderColor: colors.error } : {}) }}
                    value={accountForm.bank}
                    onChange={e => { setAccountForm({ ...accountForm, bank: e.target.value }); setAccountErrors(prev => ({ ...prev, bank: undefined })); }}>
                    <option value="">Seleccionar...</option>
                    {BANCOS_URUGUAY.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Tipo de cuenta" htmlFor="acc-type" required error={accountErrors.accountType}>
                  <select id="acc-type" className="habitia-input"
                    style={{ ...styles.input, ...(accountErrors.accountType ? { borderColor: colors.error } : {}) }}
                    value={accountForm.accountType}
                    onChange={e => { setAccountForm({ ...accountForm, accountType: e.target.value }); setAccountErrors(prev => ({ ...prev, accountType: undefined })); }}>
                    <option value="">Seleccionar...</option>
                    <option value="Cuenta Corriente">Cuenta Corriente</option>
                    <option value="Caja de Ahorro">Caja de Ahorro</option>
                  </select>
                </FormField>
                <FormField label="Número de cuenta" htmlFor="acc-number" required error={accountErrors.accountNumber}>
                  <input id="acc-number" type="text" className="habitia-input"
                    style={{ ...styles.input, ...(accountErrors.accountNumber ? { borderColor: colors.error } : {}) }}
                    placeholder="Ej: 001-1234567-00"
                    value={accountForm.accountNumber}
                    onChange={e => { setAccountForm({ ...accountForm, accountNumber: e.target.value }); setAccountErrors(prev => ({ ...prev, accountNumber: undefined })); }} />
                </FormField>
                <FormField label="Moneda" htmlFor="acc-currency" required error={accountErrors.currency}>
                  <select id="acc-currency" className="habitia-input"
                    style={{ ...styles.input, ...(accountErrors.currency ? { borderColor: colors.error } : {}) }}
                    value={accountForm.currency}
                    onChange={e => { setAccountForm({ ...accountForm, currency: e.target.value }); setAccountErrors(prev => ({ ...prev, currency: undefined })); }}>
                    <option value="UYU">UYU ($)</option>
                    <option value="USD">USD (US$)</option>
                  </select>
                </FormField>
              </div>
            </AdminModal>

            {/* Criterios de División de Gastos */}
            <div style={{ ...cardBase, padding: spacing[5], marginTop: spacing[6] }}>
              <CriteriosPorEdificios embedded />
            </div>

            {/* Botón Guardar centrado al final */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <button
                onClick={handleSaveFinanzas}
                style={{
                  ...styles.primaryBtn,
                  ...(!isFinanzasDirty || savingFinanzas ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                }}
                disabled={!isFinanzasDirty || savingFinanzas}
              >
                <FaSave size={14} />
                {savingFinanzas ? "Guardando..." : "Guardar"}
              </button>
            </div>

          </div>
        )}

        {tab === "comision" && (
          <div style={styles.card} data-tutorial="editar-tab-comision">
            <h3 style={styles.cardTitle}>Comisión del Edificio</h3>
            <p style={{ fontSize: 13, color: colors.gray600, marginBottom: 16, marginTop: 0 }}>
              Asigná los integrantes de la comisión desde los usuarios dados de alta en el edificio.
              Ningún campo es obligatorio.
            </p>
            {buildingUsers.length === 0 ? (
              <div style={{
                padding: "16px 20px",
                borderRadius: 8,
                backgroundColor: colors.warningLight,
                border: `1px solid ${colors.warning}`,
                fontSize: 13,
                color: colors.warningDark,
                marginBottom: 16,
              }}>
                No hay usuarios residentes vinculados a este edificio. Agregá residentes primero para poder asignar la comisión.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { key: "presidente", label: "Presidente" },
                  { key: "secretario", label: "Secretario" },
                  { key: "tesorero", label: "Tesorero" },
                  { key: "primerVocal", label: "Primer Vocal" },
                  { key: "segundoVocal", label: "Segundo Vocal" },
                ].map(({ key, label }) => (
                  <div key={key} style={styles.formGroup}>
                    <label style={styles.label}>{label}</label>
                    <SearchableSelect
                      value={commissionData[key]}
                      onChange={(val) => setCommissionData({ ...commissionData, [key]: val })}
                      options={buildingUsers.map((u) => ({
                        id: u.id,
                        value: u.userid,
                        label: `${u.name || u.NombreCompleto || ""} (${u.email})`,
                      }))}
                      placeholder="Seleccionar usuario..."
                      emptyLabel="— Sin asignar —"
                    />
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={handleSaveCommission}
              style={{
                ...styles.primaryBtn,
                marginTop: 20,
                ...(!isCommissionDirty || savingCommission ? { opacity: 0.5, cursor: "not-allowed" } : {}),
              }}
              disabled={savingCommission || !isCommissionDirty}
            >
              <FaSave size={14} />
              {savingCommission ? "Guardando..." : "Guardar Comisión"}
            </button>
          </div>
        )}

        {tab === "comunicaciones" && (
          <div style={styles.card} data-tutorial="editar-tab-comunicaciones">
            <h2 style={{ ...styles.cardTitle, display: "flex", alignItems: "center", gap: 8 }}>
              <FaComments color={colors.primary} /> Comunicaciones automáticas
            </h2>
            <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, margin: "0 0 16px" }}>
              Habilitá los <strong style={{ fontWeight: 400, color: colors.gray900 }}>templates de WhatsApp</strong> que querés que Habitia use en este edificio.
              Los que están <strong style={{ fontWeight: 400, color: colors.gray900 }}>aprobados por Meta</strong> permiten envío fuera de la ventana de 24 horas;
              los que aún no, se envían vía <code>wa.me</code> (abre WhatsApp con el mensaje precargado).
              Igualmente podés pre-habilitar templates pendientes — cuando Meta apruebe, el envío automático arranca solo.
            </p>

            {/* Sprint 2 — listamos TODOS los templates de Firestore. El admin
                del edificio decide cuáles habilita acá. Si Firestore está vacío
                (admin todavía no sembró), caemos al catálogo estático para
                que el admin vea algo. */}
            {firestoreTemplates === null && (
              <div style={{ padding: 16, textAlign: "center", color: colors.gray600, fontSize: 13 }}>
                Cargando templates…
              </div>
            )}
            {firestoreTemplates && firestoreTemplates.length === 0 && (
              <div style={{
                padding: 16,
                background: colors.gray50,
                border: `1px dashed ${colors.gray300}`,
                borderRadius: 8,
                fontSize: 13,
                color: colors.gray700,
                textAlign: "center",
              }}>
                <div style={{ marginBottom: 6 }}>
                  <FaComments size={28} color={colors.gray400} />
                </div>
                <strong style={{ fontWeight: 400, color: colors.gray900 }}>
                  No hay templates cargados todavía.
                </strong>
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  El equipo de plataforma debe sembrar los templates desde Herramientas → Plantillas WhatsApp → "Sembrar del catálogo".
                </div>
              </div>
            )}
            {firestoreTemplates && firestoreTemplates.length > 0 && Object.entries(groupTemplatesByCategory(firestoreTemplates)).map(([category, templates]) => (
              <div key={category} style={{ marginBottom: 24 }}>
                <h3 style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.gray700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  marginBottom: 12,
                  paddingBottom: 6,
                  borderBottom: `1px solid ${colors.gray200}`,
                }}>
                  {CATEGORY_LABELS[category] || category}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {templates.map((tpl) => {
                    const enabled = isTemplateEnabledForBuilding(tpl, buildingData);
                    const isOverride = hasBuildingOverride(tpl, buildingData);
                    const status = META_STATUS_LABELS[tpl.metaStatus] || { label: tpl.metaStatus, color: "gray" };
                    const statusColor =
                      status.color === "success" ? colors.success :
                      status.color === "warning" ? colors.warning :
                      status.color === "info" ? colors.info :
                      status.color === "error" ? colors.error :
                      colors.gray600;
                    const statusBg =
                      status.color === "success" ? colors.successLight :
                      status.color === "warning" ? colors.warningLight :
                      status.color === "info" ? colors.infoLight :
                      status.color === "error" ? colors.errorLight :
                      colors.gray100;
                    const isSaving = savingTemplateKey === tpl.key;
                    return (
                      <div key={tpl.key} style={{
                        padding: 14,
                        background: enabled ? colors.primarySubtle : colors.white,
                        border: `1px solid ${enabled ? colors.primary : colors.gray200}`,
                        borderRadius: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 12,
                        alignItems: "start",
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                            <strong style={{ fontSize: 14, color: colors.gray900, fontWeight: 400 }}>
                              {tpl.label}
                            </strong>
                            <span style={{
                              padding: "2px 8px",
                              fontSize: 10,
                              borderRadius: 9999,
                              background: statusBg,
                              color: statusColor,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}>
                              {status.label}
                            </span>
                            {tpl.requiresHumanConfirmation && (
                              <span style={{
                                padding: "2px 8px",
                                fontSize: 10,
                                borderRadius: 9999,
                                background: colors.warningLight,
                                color: colors.warningDark,
                              }}>
                                Requiere confirmación humana
                              </span>
                            )}
                          </div>
                          <p style={{ margin: "0 0 8px", fontSize: 12, color: colors.gray700, lineHeight: 1.45 }}>
                            {tpl.description}
                          </p>
                          <details style={{ fontSize: 12, color: colors.gray700 }}>
                            <summary style={{ cursor: "pointer", color: colors.primary, userSelect: "none" }}>
                              Ver copy y variables
                            </summary>
                            <div style={{
                              marginTop: 8,
                              padding: 10,
                              background: colors.white,
                              border: `1px solid ${colors.gray200}`,
                              borderRadius: 6,
                              fontSize: 12,
                              whiteSpace: "pre-wrap",
                              color: colors.gray900,
                            }}>
                              {tpl.body}
                            </div>
                            {(() => {
                              // Soporta ambos formatos: variables (catálogo estático) y variableSchema (Firestore).
                              const vars = tpl.variables || tpl.variableSchema || [];
                              if (!vars.length) return null;
                              return (
                                <div style={{ marginTop: 8 }}>
                                  <strong style={{ fontWeight: 400, color: colors.gray900, fontSize: 11 }}>Variables:</strong>
                                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 11, color: colors.gray700 }}>
                                    {vars.map((v) => {
                                      const idx = v.idx ?? v.position;
                                      const name = v.name || "—";
                                      const src = v.source || v.description || "";
                                      return (
                                        <li key={idx}>
                                          <code>{`{{${idx}}}`}</code> — {name}
                                          {src && <span style={{ color: colors.gray500 }}> ({src})</span>}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })()}
                          </details>
                          {enabled && tpl.metaStatus !== "APPROVED" && (
                            <div style={{ marginTop: 8, padding: 8, background: colors.warningLight, color: colors.warningDark, borderRadius: 6, fontSize: 11 }}>
                              Template pre-habilitado. Mientras Meta aprueba ({status.label}), Cobranzas IA usa <code>wa.me</code>. Cuando Meta apruebe, el envío 100% automático arranca solo para este edificio.
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => toggleCommunicationTemplate(tpl.key || tpl.id, !enabled)}
                            disabled={isSaving}
                            title={
                              enabled
                                ? "Click para deshabilitar en este edificio"
                                : tpl.metaStatus === "APPROVED"
                                  ? "Click para habilitar (envío automático activo apenas guardes)"
                                  : "Click para pre-habilitar. Cuando Meta apruebe, el envío automático arranca solo. Mientras tanto se usa wa.me."
                            }
                            style={{
                              padding: "6px 14px",
                              fontSize: 12,
                              fontWeight: 400,
                              color: enabled ? colors.white : colors.gray700,
                              background: enabled ? colors.primary : colors.gray100,
                              border: enabled ? "none" : `1px solid ${colors.gray300}`,
                              borderRadius: 8,
                              cursor: isSaving ? "wait" : "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              minWidth: 110,
                              justifyContent: "center",
                            }}
                          >
                            {isSaving ? (
                              <>
                                <span style={{
                                  display: "inline-block", width: 10, height: 10,
                                  border: `2px solid ${enabled ? colors.white : colors.gray400}`,
                                  borderTopColor: "transparent",
                                  borderRadius: "50%",
                                  animation: "spin 0.8s linear infinite",
                                }} />
                                Guardando…
                              </>
                            ) : enabled ? (
                              <>
                                <FaToggleOn size={14} /> Habilitado
                              </>
                            ) : (
                              <>
                                <FaToggleOff size={14} /> Deshabilitado
                              </>
                            )}
                          </button>
                          {!isSaving && !isOverride && (
                            <span style={{ fontSize: 10, color: colors.gray400 }}>
                              por defecto de la plataforma
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={{
              marginTop: 24,
              padding: 12,
              background: colors.infoLight,
              borderRadius: 8,
              fontSize: 12,
              color: colors.infoDark,
            }}>
              <strong style={{ fontWeight: 400 }}>¿Cómo funciona la aprobación de Meta?</strong>
              <br />
              Los templates de WhatsApp Business requieren aprobación caso por caso (24-72h típico). El admin de plataforma los submite desde Meta Business Manager. Una vez aprobados, este edificio podrá enviarlos sin la ventana de 24h. Mientras tanto, Cobranzas IA usa el enlace <code>wa.me</code> que abre WhatsApp en el navegador con el mensaje precargado.
            </div>
          </div>
        )}
      </div>

      {/* Modal solicitud de eliminacion */}
      <AdminModal
        open={showDeleteReasonModal}
        onClose={() => { setShowDeleteReasonModal(false); setDeleteReason(""); }}
        title="Solicitar eliminación de edificio"
        subtitle={buildingData?.name || ""}
        icon={<FaExclamationTriangle style={{ color: colors.error }} />}
        size="narrow"
        footer={
          <>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: `1px solid ${colors.gray200}`,
                background: colors.white,
                color: colors.gray700,
                fontSize: 13,
                fontWeight: 400,
                cursor: "pointer",
              }}
              onClick={() => { setShowDeleteReasonModal(false); setDeleteReason(""); }}
            >
              Cancelar
            </button>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: !deleteReason.trim() || deletingBuilding ? colors.gray300 : colors.error,
                color: colors.white,
                fontSize: 13,
                fontWeight: 400,
                cursor: !deleteReason.trim() || deletingBuilding ? "not-allowed" : "pointer",
                opacity: !deleteReason.trim() || deletingBuilding ? 0.6 : 1,
              }}
              onClick={handleSubmitDeletionRequest}
              disabled={!deleteReason.trim() || deletingBuilding}
            >
              {deletingBuilding ? "Enviando..." : "Enviar solicitud"}
            </button>
          </>
        }
      >
        <p style={{ fontSize: 14, color: colors.gray600, marginBottom: 12, marginTop: 0 }}>
          Se enviará una solicitud al usuario Administrador para eliminar este edificio y todos sus datos asociados.
          Esta acción requiere aprobación.
        </p>
        <label style={{ fontSize: 13, color: colors.gray700, display: "block", marginBottom: 6 }}>
          Motivo de eliminación *
        </label>
        <textarea
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 8,
            border: `1px solid ${colors.gray200}`,
            fontSize: 13,
            fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
            resize: "vertical",
            minHeight: 80,
            boxSizing: "border-box",
          }}
          rows={4}
          placeholder="Explique el motivo por el cual se quiere eliminar este edificio..."
          value={deleteReason}
          onChange={(e) => setDeleteReason(e.target.value)}
        />
      </AdminModal>
    </div>
  );
};

const styles = {
  page: {
    ...pageContainer,
  },
  dropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 16px",
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 400,
    color: colors.gray700,
    textAlign: "left",
    transition: "background 0.15s",
  },
  stepperBtn: {
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    background: colors.white,
    fontSize: 18,
    color: colors.gray700,
    cursor: "pointer",
    transition: "all 0.15s ease",
    flexShrink: 0,
  },
  deleteBuildingRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  },
  deleteBuildingBtn: {
    ...btnDanger,
    padding: "10px 14px",
    fontSize: typography.sizes.base,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitleStyle,
    fontSize: typography.sizes.xl,
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  // Tabs estilo pill con wrap: con 10 pestañas el underline clasico se salia
  // de pantalla y generaba scroll horizontal.
  tabContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 14px",
    cursor: "pointer",
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    background: colors.white,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    whiteSpace: "nowrap",
    transition: `all ${transitions.fast}`,
  },
  tabActive: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 14px",
    cursor: "pointer",
    border: `1px solid ${colors.primary}`,
    borderRadius: radius.sm,
    background: colors.primary,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.white,
    whiteSpace: "nowrap",
    transition: `all ${transitions.fast}`,
  },
  content: {},
  card: {
    ...cardBase,
    marginBottom: spacing[5],
  },
  cardTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: `0 0 ${spacing[5]}px 0`,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: spacing[4],
    marginBottom: spacing[5],
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
  },
  label: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
  },
  input: {
    ...inputBase,
  },
  primaryBtn: {
    ...btnPrimary,
    padding: "12px 20px",
  },
  secondaryBtn: {
    ...btnSecondary,
    padding: "12px 20px",
  },
  addBtn: {
    ...btnPrimary,
    padding: "10px 18px",
    boxShadow: shadows.sm,
    transition: `all ${transitions.normal}`,
  },
  chipContainer: {
    display: "flex",
    gap: spacing[2],
    flexWrap: "wrap",
    marginBottom: spacing[4],
  },
  chip: {
    background: colors.primarySubtle,
    borderRadius: radius.full,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.base,
    color: colors.primary,
    border: `1px solid ${colors.primaryLight}`,
  },
  chipRemove: {
    ...btnGhost,
    padding: 0,
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  unitGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: spacing[4],
    marginBottom: spacing[6],
  },
  unitCard: {
    borderRadius: radius.md,
    padding: spacing[4],
    backgroundColor: colors.gray50,
    boxShadow: shadows.xs,
  },
  unitHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  unitTitle: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
  },
  unitActions: {
    display: "flex",
    gap: 6,
  },
  iconBtn: {
    ...btnGhost,
    background: colors.primarySubtle,
    color: colors.primary,
    borderRadius: radius.sm,
  },
  iconBtnDanger: {
    ...btnGhost,
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
  },
  iconBtnSuccess: {
    ...btnGhost,
    background: colors.successLight,
    color: colors.successDark,
    borderRadius: radius.sm,
  },
  unitInfo: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    lineHeight: 1.6,
  },
  addSection: {
    padding: spacing[5],
    backgroundColor: colors.gray50,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
  },
  addSectionTitle: {
    margin: `0 0 ${spacing[4]}px 0`,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
  },
  employeeCard: {
    borderRadius: radius.md,
    padding: spacing[4],
    backgroundColor: colors.gray50,
    boxShadow: shadows.xs,
  },
  employeeName: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
  },
  employeeInfo: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    lineHeight: 1.6,
  },
  warningBanner: {
    backgroundColor: colors.warningLight,
    border: `1px solid ${colors.warning}`,
    color: colors.warningDark,
    padding: "12px 16px",
    borderRadius: radius.sm,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    width: "100%",
  },
  modalOverlay: {
    ...modalOverlay,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
  },
  closeBtn: {
    ...btnGhost,
    fontSize: typography.sizes.xl,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[3],
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderTop: `1px solid ${colors.gray200}`,
  },
  
  // ========== ESTILOS CARGA MASIVA ==========
  addSectionTop: {
    padding: spacing[5],
    backgroundColor: colors.gray50,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    marginBottom: spacing[4],
  },
  addSectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  bulkButtons: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  bulkBtn: {
    ...btnSecondary,
    gap: 6,
    padding: "8px 12px",
    fontSize: typography.sizes.sm,
    transition: `all ${transitions.normal}`,
  },
  // Botón IA con gradiente especial (no es un color de la paleta estándar)
  bulkBtnAI: {
    ...btnPrimary,
    gap: 6,
    padding: "8px 12px",
    background: `linear-gradient(135deg, ${colors.info} 0%, ${colors.primary} 100%)`,
    fontSize: typography.sizes.sm,
    transition: `all ${transitions.normal}`,
  },
  unitsCount: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.base,
    color: colors.gray600,
    marginBottom: spacing[3],
  },
  bulkModal: {
    background: colors.white,
    borderRadius: radius.lg,
    width: "100%",
    maxWidth: 600,
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: shadows.modal,
  },
  bulkTabs: {
    display: "flex",
    borderBottom: `1px solid ${colors.gray200}`,
  },
  bulkTab: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    padding: "14px",
    background: colors.gray50,
    border: "none",
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray600,
    transition: `all ${transitions.normal}`,
  },
  bulkTabActive: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    padding: "14px",
    background: colors.white,
    border: "none",
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.primary,
    borderBottom: `2px solid ${colors.primary}`,
    marginBottom: -1,
  },
  bulkModalBody: {
    padding: 20,
    minHeight: 300,
  },
  bulkResultSummary: {
    background: colors.primarySubtle,
    padding: spacing[4],
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.gray900,
    lineHeight: 1.6,
    marginBottom: spacing[3],
  },
  bulkErrorBanner: {
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.error}`,
    color: colors.errorDark,
    borderRadius: radius.sm,
    padding: "10px 12px",
    fontSize: typography.sizes.sm,
    marginBottom: spacing[3],
  },
  csvSection: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  csvInstructions: {
    background: colors.primarySubtle,
    padding: spacing[4],
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.gray900,
    lineHeight: 1.6,
  },
  downloadTemplateBtn: {
    ...btnPrimary,
    marginTop: spacing[3],
    padding: "10px 16px",
    fontSize: typography.sizes.base,
  },
  uploadSection: {
    display: "flex",
    justifyContent: "center",
    padding: spacing[5],
    border: `2px dashed ${colors.gray200}`,
    borderRadius: radius.sm,
  },
  uploadBtn: {
    ...btnSecondary,
    padding: "12px 24px",
  },
  previewSection: {
    marginTop: 16,
  },
  borradorBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
    flexWrap: "wrap",
    background: colors.primarySubtle,
    border: `1px solid ${colors.primaryLighter}`,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    marginBottom: spacing[3],
  },
  borradorBannerTexto: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.md,
    color: colors.gray900,
  },
  borradorNota: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  excelParseWarning: {
    backgroundColor: colors.warningLight,
    border: `1px solid ${colors.warning}`,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: "10px 12px",
    fontSize: typography.sizes.sm,
    marginBottom: 10,
  },
  previewTitle: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    marginBottom: spacing[3],
  },
  previewTable: {
    borderRadius: radius.sm,
    overflow: "hidden",
    boxShadow: shadows.xs,
  },
  miniTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  miniTableTh: {
    ...tableHeader,
    padding: "10px 12px",
  },
  miniTableTd: {
    ...tableCell,
    padding: "10px 12px",
  },
  moreItems: {
    textAlign: "center",
    padding: spacing[2],
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    background: colors.gray50,
    margin: 0,
  },
  aiSection: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  // Instrucciones IA con gradiente especial
  aiInstructions: {
    background: `linear-gradient(135deg, ${colors.primaryLighter} 0%, ${colors.primarySubtle} 100%)`,
    padding: spacing[4],
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.gray900,
    lineHeight: 1.6,
  },
  aiExample: {
    marginTop: spacing[3],
    padding: 10,
    background: "rgba(255,255,255,0.7)",
    borderRadius: 6,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  aiTextarea: {
    ...inputBase,
    padding: 14,
    resize: "vertical",
    minHeight: 150,
  },
  // Botón procesar IA con gradiente especial
  processAIBtn: {
    ...btnPrimary,
    justifyContent: "center",
    padding: "12px 24px",
    background: `linear-gradient(135deg, ${colors.info} 0%, ${colors.primary} 100%)`,
    alignSelf: "flex-start",
  },
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: `2px solid ${colors.white}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  
  // Estilos para carga de archivos IA
  aiInputSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  aiInputLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
  },
  fileUploadZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[5],
    border: `2px dashed ${colors.gray300}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    transition: `all ${transitions.normal}`,
    background: colors.gray50,
  },
  fileUploadText: {
    margin: "8px 0 0",
    fontSize: typography.sizes.base,
    color: colors.gray900,
    fontWeight: 400,
  },
  fileUploadHint: {
    margin: "4px 0 0",
    fontSize: typography.sizes.xs,
    color: colors.gray400,
  },
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 8,
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 12px",
    background: colors.gray100,
    borderRadius: 6,
    fontSize: typography.sizes.base,
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: colors.gray900,
  },
  fileRemoveBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    background: colors.errorLight,
    color: colors.errorDark,
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
  },
  thFinanzas: {
    ...tableHeader,
    padding: "8px 10px",
    fontSize: typography.sizes.sm,
  },
  tdFinanzas: {
    ...tableCell,
    padding: "6px 10px",
    verticalAlign: "middle",
  },
};

// CSS para la animación del spinner (se agrega via style tag)
if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .bulk-modal-table th, .bulk-modal-table td {
      padding: 8px 12px;
      text-align: left;
      border-bottom: 1px solid ${colors.gray200};
    }
    .bulk-modal-table th {
      background: ${colors.primary};
      font-weight: 400;
      color: ${colors.white};
    }
  `;
  document.head.appendChild(styleTag);
}

// Dropdown buscable para seleccionar usuario.
// El panel se renderiza en un portal con posición fija para que NO lo recorte
// el scroll/overflow del modal (antes los campos de abajo quedaban tapados).
// Se abre hacia arriba si no hay espacio suficiente debajo.
const SearchableSelect = ({ value, onChange, options, placeholder = "Seleccionar...", emptyLabel = null }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [coords, setCoords] = React.useState(null);
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const reposition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const PANEL_MAX = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < PANEL_MAX && spaceAbove > spaceBelow;
    const maxH = Math.max(160, Math.min(PANEL_MAX, (openUp ? spaceAbove : spaceBelow) - 8));
    setCoords({
      left: rect.left,
      width: rect.width,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
      maxH,
    });
  }, []);

  // Reposicionar al abrir y mientras se hace scroll/resize (el modal scrollea).
  React.useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  // Cerrar al hacer click fuera del trigger Y del panel (que vive en el portal).
  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={triggerRef} style={{ position: "relative" }}>
      <div
        onClick={() => { setOpen((o) => !o); setSearch(""); }}
        style={{
          height: 36, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
          padding: "6px 10px", fontSize: typography.sizes.md, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: colors.white, userSelect: "none",
        }}
      >
        <span style={{ color: selected ? colors.black : colors.gray400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ fontSize: 10, color: colors.gray400, marginLeft: 6 }}>▼</span>
      </div>

      {open && coords && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed", left: coords.left, top: coords.top, width: coords.width,
            transform: coords.openUp ? "translateY(-100%)" : "none",
            background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
            boxShadow: shadows.lg, zIndex: zIndex.modal + 50,
            maxHeight: coords.maxH, display: "flex", flexDirection: "column",
          }}
        >
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${colors.gray100}` }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              style={{
                width: "100%", border: `1px solid ${colors.gray200}`, borderRadius: 6,
                padding: "5px 8px", fontSize: typography.sizes.base, outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {emptyLabel !== null && (
              <div
                onClick={() => { onChange(""); setOpen(false); }}
                style={{
                  padding: "8px 12px", fontSize: typography.sizes.base, cursor: "pointer",
                  color: colors.gray600, background: value === "" ? colors.primaryLighter : "transparent",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.gray50}
                onMouseLeave={(e) => e.currentTarget.style.background = value === "" ? colors.primaryLighter : "transparent"}
              >
                {emptyLabel}
              </div>
            )}
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: typography.sizes.base, color: colors.gray400 }}>Sin resultados</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.id || o.value}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  style={{
                    padding: "8px 12px", fontSize: typography.sizes.base, cursor: "pointer",
                    background: String(o.value) === String(value) ? colors.primaryLighter : "transparent",
                    fontWeight: 400,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = colors.gray50}
                  onMouseLeave={(e) => e.currentTarget.style.background = String(o.value) === String(value) ? colors.primaryLighter : "transparent"}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EditarEdificio;
